/* eslint-disable */
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { useState, useEffect, useCallback, useRef } from "react";
import { getAuth } from 'firebase/auth';

// Authenticated feed fetch — attaches Firebase ID token for server-side pro verification
async function feedFetch(url) {
  try {
    const currentUser = getAuth().currentUser;
    const token = currentUser ? await currentUser.getIdToken() : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return fetch(url, { headers }).then(r => r.json());
  } catch {
    return fetch(url).then(r => r.json());
  }
}

const YEAR  = new Date().getFullYear();
// Use Eastern Time for dates — MLB/NHL games run on ET schedule
const getDateStr=(d=new Date())=>{
  // Stay on previous date until 3am ET — covers NHL/west coast games that run past midnight
  const etHour=parseInt(new Intl.DateTimeFormat("en-US",{timeZone:"America/New_York",hour:"numeric",hour12:false}).format(d));
  const adjusted=new Date(d);
  if(etHour<3) adjusted.setDate(adjusted.getDate()-1);
  return new Intl.DateTimeFormat("en-CA",{timeZone:"America/New_York"}).format(adjusted);
};
const fmtDisplay=(s)=>{const d=new Date(s+"T12:00:00");return d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});};
const MLB   = "/api/mlb";
const NHL   = "/api/nhl";

// Historical park factors (last 3yr avg) — used as baseline for blending
const PF_HIST = {
  BAL:114,BOS:96,NYY:123,TB:88,TOR:107,CWS:105,CLE:98,DET:104,KC:95,MIN:106,
  HOU:91,LAA:99,OAK:95,SEA:97,TEX:111,ATL:106,MIA:92,NYM:102,PHI:104,WSH:105,
  CHC:112,CIN:115,MIL:102,PIT:98,STL:99,ARI:113,COL:132,LAD:98,SD:90,SF:88,ATH:95
};
const DOMES = new Set(["TB","TOR","HOU","MIA","MIL"]);

// Team abbreviation → MLB team ID map
const TEAM_ID = {
  ARI:109,ATL:144,BAL:110,BOS:111,CHC:112,CWS:145,CIN:113,CLE:114,COL:115,
  DET:116,HOU:117,KC:118,LAA:108,LAD:119,MIA:146,MIL:158,MIN:142,NYM:121,
  NYY:147,OAK:133,ATH:133,PHI:143,PIT:134,SD:135,SF:137,SEA:136,STL:138,TB:139,
  TEX:140,TOR:141,WSH:120
};

// Get current month weight for blending current vs historical
const getPFWeight=()=>{
  const m=new Date().getMonth()+1; // 1=Jan
  if(m<=4) return 0.50;  // April: 50/50
  if(m===5) return 0.65;  // May: 65% current
  if(m===6) return 0.75;  // June: 75% current
  return 0.85;             // July+: 85% current
};

// Dynamic park factors — loaded once on app start, shared across components
let PF = {...PF_HIST};
let PFR = Object.entries(PF).sort(([,a],[,b])=>b-a).reduce((o,[t],i)=>{o[t]=i+1;return o},{});

const buildPFR=()=>{
  PFR=Object.entries(PF).sort(([,a],[,b])=>b-a).reduce((o,[t],i)=>{o[t]=i+1;return o},{});
};

// Load dynamic park factors — called inside MLBSection via jFetch
const loadDynamicParkFactors=async(fetchFn)=>{
  try{
    const weight=getPFWeight();
    const [hitData, pitData] = await Promise.all([
      fetchFn(`/api/mlb/teams/stats?season=${YEAR}&group=hitting&stats=statSplits&sitCodes=h&gameType=R&sportId=1`).catch(()=>null),
      fetchFn(`/api/mlb/teams/stats?season=${YEAR}&group=pitching&stats=statSplits&sitCodes=h&gameType=R&sportId=1`).catch(()=>null),
    ]);
    if(!hitData||!pitData) return false;

    const hitSplits=hitData.stats?.[0]?.splits||[];
    const pitSplits=pitData.stats?.[0]?.splits||[];
    if(!hitSplits.length||!pitSplits.length) return false;

    const hitMap={},pitMap={};
    hitSplits.forEach(s=>{const tid=s.team?.id;const st=s.stat||{};if(tid)hitMap[tid]={hr:parseFloat(st.homeRuns)||0,gp:Math.max(1,parseFloat(st.gamesPlayed)||1)};});
    pitSplits.forEach(s=>{const tid=s.team?.id;const st=s.stat||{};if(tid)pitMap[tid]={hr:parseFloat(st.homeRuns)||0,gp:Math.max(1,parseFloat(st.gamesPlayed)||1)};});

    const allHR=Object.values(hitMap).reduce((a,v)=>a+v.hr,0)+Object.values(pitMap).reduce((a,v)=>a+v.hr,0);
    const allGP=Object.values(hitMap).reduce((a,v)=>a+v.gp,0)||1;
    const leagueHRpG=allHR/allGP;
    if(leagueHRpG<0.3) return false;

    const newPF={};
    Object.entries(TEAM_ID).forEach(([abb,tid])=>{
      const hit=hitMap[tid],pit=pitMap[tid];
      const hist=PF_HIST[abb]||100;
      if(!hit||!pit||hit.gp<3){newPF[abb]=hist;return;}
      const parkHRpG=(hit.hr/hit.gp)+(pit.hr/pit.gp);
      const dynPF=Math.round((parkHRpG/leagueHRpG)*100);
      newPF[abb]=Math.round(weight*dynPF+(1-weight)*hist);
    });

    if(Object.keys(newPF).length>=25){PF=newPF;buildPFR();return true;}
    return false;
  }catch(e){
    console.warn("Dynamic PF failed, using historical",e);
    return false;
  }
};

// PF loaded inside MLBSection on mount

const jFetch = async url=>{
  let finalUrl=url;
  if(url.startsWith("/api/mlb")){
    const path=url.replace("/api/mlb","");
    finalUrl="/api/mlb?path="+encodeURIComponent("/api/v1"+path);
  } else if(url.startsWith("/api/nhl")){
    const path=url.replace("/api/nhl","");
    finalUrl="/api/nhl?path="+encodeURIComponent("/v1"+path);
  } else if(url.startsWith("/api/nba-cdn")){
    const path=url.replace("/api/nba-cdn","");
    finalUrl="/api/nba?base=cdn&path="+encodeURIComponent("/static/json"+path);
  } else if(url.startsWith("/api/nba")){
    const path=url.replace("/api/nba","");
    finalUrl="/api/nba?path="+encodeURIComponent(path);
  } else if(url.startsWith("https://site.api.espn.com")||url.startsWith("https://sports.core.api.espn.com")||url.startsWith("https://site.web.api.espn.com")){
    finalUrl="/api/espn?url="+encodeURIComponent(url);
  }
  const r=await fetch(finalUrl);
  if(!r.ok)throw new Error(r.status);
  return r.json();
};

// Color scale — green = 60+, yellow = 42-59, red = <42
const scoreColor = s=>s>=60?"#00e676":s>=42?"#ffb020":"#ff4060";

const fmtTime = dt=>{try{return new Date(dt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",timeZone:"America/New_York"});}catch{return "";}};

function windParse(w){
  if(!w)return{mph:0,label:"N/A",score:0};
  const mph=parseInt((w.match(/(\d+)/)||[])[1])||0;
  const l=w.toLowerCase();
  if(mph<3||l.includes("calm"))return{mph,label:"Calm",score:0};
  if(l.includes("out"))return{mph,label:`↑ Out ${mph}mph`,score:mph>=20?5:mph>=15?4:mph>=10?3:2};
  if(l.includes("in"))return{mph,label:`↓ In ${mph}mph`,score:mph>=20?-5:mph>=15?-4:mph>=10?-3:-2};
  return{mph,label:`↔ ${mph}mph`,score:0};
}

async function fetchPitcherSplits(pid){
  if(!pid)return{vsL:null,vsR:null};
  const yesterday=new Intl.DateTimeFormat("en-CA",{timeZone:"America/New_York"}).format(new Date(Date.now()-86400000));
  try{
    const d=await jFetch(`${MLB}/people/${pid}/stats?stats=statSplits&group=pitching&season=${YEAR}&sitCodes=vl,vr&gameType=R&endDate=${yesterday}`);
    const splits=d.stats?.[0]?.splits||[];
    let vsL=splits.find(s=>s.split?.code==="vl")?.stat||null;
    let vsR=splits.find(s=>s.split?.code==="vr")?.stat||null;
    if(!vsL&&!vsR){
      const d2=await jFetch(`${MLB}/people/${pid}/stats?stats=statSplits&group=pitching&season=${YEAR-1}&sitCodes=vl,vr&gameType=R&endDate=${yesterday}`);
      const s2=d2.stats?.[0]?.splits||[];
      vsL=s2.find(s=>s.split?.code==="vl")?.stat||null;
      vsR=s2.find(s=>s.split?.code==="vr")?.stat||null;
    }
    return{vsL,vsR};
  }catch{return{vsL:null,vsR:null};}
}

function AppInner(){
  const { user, userProfile, loading: authLoading, signInWithGoogle, logout, isPro, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const[sport,setSport]=useState("MLB"); // kept for compatibility
  // Sync sport state with URL on mount - handled by Routes below
  const[selDate,setSelDate]=useState(getDateStr());

  // Pause all polling when tab is hidden — saves requests when users switch away
  useEffect(()=>{
    const onVisibility=()=>{
      if(document.hidden){
        // Tab hidden — intervals still run but we gate inside each poll
        document.body.setAttribute("data-hidden","1");
      } else {
        document.body.removeAttribute("data-hidden");
      }
    };
    document.addEventListener("visibilitychange",onVisibility);
    return()=>document.removeEventListener("visibilitychange",onVisibility);
  },[]);

  return(
    <div style={{fontFamily:"'Barlow Condensed',sans-serif",background:"#060910",color:"#c8d6e8",minHeight:"100vh"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;900&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        *{box-sizing:border-box}
        ::-webkit-scrollbar{height:4px;width:4px}
        ::-webkit-scrollbar-thumb{background:#1e3060;border-radius:2px}
        tbody tr:hover td{background:#0c1428!important}
      `}</style>
      <div style={{background:"#08091a",borderBottom:"1px solid #131e35",padding:"10px 12px",position:"sticky",top:0,zIndex:10}}>
        {/* Row 1: Logo + Date picker */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}} onClick={()=>navigate('/')}>
            <img src={require('./logo.png')} alt="ScoutSidekick" style={{width:44,height:44,objectFit:"contain"}}/>
            <div>
              <div style={{fontSize:22,fontWeight:900,letterSpacing:4,color:"#fff",lineHeight:1}}>SCOUT<span style={{color:"#00c8ff"}}>SIDEKICK</span></div>
              <div style={{fontSize:9,color:"#00c8ff",letterSpacing:3,opacity:0.7,marginTop:1}}>SPORTS ANALYTICS ENGINE</div>
            </div>
          </div>
          {/* Date picker + auth */}
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{fontFamily:"monospace",fontSize:11,color:"#00c8ff",background:"#08111f",border:"1px solid #182030",borderRadius:4,padding:"4px 8px",minWidth:90,textAlign:"center"}}>
              TODAY · {fmtDisplay(getDateStr())}
            </div>
            {/* Auth button */}
            {authLoading?null:user?(
              <div style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}} onClick={()=>navigate('/account')}>
                <img src={user.photoURL} alt="avatar" style={{width:28,height:28,borderRadius:"50%",border:`2px solid ${isPro()?"#00e676":"#182030"}`}} onError={e=>e.target.style.display="none"}/>
                {isPro()&&<span style={{fontSize:9,fontWeight:800,color:"#00e676",background:"#001a00",border:"1px solid #00e67640",borderRadius:3,padding:"2px 5px",letterSpacing:1}}>PRO</span>}
              </div>
            ):(
              <button onClick={signInWithGoogle} style={{padding:"5px 10px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:11,fontWeight:800,letterSpacing:1,background:"#0c1322",border:"1px solid #00c8ff40",borderRadius:4,cursor:"pointer",color:"#00c8ff",whiteSpace:"nowrap"}}>
                SIGN IN
              </button>
            )}
          </div>
        </div>
        {/* Row 2: Sport buttons full width */}
        <div style={{display:"flex",gap:6}}>
          {(()=>{
            const path=window.location.pathname.replace('/','').toUpperCase();
            return [["MLB","#00c8ff"],["NHL","#00e676"],["NBA","#ff6b35"],["NFL","#ff6b00"],["MMA","#e63946"]].map(([s,c])=>(
              <button key={s} onClick={()=>navigate('/'+s.toLowerCase())} style={{flex:1,padding:"7px 4px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:800,letterSpacing:2,background:path===s?"#0d0f1a":"#0c1322",border:`1px solid ${path===s?c:"#182030"}`,borderRadius:5,cursor:"pointer",color:path===s?c:"#3a5070"}}>{s}</button>
            ));
          })()}
          {user&&<button onClick={()=>navigate('/account')} style={{padding:"7px 8px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:1,background:window.location.pathname==='/account'?"#0d0f1a":"#0c1322",border:`1px solid ${window.location.pathname==='/account'?"#00c8ff":"#182030"}`,borderRadius:5,cursor:"pointer",color:window.location.pathname==='/account'?"#00c8ff":"#3a5070"}}>👤</button>}
        </div>
      </div>
      <Routes>
        <Route path="/mlb" element={<MLBSection selDate={selDate}/>}/>
        <Route path="/nhl" element={<NHLSection selDate={selDate}/>}/>
        <Route path="/nba" element={<NBASection selDate={selDate}/>}/>
        <Route path="/nfl" element={<NFLSection/>}/>
        <Route path="/mma" element={<MMASection/>}/>
        <Route path="/account" element={authLoading?<div style={{padding:40,textAlign:'center',color:'#3a5070'}}>Loading...</div>:!user?<Navigate to="/" replace/>:<AccountSection user={user} userProfile={userProfile} isPro={isPro} logout={logout}/>}/>
        <Route path="/" element={<HomeSection navigate={navigate} user={user} signInWithGoogle={signInWithGoogle} isPro={isPro}/>}/>
        <Route path="*" element={<Navigate to="/" replace/>}/>
      </Routes>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// HOME / LANDING SECTION
// ════════════════════════════════════════════════════════════════
function HomeSection({navigate, user, signInWithGoogle, isPro}){
  const pro = isPro();
  const sports = [
    { key:"mlb", emoji:"⚾", name:"MLB", color:"#00c8ff", desc:"HR targets · Park factors · AI picks · Bullpen data" },
    { key:"nhl", emoji:"🏒", name:"NHL", color:"#00e676", desc:"Goal scorer rankings · Live scores · AI analyst picks" },
    { key:"nba", emoji:"🏀", name:"NBA", color:"#ff6b35", desc:"Points · Assists · Rebounds · Live top scorers" },
    { key:"nfl", emoji:"🏈", name:"NFL", color:"#ffb020", desc:"Coming September 2026 — Full season coverage", soon:true },
    { key:"mma", emoji:"🥊", name:"MMA", color:"#e63946", desc:"Fight cards · Odds · Event tracking" },
  ];
  const stats = [
    { value:"5", label:"Sports Covered" },
    { value:"📊", label:"Sort Through Deep Analytics" },
    { value:"⚡", label:"Live Scoring Feeds" },
    { value:"5", label:"AI Analyst Pick Cards" },
  ];
  return(
    <div style={{maxWidth:680,margin:"0 auto",padding:"24px 16px 60px"}}>
      {/* Hero */}
      <div style={{textAlign:"center",padding:"32px 0 28px"}}>
        <div style={{fontSize:11,color:"#00c8ff",letterSpacing:4,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,marginBottom:12,opacity:0.8}}>POWERED BY LIVE DATA · AI SCORING MODELS</div>
        <div style={{fontSize:36,fontWeight:900,color:"#fff",letterSpacing:3,lineHeight:1.1,fontFamily:"'Barlow Condensed',sans-serif",marginBottom:8}}>
          YOUR SCOUT<br/><span style={{color:"#00c8ff"}}>SIDEKICK</span>
        </div>
        <div style={{fontSize:13,color:"#5a7090",marginBottom:24,lineHeight:1.6}}>
          Your all-in-one sidekick for scouting sports stats, live scoring feeds,<br/>AI-powered picks, and deep analytics across MLB, NHL, NBA, NFL and MMA.
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
          {!user?(
            <button onClick={signInWithGoogle} style={{padding:"11px 28px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:800,letterSpacing:2,background:"#00c8ff",border:"none",borderRadius:6,cursor:"pointer",color:"#000"}}>
              GET STARTED FREE
            </button>
          ):(
            <button onClick={()=>navigate('/mlb')} style={{padding:"11px 28px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:800,letterSpacing:2,background:"#00c8ff",border:"none",borderRadius:6,cursor:"pointer",color:"#000"}}>
              GO TO DASHBOARD →
            </button>
          )}
          {!pro&&(
            <button onClick={()=>navigate('/account')} style={{padding:"11px 28px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:15,fontWeight:800,letterSpacing:2,background:"transparent",border:"1px solid #c060ff",borderRadius:6,cursor:"pointer",color:"#c060ff"}}>
              GET PRO
            </button>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:28}}>
        {stats.map(s=>(
          <div key={s.label} style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:8,padding:"14px 10px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:900,color:"#00c8ff",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1}}>{s.value}</div>
            <div style={{fontSize:9,color:"#3a5070",letterSpacing:1,marginTop:3,textTransform:"uppercase"}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Sport cards */}
      <div style={{marginBottom:10}}>
        <div style={{fontSize:11,color:"#3a5070",letterSpacing:3,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,marginBottom:12}}>SELECT A SPORT</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {sports.map(s=>(
            <div key={s.key} onClick={()=>!s.soon&&navigate('/'+s.key)}
              style={{background:"#080c18",border:`1px solid ${s.soon?"#111c2e":s.color+"30"}`,borderRadius:8,padding:"16px 18px",cursor:s.soon?"default":"pointer",display:"flex",alignItems:"center",gap:16,transition:"all 0.15s",opacity:s.soon?0.5:1}}
              onMouseEnter={e=>{if(!s.soon)e.currentTarget.style.background="#0c1220";}}
              onMouseLeave={e=>{e.currentTarget.style.background="#080c18";}}>
              <div style={{fontSize:32,flexShrink:0}}>{s.emoji}</div>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                  <span style={{fontSize:18,fontWeight:900,color:s.color,fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:2}}>{s.name}</span>
                  {s.soon&&<span style={{fontSize:9,color:"#ffb020",background:"#1a1200",border:"1px solid #ffb02040",borderRadius:3,padding:"1px 6px",fontWeight:700,letterSpacing:1}}>COMING SOON</span>}
                </div>
                <div style={{fontSize:11,color:"#3a5070"}}>{s.desc}</div>
              </div>
              {!s.soon&&<div style={{color:s.color,fontSize:18,opacity:0.6}}>›</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Features — SEO + user info */}
      <div style={{marginTop:24,marginBottom:8}}>
        <div style={{fontSize:11,color:"#3a5070",letterSpacing:3,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,marginBottom:12}}>WHAT'S INSIDE</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {[
            {emoji:"⚾", text:"Live Home Run Feed"},
            {emoji:"🏒", text:"Live Goal Scorer Feed"},
            {emoji:"🏀", text:"Live Triple Double Tracker"},
            {emoji:"🏈", text:"Live Touchdown Tracker"},
            {emoji:"🥊", text:"MMA / UFC Fight Cards & Events"},
            {emoji:"🤖", text:"AI HR & Goal Analyst Picks"},
            {emoji:"📊", text:"MLB · NHL · NBA · NFL · MMA"},
            {emoji:"🏅", text:"Baseball · Hockey · Basketball · Football · MMA"},
          ].map(f=>(
            <div key={f.text} style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:7,padding:"11px 14px",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:18,flexShrink:0}}>{f.emoji}</span>
              <span style={{fontSize:11,color:"#4a6080",fontWeight:600}}>{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Pro CTA */}
      {!pro&&(
        <div onClick={()=>navigate('/account')} style={{marginTop:24,background:"linear-gradient(135deg,#0e0818,#150a28)",border:"1px solid #c060ff40",borderRadius:10,padding:"20px 24px",cursor:"pointer",textAlign:"center"}}>
          <div style={{fontSize:11,color:"#c060ff",letterSpacing:3,fontWeight:700,marginBottom:6}}>SCOUTSIDEKICK PRO</div>
          <div style={{fontSize:17,fontWeight:900,color:"#fff",fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:1,marginBottom:6}}>UNLOCK FULL RANKINGS + ALL ANALYST PICKS</div>
          <div style={{fontSize:11,color:"#7040a0",marginBottom:14}}>Top 10 free · Pro unlocks every player, every sport</div>
          <div style={{display:"inline-block",padding:"9px 24px",background:"linear-gradient(135deg,#7020c0,#c060ff)",borderRadius:5,fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:900,color:"#fff",letterSpacing:2}}>
            GET PRO →
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MLB SECTION
// ════════════════════════════════════════════════════════════════
function MLBSection({selDate=getDateStr()}){
  const { isPro, user, signInWithGoogle } = useAuth();
  const mlbIsPro = isPro();
  const[games,setGames]=useState([]);
  const[loading,setLoading]=useState(true);
  const[sel,setSel]=useState(null);
  const[lu,setLu]=useState(null);
  const[pstats,setPstats]=useState({});
  const[loadingP,setLoadingP]=useState(false);
  const[tab,setTab]=useState("HR");
  const[scoringPlays,setScoringPlays]=useState([]);
  const[showScoring,setShowScoring]=useState(true);
  const[err,setErr]=useState(null);
  const[view,setView]=useState("top");
  const[topTargets,setTopTargets]=useState([]);
  const[loadingTop,setLoadingTop]=useState(false);
  const[topLoaded,setTopLoaded]=useState(false);
  const playerStatsCache=useRef({}); // freeze player stats at first fetch
  const pitcherStatsCache=useRef({}); // freeze pitcher stats at first fetch
  const[rankLockedAt,setRankLockedAt]=useState(null); // timestamp when rankings were locked
  const[liveScores,setLiveScores]=useState({}); // gamePk → {away,home,status,inning}
  const[hrResults,setHrResults]=useState({});   // playerId → {count,games}
  const[walkResults,setWalkResults]=useState({});  // playerId → {count,game,name,team}
  const[hrFilter,setHrFilter]=useState("all"); // all | upcoming | live | final | hit
  const[mlbSort,setMlbSort]=useState({col:"hrScore",dir:"desc"});
  const[hrFeed,setHrFeed]=useState([]);     // live HR feed [{name,team,inning,pitcher,gameStr,ts}]
  const[showFeed,setShowFeed]=useState(true);
  const[teamHR,setTeamHR]=useState([]);       // team HR projections
  const[teamHRLoaded,setTeamHRLoaded]=useState(false);
  const[loadingTeamHR,setLoadingTeamHR]=useState(false);
  const[teamHRLockedAt,setTeamHRLockedAt]=useState(null);
  const[lastLineupCount,setLastLineupCount]=useState(0);
  const[pitcherSplits,setPitcherSplits]=useState([]); // today's starters with vsL/vsR splits + gaps
  const[pitcherSplitsLoaded,setPitcherSplitsLoaded]=useState(false);
  const[loadingPitcherSplits,setLoadingPitcherSplits]=useState(false);
  const[pitcherSplitsSort,setPitcherSplitsSort]=useState({col:"hr9Gap",dir:"desc"});
  const[parlay,setParlay]=useState(null);      // AI parlay picks
  const[todayPicks,setTodayPicks]=useState({}); // cached picks by analystId {blaze:[...], kong:[...]}
  const[parlayStrategyIdx,setParlayStrategyIdx]=useState(0);
  const[bbTargets,setBBTargets]=useState([]);
  const[loadingBB,setLoadingBB]=useState(false);
  const[bbLoaded,setBBLoaded]=useState(false);
  const[bbSort,setBBSort]=useState({col:"bbScore",dir:"desc"});
  const[bbFilter,setBBFilter]=useState("all"); // all | upcoming | live | final | walked
  const[loadingParlay,setLoadingParlay]=useState(false);
  const[parlayLocked,setParlayLocked]=useState(false);
  const[analystRecords,setAnalystRecords]=useState({}); // analystId → {legs:{hit,total}, parlays:{n:{hit,total}}}
  const[selectedAnalyst,setSelectedAnalyst]=useState(0); // which analyst is selected
  const[watchlist,setWatchlist]=useState(new Set()); // pro: watched player IDs
  const[watchStats,setWatchStats]=useState({});      // playerId → today's box score line
  const[loadingWatchStats,setLoadingWatchStats]=useState(false);

  // Analyst definitions
  const ANALYSTS=[
    {id:"blaze",name:"Blaze",face:"🐉",color:"#ff4060",strategy:0,desc:"Best Overall Score",bio:"Trusts the numbers. If the score says go, Blaze goes."},
    {id:"streak",name:"Streak",face:"🐂",color:"#ffb020",strategy:1,desc:"Hot Streak",bio:"Rides momentum. Hot bats don't cool off overnight."},
    {id:"slider",name:"Slider",face:"🦊",color:"#00c8ff",strategy:2,desc:"Pitcher Matchup",bio:"Exploits weak arms. A bad pitcher is a free HR."},
    {id:"gusty",name:"Gusty",face:"🦅",color:"#00e676",strategy:3,desc:"Wind & Park",bio:"Weather is everything. Wind out + HR park = magic."},
    {id:"kong",name:"Kong",face:"🦍",color:"#c060ff",strategy:4,desc:"Power Hitters",bio:"Elite sluggers carry. Back the big bats every time."},
  ];

  // ── Watchlist: load from Firestore on mount ───────────────────────
  useEffect(()=>{
    if(!user||!mlbIsPro) return;
    (async()=>{
      try{
        const { doc, getDoc } = await import('firebase/firestore');
        const { db: fsDb } = await import('./firebase');
        const snap = await getDoc(doc(fsDb,'users',user.uid));
        if(snap.exists()){
          const ids = snap.data()?.mlbWatchlist||[];
          setWatchlist(new Set(ids.map(String)));
        }
      }catch{}
    })();
  },[user,mlbIsPro]);

  // ── Re-fetch rankings when pro status changes ──────────────────────
  // /api/feed slices to top 10 for unauth/free users. When a user signs in
  // as pro mid-session, the cached topTargets state still holds the original
  // 10 — nothing triggers a re-fetch, so the table stays at 10 even though
  // mlbIsPro is now true. On a pro-status transition, invalidate the
  // *Loaded flags so any future view-switch reloads, AND re-fetch the
  // currently-active view's data inline (we can't call the existing useCallback
  // load functions because their topLoaded check would short-circuit on the
  // stale closure value). Skip the very first render so we don't double-fetch
  // on cold load.
  const mlbIsProRef = useRef(mlbIsPro);
  useEffect(()=>{
    if(mlbIsProRef.current === mlbIsPro) return; // no transition
    mlbIsProRef.current = mlbIsPro;
    setTopLoaded(false);
    setBBLoaded(false);
    setTeamHRLoaded(false);
    setPitcherSplitsLoaded(false);
    // If user is currently looking at one of these views, refetch its data now.
    // For other views, the next time they open them will reload via the existing
    // load* functions because *Loaded is now false.
    (async()=>{
      try{
        if(view==="top"||view==="bb"||view==="teamhr"||view==="pitcherSplits"){
          const d=await feedFetch(`/api/feed?sport=mlb&date=${selDate}`);
          if(view==="top"&&d?.mlb?.rankings){
            setTopTargets(d.mlb.rankings);
            setTopLoaded(true);
          }
          if(view==="bb"&&d?.mlb?.bbtargets){
            setBBTargets(d.mlb.bbtargets);
            setBBLoaded(true);
          }
          if(view==="teamhr"&&d?.mlb?.teamhr){
            setTeamHR(d.mlb.teamhr);
            setTeamHRLoaded(true);
          }
          if(view==="pitcherSplits"&&d?.mlb?.pitcherSplits){
            setPitcherSplits(d.mlb.pitcherSplits);
            setPitcherSplitsLoaded(true);
          }
        }
      }catch(e){console.warn('Pro-status refetch failed:',e);}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[mlbIsPro]);


  const saveWatchlist = async(newSet)=>{
    if(!user) return;
    try{
      const { doc, setDoc } = await import('firebase/firestore');
      const { db: fsDb } = await import('./firebase');
      await setDoc(doc(fsDb,'users',user.uid),{mlbWatchlist:[...newSet]},{merge:true});
    }catch{}
  };

  const toggleWatch = (playerId)=>{
    if(!mlbIsPro) return;
    const id = String(playerId);
    setWatchlist(prev=>{
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      saveWatchlist(next);
      return next;
    });
  };

  // Fetch today's box score line for each watched player
  const loadWatchStats = async()=>{
    if(!watchlist.size||!topTargets.length) return;
    setLoadingWatchStats(true);
    // Get unique gamePks for watched players
    const gamePkMap = {};
    topTargets.forEach(p=>{ if(watchlist.has(String(p.id))) gamePkMap[p.gamePk]=true; });
    const gamePks = Object.keys(gamePkMap);
    const stats = {};
    await Promise.all(gamePks.map(async(gamePk)=>{
      try{
        // Route through /api/mlb proxy — direct browser calls to statsapi.mlb.com
        // can fail silently (CORS, throttling) and used to wipe all watch stats.
        const box = await jFetch(`/api/mlb/game/${gamePk}/boxscore`);
        const sides = [box?.teams?.away, box?.teams?.home];
        sides.forEach(side=>{
          if(!side?.players) return;
          Object.values(side.players).forEach(p=>{
            const id = String(p.person?.id);
            if(!watchlist.has(id)) return;
            const b = p.stats?.batting||{};
            stats[id] = {
              ab: b.atBats??'—',
              h: b.hits??'—',
              hr: b.homeRuns||0,
              bb: b.baseOnBalls||0,
              k: b.strikeOuts||0,
              rbi: b.rbi||0,
              gamePk,
            };
          });
        });
      }catch(e){
        // Log so transient failures are visible in the console during debugging,
        // but don't rethrow — we still want stats for other games to render.
        console.warn(`Boxscore fetch failed for game ${gamePk}:`, e?.message||e);
      }
    }));
    // Merge instead of replace — if this refresh tick failed to fetch some games,
    // keep the previously-known stats for their players rather than wiping them.
    setWatchStats(prev=>({...prev, ...stats}));
    setLoadingWatchStats(false);
  };

  // Refresh watch stats every 2 min when view is watchlist
  useEffect(()=>{
    if(view!=='watchlist') return;
    loadWatchStats();
    const interval = setInterval(loadWatchStats, 120000);
    return ()=>clearInterval(interval);
  },[view, watchlist, topTargets]);
  useEffect(()=>{
    // Load analyst records from Redis
    const loadRecords=async()=>{
      try{
        const d=await fetch(`/api/picks?sport=mlb`).then(r=>r.json());
        if(d?.records&&Object.keys(d.records).length) setAnalystRecords(d.records);
      }catch{}
    };
    loadRecords();
    const recordInterval=setInterval(loadRecords,5*60*1000);
    return()=>clearInterval(recordInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Settlement handled server-side in refresh.js cron

  // Load schedule + start live polling
  useEffect(()=>{
    const loadFromFeed=async()=>{
      try{
        const d=await feedFetch(`/api/feed?sport=mlb&date=${selDate}`);
        const mlb=d?.mlb||{};
        const gs=mlb.schedule||[];
        if(gs.length){
          setGames(gs);setLoading(false);
          // Extract live scores from schedule
          const scores={};
          gs.forEach(g=>{
            const ls=g.linescore||{};
            scores[String(g.gamePk)]={
              away:ls.teams?.away?.runs??g.teams?.away?.score??null,
              home:ls.teams?.home?.runs??g.teams?.home?.score??null,
              inning:ls.currentInning||null,
              inningHalf:ls.inningHalf||null,
              status:g.status?.abstractGameState||"Preview",
              detailedState:g.status?.detailedState||"",
            };
          });
          setLiveScores(scores);
        } else {
          setLoading(false);
        }
        // Load park factors from cron cache
        if(mlb.parkfactors){
          try{
            const{hitMap,pitMap,leagueHRpG}=mlb.parkfactors;
            if(leagueHRpG>=0.3){
              const weight=getPFWeight();
              const newPF={};
              Object.entries(TEAM_ID).forEach(([abb,tid])=>{
                const hit=hitMap[tid],pit=pitMap[tid];
                const hist=PF_HIST[abb]||100;
                if(!hit||!pit||hit.gp<3){newPF[abb]=hist;return;}
                const parkHRpG=(hit.hr/hit.gp)+(pit.hr/pit.gp);
                const dynPF=Math.round((parkHRpG/leagueHRpG)*100);
                newPF[abb]=Math.round(weight*dynPF+(1-weight)*hist);
              });
              if(Object.keys(newPF).length>=25){PF=newPF;buildPFR();}
            }
          }catch(e){}
        }
      }catch(e){setLoading(false);}
    };

    const pollFeed=()=>{
      if(document.hidden) return;
      feedFetch(`/api/feed?sport=mlb&date=${selDate}`)
        .then(d=>{
          const mlb=d?.mlb||{};
          if(mlb.scores&&Object.keys(mlb.scores).length) setLiveScores(mlb.scores);
          if(mlb.hrResults&&Object.keys(mlb.hrResults).length) setHrResults(prev=>Object.keys(mlb.hrResults).length>0?mlb.hrResults:prev);
          if(mlb.walkResults&&Object.keys(mlb.walkResults).length) setWalkResults(prev=>Object.keys(mlb.walkResults).length>0?mlb.walkResults:prev);
          if(mlb.hrFeed?.length) setHrFeed(prev=>{
            const existingTs=new Set(prev.map(h=>h.ts));
            const newItems=(mlb.hrFeed||[]).filter(h=>!existingTs.has(h.ts));
            return newItems.length?[...newItems,...prev].sort((a,b)=>new Date(b.ts)-new Date(a.ts)):prev;
          });
          // Check for new lineups in schedule
          if(mlb.schedule?.length){
            const gs=mlb.schedule;
            const posted=gs.filter(g=>g.lineups?.homePlayers?.length||g.lineups?.awayPlayers?.length).length;
            setLastLineupCount(prev=>{
              if(posted>prev){
                setGames(gs);
                gs.forEach(g=>{
                  const st=g.status?.abstractGameState||"";
                  if(st!=="Live"&&st!=="Final"){
                    const players=[...(g.lineups?.homePlayers||[]),...(g.lineups?.awayPlayers||[])];
                    players.forEach(p=>{
                      if(p?.id&&playerStatsCache.current[p.id]){
                        delete playerStatsCache.current[p.id]._hrScore;
                        delete playerStatsCache.current[p.id]._hrDetails;
                        delete playerStatsCache.current[p.id]._pitcherId;
                      }
                    });
                    setTopLoaded(false);
                  }
                });
              }
              return posted>prev?posted:prev;
            });
          }
        }).catch(()=>{});
    };

    setGames([]);setTopTargets([]);setTopLoaded(false);setHrFeed([]);setHrResults({});setWalkResults({});setLiveScores({});setTeamHR([]);setTeamHRLoaded(false);setTeamHRLockedAt(null);setRankLockedAt(null);setLastLineupCount(0);setBBTargets([]);setBBLoaded(false);
    playerStatsCache.current={};
    pitcherStatsCache.current={};
    loadFromFeed();
    const interval=setInterval(pollFeed,180000);
    return()=>clearInterval(interval);
  },[selDate]);

  // Auto-load top HR targets on mount once games are ready
  // Reset top targets when date changes
  useEffect(()=>{
    setTopTargets([]);
    setTopLoaded(false);
    setHrFeed([]);
    setHrResults({});
    setWalkResults({});
    setParlayLocked(false);
  },[selDate]);

  useEffect(()=>{
    if(games.length===0||loadingTop||topLoaded) return;
    // Always try feed first — cron pre-computes rankings, no lineup check needed
    loadTopTargets();
    // Track lineup count for cache invalidation only
    const currentLineupCount=games.filter(g=>g.lineups?.homePlayers?.length||g.lineups?.awayPlayers?.length).length;
    if(currentLineupCount>lastLineupCount){
      setLastLineupCount(currentLineupCount);
      setTeamHRLoaded(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[games]);

  // Poll HR results + build live HR feed every 60 seconds
  useEffect(()=>{
    const checkHRs=async()=>{
      if(document.hidden) return;
      // Use feed endpoint — already populated by server-side cron
      try{
        const d=await feedFetch(`/api/feed?sport=mlb&date=${selDate}`);
        const hasData=d?.mlb?.scores&&Object.keys(d.mlb.scores).length>0;
        if(!hasData) throw new Error("feed empty"); // fall through to direct API
        if(d?.mlb?.hrResults&&Object.keys(d.mlb.hrResults).length) setHrResults(d.mlb.hrResults);
        if(d?.mlb?.walkResults&&Object.keys(d.mlb.walkResults).length) setWalkResults(d.mlb.walkResults);
        if(d?.mlb?.hrFeed?.length) setHrFeed(prev=>{
          const existingTs=new Set(prev.map(h=>h.ts));
          const newItems=(d.mlb.hrFeed||[]).filter(h=>!existingTs.has(h.ts));
          return newItems.length?[...newItems,...prev].sort((a,b)=>new Date(b.ts)-new Date(a.ts)):prev;
        });
        if(d?.mlb?.scores) setLiveScores(d.mlb.scores);
        return;
      }catch(e){}
      // Fallback to direct play-by-play if feed not yet populated
      const activeGames=games.filter(g=>{
        const st=g.status?.abstractGameState||"";
        return st==="Live"||st==="Final";
      });
      if(!activeGames.length) return;

      const feedItems=[];

      await Promise.all(activeGames.map(async g=>{
        try{
          const awayAbb=g.teams?.away?.team?.abbreviation||"?";
          const homeAbb=g.teams?.home?.team?.abbreviation||"?";
          const gameStr=`${awayAbb} @ ${homeAbb}`;
          const isFinal=g.status?.abstractGameState==="Final";

          // Get play-by-play for HR events — this is the source of truth for today's HRs
          const pbp=await jFetch(`${MLB}/game/${g.gamePk}/playByPlay`);
          const allPlays=pbp?.allPlays||[];
          allPlays.forEach(play=>{
            if(play?.result?.eventType!=="home_run") return;
            const batter=play?.matchup?.batter;
            const pitcher=play?.matchup?.pitcher;
            const inning=play?.about?.inning;
            const half=play?.about?.halfInning; // "top" or "bottom"
            const desc=play?.result?.description||"";
            const ts=play?.about?.startTime||"";
            const awayScore=play?.result?.awayScore??null;
            const homeScore=play?.result?.homeScore??null;
            if(batter?.id){
              feedItems.push({
                id:batter.id,
                name:batter.fullName||"Unknown",
                team:half==="top"?awayAbb:homeAbb,
                pitcher:pitcher?.fullName||"Unknown",
                inning:`${half==="top"?"▲":"▼"}${inning}`,
                gameStr,
                awayScore,homeScore,awayAbb,homeAbb,
                desc,ts,
                isFinal,
              });
            }
          });
        }catch(e){}
      }));

      // Sort feed by time desc (most recent first)
      feedItems.sort((a,b)=>new Date(b.ts)-new Date(a.ts));

      // Build hrResults from actual HR play-by-play events — not boxscore which has season totals
      const results={};
      feedItems.forEach(hr=>{
        if(!hr.id) return;
        const key=String(hr.id);
        if(!results[key]) results[key]={count:0,game:hr.gameStr,name:hr.name,team:hr.team};
        results[key].count++;
      });

      setHrResults(results);
      setHrFeed(feedItems);
    };
    if(games.length>0) checkHRs();
    const interval=setInterval(checkHRs,120000);
    return()=>{clearInterval(interval);};
  },[games]);

  const fetchPitcher=async(pid,fb)=>{
    if(!pid)return{name:fb?.fullName||"TBD",hand:"?",era:"---",whip:"---",ip:"0",hr9:"---",splits:{vsL:null,vsR:null}};
    if(pitcherStatsCache.current[pid]) return pitcherStatsCache.current[pid];
    try{
      const[main,splits]=await Promise.all([
        jFetch(`${MLB}/people/${pid}?hydrate=stats(group=[pitching],type=[season],season=${YEAR})`),
        fetchPitcherSplits(pid),
      ]);
      const p=main.people?.[0]||{};const st=p.stats?.[0]?.splits?.[0]?.stat||{};
      const ip=parseFloat(st.inningsPitched)||0;
      const hrAllowed=parseInt(st.homeRuns)||0;
      const bbAllowed=parseInt(st.baseOnBalls)||0;
      const hr9=ip>0?((hrAllowed/ip)*9).toFixed(2):"---";
      const bb9=ip>0?((bbAllowed/ip)*9).toFixed(2):"---";
      const result={id:pid,name:p.fullName||fb?.fullName,hand:p.pitchHand?.code,era:st.era||"---",whip:st.whip||"---",ip:st.inningsPitched||"0",hr9,bb9,splits};
      pitcherStatsCache.current[pid]=result; // freeze pitcher stats
      return result;
    }catch{return{id:pid,name:fb?.fullName||"TBD",hand:"?",era:"---",whip:"---",ip:"0",hr9:"---",splits:{vsL:null,vsR:null}};}
  };

  const fetchBatter=async(batter,facePid,facePHand,order,pitcherSplits)=>{
    const id=batter.id;
    // Return cached stats if we already fetched this player — freeze stats at first load
    if(playerStatsCache.current[id]){
      return{...playerStatsCache.current[id],order};
    }
    try{
      const siteCode=facePHand==="L"?"vl":"vr";
      const yesterday=new Intl.DateTimeFormat("en-CA",{timeZone:"America/New_York"}).format(new Date(Date.now()-86400000));
      // Original 7 calls — reliable and well-tested
      const[log,s26,s25,s24,s23,vsR,platoon]=await Promise.allSettled([
        jFetch(`${MLB}/people/${id}/stats?stats=gameLog&group=hitting&gameType=R&season=${YEAR}&limit=7`),
        jFetch(`${MLB}/people/${id}/stats?stats=season&group=hitting&season=${YEAR}&gameType=R&endDate=${yesterday}`),
        jFetch(`${MLB}/people/${id}/stats?stats=season&group=hitting&season=${YEAR-1}&gameType=R`),
        jFetch(`${MLB}/people/${id}/stats?stats=season&group=hitting&season=${YEAR-2}&gameType=R`),
        jFetch(`${MLB}/people/${id}/stats?stats=season&group=hitting&season=${YEAR-3}&gameType=R`),
        facePid?jFetch(`${MLB}/people/${id}/stats?stats=vsPlayerTotal&group=hitting&opposingPlayerId=${facePid}`):Promise.resolve(null),
        jFetch(`${MLB}/people/${id}/stats?stats=statSplits&group=hitting&season=${YEAR}&sitCodes=${siteCode}&gameType=R&endDate=${yesterday}`),
      ]);
      // Use ET date to exclude today's game from l7
      const todayGameDate=new Intl.DateTimeFormat("en-CA",{timeZone:"America/New_York"}).format(new Date());
      const splits=log.status==="fulfilled"?log.value.stats?.[0]?.splits||[]:[];
      // Exclude today's game from l7 — we want pre-game stats only
      const preSplits=splits.filter(x=>{
        const gd=(x.date||x.gameDate||"").split("T")[0];
        return !gd||gd<todayGameDate;
      });
      const l7=preSplits.slice(0,7).reduce((a,x)=>{
        const s=x.stat;a.hr+=s.homeRuns||0;a.rbi+=s.rbi||0;
        a.xbh+=(s.doubles||0)+(s.triples||0)+(s.homeRuns||0);a.sb+=s.stolenBases||0;a.h+=s.hits||0;a.ab+=s.atBats||0;a.bb+=s.baseOnBalls||0;return a;
      },{hr:0,rbi:0,xbh:0,sb:0,h:0,ab:0,bb:0});
      const ss26=s26.status==="fulfilled"?s26.value.stats?.[0]?.splits?.[0]?.stat||{}:{};
      const ss25=s25.status==="fulfilled"?s25.value.stats?.[0]?.splits?.[0]?.stat||{}:{};
      const has26=parseInt(ss26.atBats||0)>=10;
      const ss=has26?ss26:ss25;
      const ssY=has26?String(YEAR):String(YEAR-1);
      const yr1=s25.status==="fulfilled"?s25.value.stats?.[0]?.splits?.[0]?.stat||{}:{};
      const yr2=s24.status==="fulfilled"?s24.value.stats?.[0]?.splits?.[0]?.stat||{}:{};
      const yr3=s23.status==="fulfilled"?s23.value.stats?.[0]?.splits?.[0]?.stat||{}:{};
      const hrYears=[yr1,yr2,yr3].filter(y=>parseInt(y.gamesPlayed||0)>=50);
      const avgSeasonHR=hrYears.length>0
        ?hrYears.reduce((a,y)=>a+parseInt(y.homeRuns||0),0)/hrYears.length
        :parseInt(yr1.homeRuns||0)||0;
      const vsS=(vsR.status==="fulfilled"&&vsR.value)?vsR.value.stats?.[0]?.splits?.[0]?.stat||null:null;
      const platStat=platoon.status==="fulfilled"?platoon.value.stats?.[0]?.splits?.[0]?.stat||null:null;
      const rawHand=batter.batSide?.code||batter.person?.batSide?.code||"R";
      // Switch hitters bat opposite to pitcher hand — vs LHP they bat RHB, vs RHP they bat LHB
      const batterHand=rawHand==="S"?(facePHand==="L"?"R":"L"):rawHand;
      const pitSplit=batterHand==="L"?(pitcherSplits?.vsL||null):(pitcherSplits?.vsR||null);
      const result={id,name:batter.fullName,order,l7,ss,vsS,ssY,platStat,platHand:siteCode,pitSplit,batterHand,avgSeasonHR};
      playerStatsCache.current[id]=result; // freeze stats — never re-fetch
      return result;
    }catch{return{id,name:batter.fullName,order,error:true};}
  };

  // ── HR SCORE — rebalanced weights ────────────────────────────
  // Philosophy: Elite power hitter + hot streak + HR park + wind out + good split = GREEN
  //
  // Factor                    Max   Why
  // ─────────────────────────────────────────────────────────────
  // 1. Career Power Tier       18   Foundation — proven HR hitter
  // 2. Last 7 HR (hot streak)  18   Is he hot RIGHT NOW?
  // 3. Park Factor             16   Is this an HR-friendly park?
  // 4. Wind (out = boost)      16   Wind out is a massive HR factor
  // 5. Platoon split           12   Does he hit this pitcher hand well?
  // 6. SP HR/9 + vs hand       10   Is this pitcher HR-prone to this hand?
  // 7. vs Starter career        6   Has he specifically hit THIS guy?
  // 8. ISO                      4   Current season power quality
  // ─────────────────────────────────────────────────────────────
  // Total                     100
  //
  // The perfect storm to hit green (60+):
  //   Elite power hitter (18) + 2 HR last 7 (12) + HR park like COL/NYY (14) + wind out 15mph (14) + good platoon split (9) = ~67 ✅ GREEN

  function scHR(s,park,wind,pitcherHR9){
    if(!s||s.error)return null;
    const ss=s.ss||{};
    const vsS=s.vsS||null;
    const platStat=s.platStat||null;
    const pitSplit=s.pitSplit||null;
    const avgSeasonHR=s.avgSeasonHR||0;
    const sAB=parseInt(ss.atBats)||1,sHR=parseInt(ss.homeRuns)||0;
    const sSlg=parseFloat(ss.sluggingPct)||0,sAvg=parseFloat(ss.avg)||0;
    const ISO=Math.max(0,sSlg-sAvg);
    const vsAB=parseInt(vsS?.atBats)||0,vsHR=parseInt(vsS?.homeRuns)||0;
    const platAB=parseInt(platStat?.atBats)||0,platHR=parseInt(platStat?.homeRuns)||0;
    const platSLG=parseFloat(platStat?.sluggingPct)||0;
    const l7HR=s.l7?.hr||0;

    // 1. Career Power Tier (18pts)
    // 30+ HR/yr=18, 25+=15, 20+=12, 15+=8, 10+=5, <10=2
    const careerSc=avgSeasonHR>=30?18:avgSeasonHR>=25?15:avgSeasonHR>=20?12:avgSeasonHR>=15?8:avgSeasonHR>=10?5:2;

    // 2. Last 7 HR — hot streak (18pts)
    // 0 HR=2, 1 HR=8, 2 HR=13, 3 HR=16, 4+=18
    const l7Sc=l7HR>=4?18:l7HR===3?16:l7HR===2?13:l7HR===1?8:2;

    // 3. Park Factor (16pts)
    // Scales from 80 (min) to 132 (Coors) — aggressive curve
    const pkSc=Math.max(0,Math.min(16,(park.f-80)/52*16));

    // 4. Wind — direction and speed (16pts)
    // Out wind is huge. Calm/cross = neutral. In wind = penalty.
    // wind.score: out=2-5, calm=0, in=-2 to -5
    const rawW=wind.score; // -5 to +5
    const wSc=Math.round(Math.min(16,Math.max(0,(rawW+5)/10*16)));

    // 5. Platoon split — batter vs pitcher hand (12pts)
    const platSc=platAB>=20?Math.min(12,(platHR/platAB)*192):platAB>0&&platHR>0?8:platSLG>=0.500?9:4;

    // 6. Pitcher HR-proneness — HR/9 + split vs this hand (10pts)
    const phr9=parseFloat(pitcherHR9)||1.2;
    const phr9Sc=Math.min(5,Math.max(0,(phr9-0.5)/1.5*5));
    // Pitching splits use battersFaced as primary count, atBats is often 0
    const pitAB=parseInt(pitSplit?.battersFaced)||parseInt(pitSplit?.atBats)||0;
    const pitHR=parseInt(pitSplit?.homeRuns)||0;
    // MLB API uses 'ops' or falls back to obp+sluggingPercentage
    const pitOPS=parseFloat(pitSplit?.ops)||
      (parseFloat(pitSplit?.obp||0)+parseFloat(pitSplit?.sluggingPercentage||0))||0.750;
    let pitHandSc=3;
    if(pitAB>=10){
      const hrRate=pitHR/Math.max(1,pitAB);
      pitHandSc=Math.min(5,Math.round(hrRate*200+(Math.max(0,(pitOPS-0.600)/0.400*2.5))));
    }else if(pitAB>0&&pitHR>0){pitHandSc=4;}
    const pitTotalSc=Math.min(10,phr9Sc+pitHandSc);

    // 7. vs This Starter career (6pts)
    const vsSc=vsAB>=10?Math.min(6,(vsHR/vsAB)*96):vsAB>=5?Math.min(4,(vsHR/vsAB)*96):vsAB>0&&vsHR>0?4:2;

    // 8. ISO — current season power quality (4pts)
    const isoSc=sAB>=20?Math.min(4,Math.max(0,(ISO-0.05)/0.25*4)):1;

    const total=Math.min(100,Math.round(careerSc+l7Sc+pkSc+wSc+platSc+pitTotalSc+vsSc+isoSc));

    const careerTier=avgSeasonHR>=30?"ELITE":avgSeasonHR>=20?"ABOVE AVG":avgSeasonHR>=10?"AVERAGE":"LOW";
    const pitHRRate=pitAB>=20?(pitHR/pitAB*100).toFixed(1)+"%":null;

    return{
      total,
      ISO:ISO>0?ISO.toFixed(3):null,
      avgSeasonHR:avgSeasonHR.toFixed(1),careerTier,
      l7HR,
      platHR,platAB,platHand:s.platHand,batterHand:s.batterHand,
      pitHR9:pitcherHR9,
      pitHRRate,
      pitSplitHR:pitHR||0,
      pitSplitBF:pitAB||0,
      pitSplitHRRate:pitAB>=1?((pitHR/pitAB)*100).toFixed(1)+"%":null,
      pitAB,
      vsHR,vsAB,sHR,sAB,
      parkF:park.f,parkR:park.rank,
      windLabel:wind.label,windScore:wind.score,windMph:wind.mph,
    };
  }

  function scRBI(s){
    if(!s||s.error)return null;
    const ord=s.order||5,g=parseInt(s.ss?.gamesPlayed)||1,rbi=parseInt(s.ss?.rbi)||0,ops=parseFloat(s.ss?.ops)||0;
    return{total:Math.min(100,Math.round((ord<=2?10:ord<=5?25:14)+Math.min(25,(rbi/g)*80)+Math.min(25,(s.l7?.rbi||0)*5)+Math.min(25,Math.max(0,(ops-0.45)/0.85*25)))),order:ord,l7RBI:s.l7?.rbi||0,seasonRBI:rbi,ops:s.ss?.ops||"---",g};
  }
  function scSB(s){
    if(!s||s.error)return null;
    const sb=parseInt(s.ss?.stolenBases)||0,cs=parseInt(s.ss?.caughtStealing)||0,rt=sb+cs>0?sb/(sb+cs):0.7;
    return{total:Math.min(100,Math.round(Math.min(40,sb*4)+Math.min(30,rt*30)+(s.order<=2?20:s.order===3?12:8)+Math.min(10,(s.l7?.sb||0)*4))),order:s.order,seasonSB:sb,cs,sbRate:sb+cs>0?`${Math.round(rt*100)}%`:"N/A",l7SB:s.l7?.sb||0};
  }
  function scXBH(s,park){
    if(!s||s.error)return null;
    const sAB=parseInt(s.ss?.atBats)||1;
    const xbh=(parseInt(s.ss?.doubles)||0)+(parseInt(s.ss?.triples)||0)+(parseInt(s.ss?.homeRuns)||0);
    const vOPS=parseFloat(s.vsS?.ops)||0;
    return{total:Math.min(100,Math.round((sAB>=20?Math.min(30,(xbh/sAB)*260):4)+Math.min(30,(s.l7?.xbh||0)*6)+(s.vsS?Math.min(20,Math.max(0,(vOPS-0.4)/0.9*20)):9)+Math.max(0,Math.min(20,(park.f-80)/52*20)))),seasonXBH:xbh,l7XBH:s.l7?.xbh||0,vsOPS:s.vsS?.ops||"---",parkF:park.f,parkR:park.rank};
  }

  function scBB(s){
    if(!s||s.error)return null;
    const bb=parseInt(s.ss?.baseOnBalls)||0;
    const g=parseInt(s.ss?.gamesPlayed)||1;
    const obp=parseFloat(s.ss?.obp||s.ss?.onBasePercentage)||0;
    const l7BB=s.l7?.bb||0;
    const ord=s.order||5;
    // Score: OBP (35pts) + BB/game (25pts) + L7 BB (20pts) + lineup order (20pts)
    const obpSc=Math.min(35,Math.max(0,(obp-0.280)/0.120*35));
    const bbgSc=Math.min(25,(bb/g)*50);
    const l7Sc=Math.min(20,l7BB*5);
    const ordSc=ord===1?20:ord===2?18:ord===3?14:ord<=5?10:6;
    return{total:Math.min(100,Math.round(obpSc+bbgSc+l7Sc+ordSc)),seasonBB:bb,bbPerGame:(bb/g).toFixed(2),l7BB,obp:s.ss?.obp||s.ss?.onBasePercentage||"---",order:ord,g};
  }

  function scHITS(s,park,pitcherWHIP){
    if(!s||s.error)return null;
    const ss=s.ss||{};
    const sAB=Math.max(1,parseInt(ss.atBats)||1);
    const sH=parseInt(ss.hits)||0;
    const avg=parseFloat(ss.avg)||0;
    const l7H=s.l7?.h||0;
    const l7AB=Math.max(1,s.l7?.ab||28);
    const l7Avg=l7H/l7AB;
    const ord=s.order||5;
    const vsWHIP=parseFloat(pitcherWHIP)||1.30;
    const vsBAA=parseFloat(s.pitSplit?.avg)||0.260;

    // 1. Season AVG (25pts) — contact quality baseline
    // .300+=25, .280+=20, .260+=14, .240+=9, <.240=4
    const avgSc=avg>=0.300?25:avg>=0.280?20:avg>=0.260?14:avg>=0.240?9:4;

    // 2. Last 7 games contact rate (22pts) — recent form
    // 0.300+ l7avg=22, 0.280+=18, 0.260+=13, 0.240+=8, <.240=3
    const l7Sc=l7Avg>=0.300?22:l7Avg>=0.280?18:l7Avg>=0.260?13:l7Avg>=0.240?8:3;

    // 3. Lineup order / plate appearances (18pts)
    // Leadoff gets most PAs, cleanup gets fewer but better counts
    // #1: 18, #2: 16, #3: 14, #4: 11, #5-6: 8, #7-9: 5
    const ordSc=ord===1?18:ord===2?16:ord===3?14:ord===4?11:ord<=6?8:5;

    // 4. Opposing pitcher hittability — WHIP (18pts)
    // High WHIP = more baserunners = more hit opportunities
    // WHIP 1.60+=18, 1.40+=14, 1.20+=10, 1.00+=6, <1.00=3
    const whipSc=vsWHIP>=1.60?18:vsWHIP>=1.40?14:vsWHIP>=1.20?10:vsWHIP>=1.00?6:3;

    // 5. Pitcher BAA vs this hand (12pts) — how hittable vs this batter's hand
    const baaSc=vsBAA>=0.290?12:vsBAA>=0.270?9:vsBAA>=0.250?6:vsBAA>=0.230?4:2;

    // 6. Park hits factor (5pts) — pitcher parks suppress hits
    // Use park factor as proxy: <95=suppress, 95-105=neutral, >105=slight boost
    const pkF=park?.f||100;
    const pkSc=pkF>=110?5:pkF>=100?3:pkF>=90?1:0;

    const total=Math.min(100,Math.round(avgSc+l7Sc+ordSc+whipSc+baaSc+pkSc));
    return{
      total,
      avg:ss.avg||"---",
      l7H,l7AB,l7Avg:l7Avg.toFixed(3),
      seasonH:sH,seasonAB:sAB,
      order:ord,
      vsWHIP:vsWHIP.toFixed(2),
      vsBAA:vsBAA>0?vsBAA.toFixed(3):null,
    };
  }

  function getScore(s,park,wind,pitcherHR9){
    if(tab==="HR")return scHR(s,park,wind,pitcherHR9);
    if(tab==="RBI")return scRBI(s);
    if(tab==="SB")return scSB(s);
    if(tab==="XBH")return scXBH(s,park);
    if(tab==="H")return scHITS(s,park,s.pitcherWHIP);
    if(tab==="BB")return scBB(s);
    return null;
  }

  const pickGame=useCallback(async g=>{
    if(sel?.gamePk===g.gamePk){setSel(null);setLu(null);setPstats({});return;}
    setSel(g);setLoadingP(true);setPstats({});setScoringPlays([]);
    const ha=g.teams.home.team.abbreviation;
    const aa=g.teams.away.team.abbreviation;
    const hp=g.teams.home.probablePitcher,ap=g.teams.away.probablePitcher;
    const hLU=g.lineups?.homePlayers||[],aLU=g.lineups?.awayPlayers||[];
    const isDome=DOMES.has(ha);
    const wind=isDome?{mph:0,label:"🏟 Dome",score:0}:windParse(g.weather?.wind);
    const park={f:PF[ha]||100,rank:PFR[ha]||15};
    try{
      const d=await feedFetch(`/api/feed?sport=gameview&gamePk=${g.gamePk}`);
      const hPD=d.homePitcher||{name:hp?.fullName||"TBD",hand:"?",era:"---",whip:"---",ip:"0",hr9:"---",splits:{vsL:null,vsR:null}};
      const aPD=d.awayPitcher||{name:ap?.fullName||"TBD",hand:"?",era:"---",whip:"---",ip:"0",hr9:"---",splits:{vsL:null,vsR:null}};
      setLu({ha,aa,hp,ap,hLU,aLU,wind,park,isDome,homeTeam:g.teams.home.team,awayTeam:g.teams.away.team,weather:g.weather,hPD,aPD});
      // Build pstats from cached batter data
      const batters=d.batters||{};
      const sm={};
      // Add pitcher HR9 to each batter
      [...hLU.map(b=>({...b,pitcherHR9:aPD?.hr9})),...aLU.map(b=>({...b,pitcherHR9:hPD?.hr9}))].forEach(b=>{
        if(batters[b.id]) sm[b.id]={...batters[b.id],pitcherHR9:b.pitcherHR9};
      });
      // Fallback: use rankings data for any batter not in gameview cache
      if(Object.keys(sm).length===0){
        topTargets.forEach(p=>{
          if(String(p.gamePk)===String(g.gamePk)) sm[p.id]={id:p.id,name:p.name,order:p.order,l7:{hr:0,rbi:0,xbh:0,sb:0,h:0,ab:0,bb:0},ss:{},vsS:null,platStat:null,platHand:"vr",pitSplit:null,batterHand:"R",avgSeasonHR:0,pitcherHR9:null};
        });
      }
      setPstats(sm);
      if(d.scoringPlays?.length) setScoringPlays(d.scoringPlays);
    }catch(e){
      // Last resort fallback — show empty game view
      setLu({ha,aa,hp,ap,hLU,aLU,wind,park,isDome,homeTeam:g.teams.home.team,awayTeam:g.teams.away.team,weather:g.weather,hPD:null,aPD:null});
    }
    setLoadingP(false);
  },[sel,topTargets]);

  // ── loadTeamHR: project each team's HR output for today ──────────
  const loadTeamHR=useCallback(async()=>{
    if(teamHRLoaded){setView("teamhr");return;}
    setView("teamhr");setLoadingTeamHR(true);setTeamHR([]);
    try{
      const d=await feedFetch(`/api/feed?sport=mlb&date=${selDate}`);
      if(d?.mlb?.teamhr?.length){
        setTeamHR(d.mlb.teamhr);
        setTeamHRLoaded(true);
        const allIn=d.mlb.teamhr.every(t=>t.hasLineup);
        if(allIn){
          const lockTime=new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",timeZone:"America/New_York"});
          setTeamHRLockedAt(`${lockTime} ET`);
        }
      }else{
        setTeamHR([]);
      }
    }catch(e){}
    setLoadingTeamHR(false);
  },[selDate,teamHRLoaded]);

  // ── loadPitcherSplits: today's starters with biggest L vs R split gaps ──
  // Reads from feed:mlb:pitcherSplits:${date}, populated server-side during
  // refreshMLBRankings. No new external API calls.
  const loadPitcherSplits=useCallback(async()=>{
    if(pitcherSplitsLoaded){setView("pitcherSplits");return;}
    setView("pitcherSplits");setLoadingPitcherSplits(true);setPitcherSplits([]);
    try{
      const d=await feedFetch(`/api/feed?sport=mlb&date=${selDate}`);
      if(d?.mlb?.pitcherSplits?.length){
        setPitcherSplits(d.mlb.pitcherSplits);
        setPitcherSplitsLoaded(true);
      }else{
        setPitcherSplits([]);
      }
    }catch(e){}
    setLoadingPitcherSplits(false);
  },[selDate,pitcherSplitsLoaded]);

  const[parlaySize,setParlaySize]=useState(6);

  const generateParlay=(size,forceStratIdx)=>{
    if(!topTargets.length){alert("Load Top HR Targets first.");return;}
    const n=size||parlaySize;
    setLoadingParlay(true);setParlay(null);setView("parlay");

    const strategies=[
      {
        name:"Best Overall Score",
        desc:"Top composite HR scores across all factors",
        emoji:"🔥",
        sort:(a,b)=>Number(b.hrScore)-Number(a.hrScore),
        filter:p=>p.hrScore>=35,
      },
      {
        name:"Hot Streak",
        desc:"Players on fire — most HRs in last 7 games",
        emoji:"📈",
        sort:(a,b)=>(b.hrDetails?.l7HR||0)-(a.hrDetails?.l7HR||0)||(Number(b.hrScore)-Number(a.hrScore)),
        filter:p=>(p.hrDetails?.l7HR||0)>=1,
      },
      {
        name:"Pitcher Matchup",
        desc:"Facing the most HR-prone starters",
        emoji:"⚾",
        sort:(a,b)=>(parseFloat(b.hrDetails?.pitHR9||0))-(parseFloat(a.hrDetails?.pitHR9||0))||(Number(b.hrScore)-Number(a.hrScore)),
        filter:p=>parseFloat(p.hrDetails?.pitHR9||0)>=1.2,
      },
      {
        name:"Wind & Park",
        desc:"Best environmental conditions — wind out + HR parks",
        emoji:"💨",
        sort:(a,b)=>{
          const envA=(a.hrDetails?.windScore||0)+(a.park?.f||100)/10;
          const envB=(b.hrDetails?.windScore||0)+(b.park?.f||100)/10;
          return envB-envA||(Number(b.hrScore)-Number(a.hrScore));
        },
        filter:p=>(p.hrDetails?.windScore||0)>=0&&(p.park?.f||100)>=95,
      },
      {
        name:"Power Hitters",
        desc:"Elite career HR hitters in favorable matchups",
        emoji:"💪",
        sort:(a,b)=>(parseFloat(b.hrDetails?.avgSeasonHR||0))-(parseFloat(a.hrDetails?.avgSeasonHR||0))||(Number(b.hrScore)-Number(a.hrScore)),
        filter:p=>parseFloat(p.hrDetails?.avgSeasonHR||0)>=15,
      },
    ];

    const stratIdx=forceStratIdx!==undefined?forceStratIdx:parlayStrategyIdx;
    const strat=strategies[stratIdx%strategies.length];
    const nextIdx=(stratIdx+1)%strategies.length;
    setParlayStrategyIdx(nextIdx);

    try{
      const eligible=[...topTargets]
        .filter(p=>p.gameSt!=="final"&&!p.preLineup&&strat.filter(p))
        .sort(strat.sort);

      const allPreLineup=topTargets.length>0&&topTargets.every(p=>p.preLineup);
      const usedGames=new Set();
      const usedPlayers=new Set();
      const picks=[];
      const makePick=(p)=>{
        const d=p.hrDetails||{};
        const reasons=[];
        if(d.l7HR>=2) reasons.push(`${d.l7HR} HR last 7`);
        if(d.careerTier==="ELITE") reasons.push("elite power");
        if(parseFloat(d.pitHR9||0)>=1.5) reasons.push(`SP ${d.pitHR9} HR/9`);
        if(p.park?.f>=110) reasons.push("HR park");
        if(d.windScore>0) reasons.push("wind out");
        const origRank=topTargets.findIndex(t=>t.id===p.id)+1;
        return{rank:origRank,name:p.name,team:p.team,opp:p.oppSP?.name||"TBD",score:p.hrScore,reason:reasons.length?reasons.join(" · "):"Strong overall matchup",gameTime:p.gameTime};
      };
      // First pass — one player per game
      for(const p of eligible){
        if(picks.length>=n) break;
        const gameKey=String(p.gamePk);
        if(!usedGames.has(gameKey)&&!usedPlayers.has(String(p.id))){
          usedGames.add(gameKey);
          usedPlayers.add(String(p.id));
          picks.push(makePick(p));
        }
      }
      // Second pass — fill remaining slots, allow multiple per game but no duplicate players
      if(picks.length<n){
        for(const p of eligible){
          if(picks.length>=n) break;
          if(!usedPlayers.has(String(p.id))){
            usedPlayers.add(String(p.id));
            picks.push(makePick(p));
          }
        }
      }
      if(!picks.length){setParlay({error:allPreLineup?"Lineups haven't been posted yet — check back closer to game time.":"No eligible players — try a different strategy or check back when lineups post."});setLoadingParlay(false);return;}
      const avgScore=Math.round(picks.reduce((a,p)=>a+p.score,0)/picks.length);
      const confidence=avgScore>=65?"HIGH":avgScore>=50?"MEDIUM":"LOW";
      const analyst=ANALYSTS[stratIdx%ANALYSTS.length];
      const note=parlayLocked?`${n}-pick selections · locked`:`${n}-pick selections · ${usedGames.size} games · updates as lineups post`;
      setParlay({picks,strategy:strat.name,strategyEmoji:strat.emoji,strategyDesc:strat.desc,analystId:analyst.id,parlayNote:note,confidence});
      // Display-only — never POST from here. The auto-save loop (when all
      // lineups are in) is the sole writer to /api/picks. Previously this
      // would overwrite the canonical 6-pick auto-save with whatever
      // parlaySize the user happened to have selected (default 4),
      // permanently corrupting that analyst's record for everyone.
      try{
        setTodayPicks(prev=>prev[analyst.id]?prev:({...prev,[analyst.id]:{analystId:analyst.id,size:n,picks}}));
      }catch{}
    }catch(e){setParlay({error:"Failed to generate. Try again."});}
    setLoadingParlay(false);
  };

  // ── BB Target Score ─────────────────────────────────────────────
  // Factor                    Max   Why
  // 1. Season OBP             25    Plate discipline baseline
  // 2. BB/game season         20    Walk rate
  // 3. Last 7 BB              18    Recent form
  // 4. SP BB/9                20    How wild is the opposing pitcher?
  // 5. Lineup order           12    Leadoff/2-hole see more pitches
  // 6. SP WHIP                 5    Overall control proxy
  // Total                    100

  function scBBTarget(s,pitcherBB9,pitcherWHIP){
    if(!s||s.error) return null;
    const ss=s.ss||{};
    const g=Math.max(1,parseInt(ss.gamesPlayed)||1);
    const bb=parseInt(ss.baseOnBalls)||0;
    const obp=parseFloat(ss.obp||ss.onBasePercentage)||0;
    const l7BB=s.l7?.bb||0;
    const ord=s.order||5;

    // 1. Season OBP (25pts)
    const obpSc=Math.min(25,Math.max(0,(obp-0.280)/0.150*25));

    // 2. BB/game (20pts)
    const bbg=bb/g;
    const bbgSc=Math.min(20,bbg*25);

    // 3. Last 7 BB (18pts)
    const l7Sc=Math.min(18,l7BB*4);

    // 4. SP BB/9 — higher = more walks issued = better for batter (20pts)
    const spbb9=parseFloat(pitcherBB9)||3.0;
    const bb9Sc=Math.min(20,Math.max(0,(spbb9-1.5)/3.5*20));

    // 5. Lineup order (12pts) — leadoff/2-hole see most pitches
    const ordSc=ord===1?12:ord===2?10:ord===3?7:ord<=5?5:3;

    // 6. SP WHIP (5pts) — high WHIP = less control
    const whip=parseFloat(pitcherWHIP)||1.30;
    const whipSc=Math.min(5,Math.max(0,(whip-0.9)/0.9*5));

    const total=Math.min(100,Math.round(obpSc+bbgSc+l7Sc+bb9Sc+ordSc+whipSc));
    return{total,obp:obp>0?obp.toFixed(3):"---",bbPerGame:bbg.toFixed(2),seasonBB:bb,l7BB,spBB9:pitcherBB9||"---",spWHIP:pitcherWHIP||"---",order:ord};
  }

  const loadBBTargets=useCallback(async()=>{
    if(bbLoaded){setView("bb");return;}
    setView("bb");setLoadingBB(true);setBBTargets([]);
    try{
      const d=await feedFetch(`/api/feed?sport=mlb&date=${selDate}`);
      if(d?.mlb?.bbtargets?.length){
        setBBTargets(d.mlb.bbtargets);
        setBBLoaded(true);
      }else{
        setBBTargets([]);
      }
    }catch(e){}
    setLoadingBB(false);
  },[selDate,bbLoaded]);

  const loadTopTargets=useCallback(async()=>{
    if(topLoaded){setView("top");return;}
    setView("top");setLoadingTop(true);setTopTargets([]);
    // Try feed first — rankings pre-computed by server cron
    try{
      const d=await feedFetch(`/api/feed?sport=mlb&date=${selDate}`);
      if(d?.mlb?.rankings?.length){
        setTopTargets(d.mlb.rankings);
        setTopLoaded(true);
        setLoadingTop(false);
        // Auto-save analyst picks — only when real lineups are posted
        const anyRealLineup=d.mlb.rankings.some(p=>!p.preLineup);
        // All lineups in = every game has both away and home lineup posted
        const totalExpected=games.length*2;
        const currentLineupCount=games.reduce((a,g)=>{if(g.lineups?.awayPlayers?.length)a++;if(g.lineups?.homePlayers?.length)a++;return a;},0);
        const allLineupsIn=totalExpected>0&&currentLineupCount>=totalExpected;
        const existingMlbPicks=await fetch(`/api/picks?sport=mlb`).then(r=>r.json()).catch(()=>({picks:[]}));
        // Only save picks if we have real lineup data AND picks not already saved
        // Lock picks when ALL lineups are in
        if(allLineupsIn&&!existingMlbPicks?.picks?.length){
          const sorted=d.mlb.rankings;
          const autoSave=[];
          ANALYSTS.forEach(analyst=>{
            try{
              const strategies=[
                {sort:(a,b)=>Number(b.hrScore)-Number(a.hrScore),filter:p=>p.hrScore>=35},
                {sort:(a,b)=>(b.hrDetails?.l7HR||0)-(a.hrDetails?.l7HR||0)||(Number(b.hrScore)-Number(a.hrScore)),filter:p=>(p.hrDetails?.l7HR||0)>=1},
                {sort:(a,b)=>(parseFloat(b.hrDetails?.pitHR9||0))-(parseFloat(a.hrDetails?.pitHR9||0))||(Number(b.hrScore)-Number(a.hrScore)),filter:p=>parseFloat(p.hrDetails?.pitHR9||0)>=1.2},
                {sort:(a,b)=>{const eA=(a.hrDetails?.windScore||0)+(a.park?.f||100)/10;const eB=(b.hrDetails?.windScore||0)+(b.park?.f||100)/10;return eB-eA||(Number(b.hrScore)-Number(a.hrScore));},filter:p=>(p.park?.f||100)>=95},
                {sort:(a,b)=>(parseFloat(b.hrDetails?.avgSeasonHR||0))-(parseFloat(a.hrDetails?.avgSeasonHR||0))||(Number(b.hrScore)-Number(a.hrScore)),filter:p=>parseFloat(p.hrDetails?.avgSeasonHR||0)>=15},
              ];
              const strat=strategies[analyst.strategy];
              const n=6;
              const eligible=[...sorted].filter(p=>p.gameSt!=="final"&&!p.preLineup&&strat.filter(p)).sort(strat.sort);
              const fallback=[...sorted].filter(p=>!p.preLineup&&!eligible.find(e=>e.id===p.id)).sort(strat.sort);
              const pool=[...eligible,...fallback];
              const usedGames=new Set();
              const usedPlayers=new Set();
              const picks=[];
              // First pass — one player per game
              for(const p of pool){
                if(picks.length>=n) break;
                if(!usedGames.has(p.gamePk)&&!usedPlayers.has(String(p.id))){
                  usedGames.add(p.gamePk);
                  usedPlayers.add(String(p.id));
                  const dd=p.hrDetails||{};
                  const reasons=[];
                  if(dd.l7HR>=2) reasons.push(`${dd.l7HR} HR last 7`);
                  if(dd.careerTier==="ELITE") reasons.push("elite power");
                  if(parseFloat(dd.pitHR9||0)>=1.5) reasons.push(`SP ${dd.pitHR9} HR/9`);
                  if(p.park?.f>=110) reasons.push("HR park");
                  if(dd.windScore>0) reasons.push("wind out");
                  const origRank=sorted.findIndex(t=>t.id===p.id)+1;
                  picks.push({rank:origRank,name:p.name,team:p.team,id:p.id,opp:p.oppSP?.name||"TBD",score:p.hrScore,reason:reasons.length?reasons.join(" · "):"Strong overall matchup",gameTime:p.gameTime});
                }
              }
              // Second pass — fill remaining slots, allow multiple per game
              if(picks.length<n){
                for(const p of pool){
                  if(picks.length>=n) break;
                  if(!usedPlayers.has(String(p.id))){
                    usedPlayers.add(String(p.id));
                    const dd=p.hrDetails||{};
                    const reasons=[];
                    if(dd.l7HR>=2) reasons.push(`${dd.l7HR} HR last 7`);
                    if(dd.careerTier==="ELITE") reasons.push("elite power");
                    if(parseFloat(dd.pitHR9||0)>=1.5) reasons.push(`SP ${dd.pitHR9} HR/9`);
                    if(p.park?.f>=110) reasons.push("HR park");
                    if(dd.windScore>0) reasons.push("wind out");
                    const origRank=sorted.findIndex(t=>t.id===p.id)+1;
                    picks.push({rank:origRank,name:p.name,team:p.team,id:p.id,opp:p.oppSP?.name||"TBD",score:p.hrScore,reason:reasons.length?reasons.join(" · "):"Strong overall matchup",gameTime:p.gameTime});
                  }
                }
              }
              if(picks.length>0) autoSave.push({analystId:analyst.id,size:n,picks});
            }catch(e){}
          });
          if(autoSave.length>0){
            const picksMap={};
            autoSave.forEach(({analystId,size,picks})=>{
              picksMap[analystId]={analystId,size,picks};
              fetch(`/api/picks?sport=mlb`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({analystId,size,picks})}).catch(()=>{});
            });
            setTodayPicks(picksMap);
          }
        }
        // Lock picks only when ALL lineups are in
        if(allLineupsIn) setParlayLocked(true);
        return;
      }
    }catch(e){}
    // Feed not ready — retry in 30s
    setLoadingTop(false);
    setTopLoaded(false);
    setTopTargets([]);
    setTimeout(()=>{ if(!topLoaded) loadTopTargets(); }, 30000);
    return;
    // eslint-disable-next-line no-unreachable
    // Fallback — compute client-side if feed not ready
    const all=[];
    // Lock wind for ALL games once ANY game has started — prevents wind from shifting rankings mid-day
    const anyGameStarted=games.some(g=>{const s=g.status?.abstractGameState||"Preview";return s==="Live"||s==="Final";});
    for(const g of games){
      const ha=g.teams.home.team.abbreviation,aa=g.teams.away.team.abbreviation;
      const hp=g.teams.home.probablePitcher,ap=g.teams.away.probablePitcher;
      const hLU=g.lineups?.homePlayers||[],aLU=g.lineups?.awayPlayers||[];
      if(!hLU.length&&!aLU.length)continue;
      const isDome=DOMES.has(ha);
      const gameSt=g.status?.abstractGameState||"Preview";
      const gameStarted=gameSt==="Live"||gameSt==="Final";
      // Lock wind for ALL games once any game starts — prevents wind shifting rankings mid-day
      // Parse wind — score frozen at load time in hrDetails, label shows current mph + lock
      const rawWind=isDome?{mph:0,label:"🏟 Dome",score:0}:g.weather?.wind?windParse(g.weather.wind):{mph:0,label:"Pending",score:0};
      const wind=(anyGameStarted||gameStarted)?{...rawWind,label:`🔒 ${rawWind.mph>0?rawWind.mph+"mph":"Locked"}`}:rawWind;
      const park={f:PF[ha]||100,rank:PFR[ha]||15};
      // Pitchers may not be set for future dates
      const[hPD,aPD]=await Promise.all([
        hp?.id?fetchPitcher(hp.id,hp):Promise.resolve({name:"TBD",hand:"?",era:"—",whip:"—",ip:"0",hr9:"—",splits:{vsL:null,vsR:null}}),
        ap?.id?fetchPitcher(ap.id,ap):Promise.resolve({name:"TBD",hand:"?",era:"—",whip:"—",ip:"0",hr9:"—",splits:{vsL:null,vsR:null}}),
      ]);
      const allB=[
        ...hLU.map((b,i)=>({b,facePid:ap?.id,facePHand:aPD?.hand,order:i+1,team:ha,opp:aa,oppSP:aPD,park,wind,isDome,pitcherSplits:aPD?.splits,pitcherHR9:aPD?.hr9,pitcherWHIP:aPD?.whip})),
        ...aLU.map((b,i)=>({b,facePid:hp?.id,facePHand:hPD?.hand,order:i+1,team:aa,opp:ha,oppSP:hPD,park,wind,isDome,pitcherSplits:hPD?.splits,pitcherHR9:hPD?.hr9,pitcherWHIP:hPD?.whip})),
      ];
      const bRes=await Promise.all(allB.map(({b,facePid,facePHand,order,pitcherSplits})=>fetchBatter(b,facePid,facePHand,order,pitcherSplits)));
      bRes.forEach((ps,i)=>{
        if(!ps||ps.error)return;
        const meta=allB[i];
        ps.pitcherHR9=meta.pitcherHR9;
        ps.pitcherWHIP=meta.pitcherWHIP;
        const gameStatus=g.status?.abstractGameState||"Preview";
        const gameSt=gameStatus==="Live"?"live":gameStatus==="Final"?"final":"upcoming";
        // Use cached score if already computed — prevents score drift on re-runs
        // Exception: if the opposing pitcher changed, invalidate cache for these batters
        const cachedEntry=playerStatsCache.current[ps.id];
        // Only allow pitcher change invalidation for upcoming games — never mid-game or final
        // Once a game starts, scores are locked regardless of pitcher changes
        const gameIsActive=gameSt==="live"||gameSt==="final";
        const pitcherChanged=!gameIsActive&&cachedEntry?._pitcherId!==undefined&&cachedEntry._pitcherId!==(meta.facePid||null);
        if(pitcherChanged){
          delete playerStatsCache.current[ps.id]._hrScore;
          delete playerStatsCache.current[ps.id]._hrDetails;
          delete playerStatsCache.current[ps.id]._pitcherId;
          if(meta.facePid) delete pitcherStatsCache.current[meta.facePid];
        }
        if(!pitcherChanged&&cachedEntry?._hrScore!=null){
          all.push({...ps,hrScore:cachedEntry._hrScore,hrDetails:cachedEntry._hrDetails,team:meta.team,oppSP:meta.oppSP,park:meta.park,wind:meta.wind,isDome:meta.isDome,gameTime:fmtTime(g.gameDate),gamePk:String(g.gamePk),gameSt});
          return;
        }
        const sc=scHR(ps,meta.park,meta.wind,meta.pitcherHR9);
        if(sc==null)return;
        // Freeze score in cache — store pitcher ID used so we can detect SP changes
        playerStatsCache.current[ps.id]._hrScore=sc.total;
        playerStatsCache.current[ps.id]._hrDetails=sc;
        playerStatsCache.current[ps.id]._pitcherId=meta.facePid||null;
        all.push({...ps,hrScore:sc.total,hrDetails:sc,team:meta.team,oppSP:meta.oppSP,park:meta.park,wind:meta.wind,isDome:meta.isDome,gameTime:fmtTime(g.gameDate),gamePk:String(g.gamePk),gameSt});
      });
    }
    // Deduplicate by player ID (MLB API sometimes returns same player in multiple lineups)
    const seen=new Set();
    const deduped=all.filter(p=>{if(seen.has(p.id))return false;seen.add(p.id);return true;});
    const sorted=[...deduped].sort((a,b)=>Number(b.hrScore)-Number(a.hrScore));
    setTopTargets(sorted);

    // Lock after every load — new lineups detected in useEffect will unlock for recalc
    // Count individual team lineups (each game has 2 teams)
    const lineupCount=games.reduce((a,g)=>{
      if(g.lineups?.homePlayers?.length) a++;
      if(g.lineups?.awayPlayers?.length) a++;
      return a;
    },0);
    const totalLineups=games.length*2;
    const allLineupsIn=lineupCount>=totalLineups&&totalLineups>0;
    const lockTime=new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",timeZone:"America/New_York"});
    setRankLockedAt(allLineupsIn
      ?`${lockTime} ET · All ${games.length * 2} lineups posted (${games.length} games)`
      :`${lockTime} ET · ${lineupCount}/${totalLineups} lineups · Updates when new lineups post`
    );
    setTopLoaded(true); // ALWAYS lock — live stats never affect rankings
    // Auto-generate + save all 5 analysts' picks once all lineups are in
    if(allLineupsIn){
      setParlayLocked(true);
      // Check Redis if already saved today
      const existingMlbPicks=await fetch(`/api/picks?sport=mlb`).then(r=>r.json()).catch(()=>({picks:[]}));
      if(!existingMlbPicks?.picks?.length){
        const autoSave=[];
        ANALYSTS.forEach(analyst=>{
          try{
            const strategies=[
              {sort:(a,b)=>Number(b.hrScore)-Number(a.hrScore),filter:p=>p.hrScore>=35},
              {sort:(a,b)=>(b.hrDetails?.l7HR||0)-(a.hrDetails?.l7HR||0)||(Number(b.hrScore)-Number(a.hrScore)),filter:p=>(p.hrDetails?.l7HR||0)>=1},
              {sort:(a,b)=>(parseFloat(b.hrDetails?.pitHR9||0))-(parseFloat(a.hrDetails?.pitHR9||0))||(Number(b.hrScore)-Number(a.hrScore)),filter:p=>parseFloat(p.hrDetails?.pitHR9||0)>=1.2},
              {sort:(a,b)=>{const eA=(a.hrDetails?.windScore||0)+(a.park?.f||100)/10;const eB=(b.hrDetails?.windScore||0)+(b.park?.f||100)/10;return eB-eA||(Number(b.hrScore)-Number(a.hrScore));},filter:p=>(p.park?.f||100)>=95},
              {sort:(a,b)=>(parseFloat(b.hrDetails?.avgSeasonHR||0))-(parseFloat(a.hrDetails?.avgSeasonHR||0))||(Number(b.hrScore)-Number(a.hrScore)),filter:p=>parseFloat(p.hrDetails?.avgSeasonHR||0)>=15},
            ];
            const strat=strategies[analyst.strategy];
            const n=6; // save 6 picks max so resizing to any size works
            const eligible=[...sorted].filter(p=>!p.preLineup&&strat.filter(p)).sort(strat.sort);
            // Fallback pool — all players sorted by strategy if filtered pool is too small
            const fallback=[...sorted].filter(p=>!p.preLineup&&!eligible.find(e=>e.id===p.id)).sort(strat.sort);
            const pool=[...eligible,...fallback];
            const usedGames=new Set();
            const picks=[];
            for(const p of pool){
              if(picks.length>=n) break;
              if(!usedGames.has(p.gamePk)){
                usedGames.add(p.gamePk);
                const d=p.hrDetails||{};
                const reasons=[];
                if(d.l7HR>=2) reasons.push(`${d.l7HR} HR last 7`);
                if(d.careerTier==="ELITE") reasons.push("elite power");
                if(parseFloat(d.pitHR9||0)>=1.5) reasons.push(`SP ${d.pitHR9} HR/9`);
                if(p.park?.f>=110) reasons.push("HR park");
                if(d.windScore>0) reasons.push("wind out");
                const origRank=sorted.findIndex(t=>t.id===p.id)+1;
                picks.push({rank:origRank,name:p.name,team:p.team,id:p.id,opp:p.oppSP?.name||"TBD",score:p.hrScore,reason:reasons.length?reasons.join(" · "):"Strong overall matchup",gameTime:p.gameTime});
              }
            }
            if(picks.length>0) autoSave.push({analystId:analyst.id,size:n,picks});
          }catch(e){}
        });
        if(autoSave.length>0){
          // Cache in state + save to Redis
          const picksMap={};
          autoSave.forEach(({analystId,size,picks})=>{
            picksMap[analystId]={analystId,size,picks};
            fetch(`/api/picks?sport=mlb`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({analystId,size,picks})}).catch(()=>{});
          });
          setTodayPicks(picksMap);
        }
      }
    }
    setLoadingTop(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[games,topLoaded,selDate]);

  // ── HR Row ────────────────────────────────────────────────────
  const renderHRRow=(player,park,wind,order)=>{
    const id=player.id;const s=pstats[id];
    const score=s?scHR({...s,order},park,wind,s.pitcherHR9):null;
    const total=score?.total;const clr=total!=null?scoreColor(total):"#3a5070";
    const tierC=score?.careerTier==="ELITE"?"#00e676":score?.careerTier==="ABOVE AVG"?"#ffb020":score?.careerTier==="AVERAGE"?"#7090c0":"#3a5070";
    const windC=score?.windScore>0?"#00e676":score?.windScore<0?"#ff4060":"#7080a0";
    return(
      <tr key={id}>
        <td style={{padding:"8px 4px",fontFamily:"monospace",fontSize:11,color:"#2e4060",textAlign:"center",width:22}}>{order}</td>
        <td style={{padding:"8px 6px",minWidth:155}}>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:total>=60?"#00e676":total>=42?"#ffb020":"#ff4060",flexShrink:0,boxShadow:total>=60?"0 0 6px #00e676":"none"}}/>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:"#d0dff0"}}>{player.fullName||s?.name||"—"}</div>
              {s&&!s.error&&<div style={{fontSize:9,color:"#3a5070",marginTop:1}}>#{order} · {s.batterHand==="L"?"LHB":"RHB"} · S{s.ssY}</div>}
            </div>
          </div>
        </td>
        <td style={{padding:"8px 6px",width:46}}>
          <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:40,height:26,borderRadius:4,background:total!=null?clr+"18":"#0e1828",border:`1px solid ${total!=null?clr+"55":"#1a2540"}`,fontFamily:"monospace",fontSize:14,fontWeight:700,color:clr}}>
            {loadingP?"·":total!=null?total:"—"}
          </div>
        </td>
        {loadingP?[0,1,2,3,4,5,6,7].map(i=><td key={i} style={{padding:"8px 6px"}}><span style={{fontFamily:"monospace",color:"#1e3050",fontSize:11}}>···</span></td>):
         !s||s.error?<td colSpan={8} style={{padding:"8px",fontSize:11,color:"#2a3a50",fontFamily:"monospace"}}>no data</td>:
         score==null?<td colSpan={8}/>:<>
          {/* Career Power */}
          <td style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
            <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:tierC}}>{score.avgSeasonHR}/yr</div>
            <div style={{fontSize:9,color:tierC,marginTop:1,opacity:0.8}}>{score.careerTier}</div>
          </td>
          {/* Last 7 HR */}
          <td style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
            <div style={{fontFamily:"monospace",fontSize:14,fontWeight:700,color:score.l7HR>=3?"#00e676":score.l7HR>=2?"#7eff8a":score.l7HR>=1?"#ffb020":"#4a6080"}}>{score.l7HR} HR</div>
            <div style={{fontSize:9,color:"#344560",marginTop:1}}>LAST 7</div>
          </td>
          {/* Park */}
          <td style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
            <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:score.parkF>=120?"#00e676":score.parkF>=108?"#7eff8a":score.parkF>=95?"#ffb020":"#ff4060"}}>{score.parkF}</div>
            <div style={{fontSize:9,color:"#344560",marginTop:1}}>PARK #{score.parkR}/30</div>
          </td>
          {/* Wind */}
          <td style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
            <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:windC}}>{score.windLabel}</div>
            <div style={{fontSize:9,color:windC,marginTop:1,opacity:0.7}}>{score.windScore>0?"HR BOOST":score.windScore<0?"HR SUPPRESS":"NEUTRAL"}</div>
          </td>
          {/* Batter Split */}
          <td style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
            <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:score.platAB>=1?(score.platHR/score.platAB>=0.05?"#00e676":score.platHR/score.platAB>=0.03?"#ffb020":"#4a6080"):"#3a5070"}}>{score.platAB>0?`${score.platHR}HR/${score.platAB}AB${score.platAB<10?"*":""}` :"No data"}</div>
            <div style={{fontSize:9,color:"#344560",marginTop:1}}>BAT {score.batterHand} vs {score.platHand==="vl"?"LHP":"RHP"}{score.platAB>0&&score.platAB<10?" · small sample":""}</div>
          </td>
          {/* Pitcher Split */}
          <td style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
            <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:score.pitSplitHRRate?(parseFloat(score.pitSplitHRRate)>=5?"#00e676":parseFloat(score.pitSplitHRRate)>=3?"#ffb020":"#4a6080"):"#2a3a50"}}>{score.pitSplitBF>0?`${score.pitSplitHR}HR/${score.pitSplitBF}BF${score.pitSplitBF<10?"*":""}` :"—"}</div>
            <div style={{fontSize:9,color:"#344560",marginTop:1}}>PIT vs {score.batterHand==="L"?"LHB":"RHB"}{score.pitSplitBF>0&&score.pitSplitBF<10?" · small sample":""}</div>
          </td>
          {/* SP HR/9 */}
          <td style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
            <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:parseFloat(score.pitHR9)>=1.8?"#00e676":parseFloat(score.pitHR9)>=1.2?"#ffb020":"#4a6080"}}>{score.pitHR9||"—"}</div>
            <div style={{fontSize:9,color:"#344560",marginTop:1}}>SP HR/9</div>
          </td>

          {/* vs Starter */}
          <td style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
            <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:score.vsHR>=2?"#00e676":score.vsHR>=1?"#ffb020":"#4a6080"}}>{score.vsAB>0?`${score.vsHR}HR/${score.vsAB}AB`:"No hist."}</div>
            <div style={{fontSize:9,color:"#344560",marginTop:1}}>VS STARTER</div>
          </td>
         </>}
      </tr>
    );
  };

  const renderStatRow=(player,park,wind,order)=>{
    const id=player.id;const s=pstats[id];
    const score=s?getScore({...s,order},park,wind,s.pitcherHR9):null;
    const total=score?.total;const clr=total!=null?scoreColor(total):"#3a5070";
    let cells=null;
    if(s&&!s.error&&score){
      if(tab==="H"){const{avg,l7H,l7Avg,order:ord,vsWHIP,vsBAA}=score;cells=[{v:avg,c:parseFloat(avg)>=0.300?"#00e676":parseFloat(avg)>=0.270?"#ffb020":"#4a6080",l:"AVG"},{v:l7H,c:l7H>=10?"#00e676":l7H>=7?"#ffb020":"#4a6080",l:"LAST 7 H"},{v:l7Avg,c:parseFloat(l7Avg)>=0.300?"#00e676":parseFloat(l7Avg)>=0.260?"#ffb020":"#4a6080",l:"L7 AVG"},{v:`#${ord}`,c:ord===1?"#00e676":ord<=3?"#7eff8a":ord<=6?"#ffb020":"#4a6080",l:"ORDER"},{v:vsWHIP,c:parseFloat(vsWHIP)>=1.40?"#00e676":parseFloat(vsWHIP)>=1.20?"#ffb020":"#4a6080",l:"SP WHIP"},{v:vsBAA||"---",c:vsBAA&&parseFloat(vsBAA)>=0.280?"#00e676":vsBAA&&parseFloat(vsBAA)>=0.250?"#ffb020":"#4a6080",l:"SP BAA"}];}else if(tab==="RBI"){const{order:ord,l7RBI,seasonRBI,ops,g}=score;cells=[{v:`#${ord}`,c:ord>=3&&ord<=5?"#00e676":ord<=6?"#ffb020":"#4a6080",l:"ORDER"},{v:l7RBI,c:l7RBI>=5?"#00e676":l7RBI>=3?"#ffb020":"#4a6080",l:"LAST 7 RBI"},{v:seasonRBI,c:seasonRBI>=40?"#00e676":seasonRBI>=15?"#ffb020":"#4a6080",l:"SSN RBI"},{v:ops,c:parseFloat(ops)>=0.900?"#00e676":parseFloat(ops)>=0.750?"#ffb020":"#ff4060",l:"OPS"},{v:g>0?(seasonRBI/g).toFixed(2):"---",c:"#7080a0",l:"RBI/G"}];}
      else if(tab==="SB"){const{order:ord,seasonSB,cs,sbRate,l7SB}=score;cells=[{v:`#${ord}`,c:ord<=2?"#00e676":ord<=4?"#ffb020":"#4a6080",l:"ORDER"},{v:seasonSB,c:seasonSB>=25?"#00e676":seasonSB>=10?"#ffb020":"#4a6080",l:"SSN SB"},{v:sbRate,c:parseFloat(sbRate)>=80?"#00e676":"#ffb020",l:"SB%"},{v:cs,c:cs>4?"#ff4060":"#4a6080",l:"CS"},{v:l7SB,c:l7SB>=2?"#00e676":l7SB>=1?"#ffb020":"#4a6080",l:"LAST 7 SB"}];}
      else if(tab==="BB"){const{seasonBB,bbPerGame,l7BB,obp,order:ord}=score;cells=[{v:`#${ord}`,c:ord<=2?"#00e676":ord<=4?"#ffb020":"#4a6080",l:"ORDER"},{v:seasonBB,c:seasonBB>=60?"#00e676":seasonBB>=35?"#ffb020":"#4a6080",l:"SSN BB"},{v:bbPerGame,c:parseFloat(bbPerGame)>=0.8?"#00e676":parseFloat(bbPerGame)>=0.5?"#ffb020":"#4a6080",l:"BB/G"},{v:l7BB,c:l7BB>=5?"#00e676":l7BB>=3?"#ffb020":"#4a6080",l:"LAST 7 BB"},{v:obp,c:parseFloat(obp)>=0.380?"#00e676":parseFloat(obp)>=0.330?"#ffb020":"#4a6080",l:"OBP"}];}
      else if(tab==="XBH"){const{seasonXBH,l7XBH,vsOPS,parkF,parkR}=score;cells=[{v:l7XBH,c:l7XBH>=4?"#00e676":l7XBH>=2?"#ffb020":"#4a6080",l:"LAST 7 XBH"},{v:seasonXBH,c:seasonXBH>=40?"#00e676":seasonXBH>=20?"#ffb020":"#4a6080",l:"SSN XBH"},{v:vsOPS,c:parseFloat(vsOPS)>=0.850?"#00e676":parseFloat(vsOPS)>=0.650?"#ffb020":"#4a6080",l:"VS SP OPS"},{v:`${parkF} #${parkR}/30`,c:parkF>=115?"#00e676":parkF>=105?"#7eff8a":parkF>=95?"#ffb020":"#ff4060",l:"PARK"},{v:"",c:"transparent",l:""}];}
    }
    return(
      <tr key={id}>
        <td style={{padding:"8px 4px",fontFamily:"monospace",fontSize:11,color:"#2e4060",textAlign:"center",width:22}}>{order}</td>
        <td style={{padding:"8px 6px",minWidth:150}}><div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:6,height:6,borderRadius:"50%",background:total>=60?"#00e676":total>=42?"#ffb020":"#ff4060",flexShrink:0,boxShadow:total>=60?"0 0 5px #00e676":"none"}}/><span style={{fontSize:14,fontWeight:700,color:"#d0dff0"}}>{player.fullName||s?.name||"—"}</span></div></td>
        <td style={{padding:"8px 6px",width:46}}><div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:40,height:26,borderRadius:4,background:total!=null?clr+"18":"#0e1828",border:`1px solid ${total!=null?clr+"55":"#1a2540"}`,fontFamily:"monospace",fontSize:14,fontWeight:700,color:clr}}>{loadingP?"·":total!=null?total:"—"}</div></td>
        {loadingP?[0,1,2,3,4].map(i=><td key={i} style={{padding:"8px 6px"}}><span style={{fontFamily:"monospace",color:"#1e3050"}}>···</span></td>):
         !s||s.error?<td colSpan={5} style={{padding:"8px",fontSize:11,color:"#2a3a50",fontFamily:"monospace"}}>no data</td>:
         cells?cells.map((f,i)=><td key={i} style={{padding:"8px 8px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:f.c}}>{String(f.v??'')}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>{f.l}</div></td>):<td colSpan={5}/>}
      </tr>
    );
  };

  const renderLineup=(players,park,wind,label,pitcherHand,pitcherHR9)=>{
    const hasLU=players&&players.length>0;
    const sorted=!hasLU?[]:([...players].map((p,i)=>({...p,_order:i+1})).sort((a,b)=>{
      const sa=pstats[a.id],sb=pstats[b.id];
      const fa=sa?getScore({...sa,order:a._order},park,wind,sa.pitcherHR9):null;
      const fb=sb?getScore({...sb,order:b._order},park,wind,sb.pitcherHR9):null;
      return(fb?.total||0)-(fa?.total||0);
    }));
    const thS={fontSize:9,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"5px 8px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap"};
    const hrH=["#","Player","Score","Career Pwr","Last 7","Park","Wind","Bat Split","Pit Split","SP HR/9","SP vs Hand","vs Starter"];
    const otherH={H:["#","Player","Score","AVG","Last 7 H","L7 AVG","Order","SP WHIP","SP BAA"],RBI:["#","Player","Score","Order","L7 RBI","Ssn RBI","OPS","RBI/G"],SB:["#","Player","Score","Order","Ssn SB","SB%","CS","L7 SB"],XBH:["#","Player","Score","L7 XBH","Ssn XBH","vs OPS","Park",""],BB:["#","Player","Score","Order","Ssn BB","BB/G","L7 BB","OBP"]};
    return(
      <div style={{marginBottom:20}}>
        <div style={{fontSize:11,letterSpacing:2,fontWeight:700,color:"#3a5570",padding:"5px 2px",borderBottom:"1px solid #101c30",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span>{label}</span>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {pitcherHR9&&pitcherHR9!=="---"&&<span style={{fontSize:10,color:parseFloat(pitcherHR9)>=1.8?"#00e676":parseFloat(pitcherHR9)>=1.2?"#ffb020":"#4a6080",background:"#0a1020",border:"1px solid #1a2a3a",borderRadius:3,padding:"2px 7px",letterSpacing:1}}>SP HR/9: {pitcherHR9}</span>}
            {pitcherHand&&<span style={{fontSize:10,color:"#00c8ff",background:"#0a1828",border:"1px solid #0e2040",borderRadius:3,padding:"2px 7px",letterSpacing:1}}>FACING {pitcherHand==="L"?"LHP":"RHP"}</span>}
          </div>
        </div>
        {!hasLU?<div style={{textAlign:"center",padding:"18px",color:"#243650",fontSize:12,border:"1px dashed #111e30",borderRadius:5}}>⚾ Lineup not yet posted</div>:(
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:tab==="HR"?880:560}}>
              <thead><tr>{(tab==="HR"?hrH:otherH[tab]||[]).map((h,i)=><th key={i} style={thS}>{h}</th>)}</tr></thead>
              <tbody>{sorted.map(p=>tab==="HR"?renderHRRow(p,park,wind,p._order):renderStatRow(p,park,wind,p._order))}</tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Determine game status for a player based on liveScores
  const getPlayerGameStatus=(p)=>{
    if(!p.gamePk) return "upcoming";
    const pk=String(p.gamePk);
    // check liveScores (polled every 60s)
    const ls=liveScores[pk]||liveScores[Number(p.gamePk)];
    if(ls){
      const s=(ls.status||"").toLowerCase();
      if(s==="live"||s==="in progress") return "live";
      if(s==="final"||s==="game over"||s==="completed") return "final";
      if(s==="preview"||s==="scheduled"||s==="pre-game") return "upcoming";
    }
    // check live games array (also updated every 60s)
    const g=games.find(gm=>String(gm.gamePk)===pk);
    if(g){
      const s=(g.status?.abstractGameState||"").toLowerCase();
      if(s==="live") return "live";
      if(s==="final") return "final";
    }
    return "upcoming";
  };

  const applyHRFilter=(p)=>{
    if(hrFilter==="all") return true;
    if(hrFilter==="hit"){
      // Only show players with a confirmed HR today (count > 0 in hrResults)
      const r=hrResults[String(p.id)];
      return r&&r.count>0;
    }
    const st=getPlayerGameStatus(p);
    return st===hrFilter;
  };

  // ── Pitcher Splits leaderboard ─────────────────────────────────────
  // Today's starters sorted by their L vs R split gaps. The bigger the
  // gap, the more exploitable the matchup — pick a batter on the side
  // where the pitcher's stats are worst.
  const renderPitcherSplits=()=>{
    const thS={fontSize:9,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"6px 10px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap",cursor:"pointer",userSelect:"none"};
    const sortIcon=(col)=>pitcherSplitsSort.col===col?(pitcherSplitsSort.dir==="desc"?" ▼":" ▲"):"";
    const sorted=[...pitcherSplits].sort((a,b)=>{
      const c=pitcherSplitsSort.col,dir=pitcherSplitsSort.dir==="desc"?-1:1;
      const av=c==="opsGap"?a.opsGap:c==="baGap"?a.baGap:c==="hr9Gap"?a.hr9Gap:c==="opsL"?a.vsL?.ops:c==="opsR"?a.vsR?.ops:c==="baL"?a.vsL?.ba:c==="baR"?a.vsR?.ba:c==="hrL"?a.vsL?.hr:c==="hrR"?a.vsR?.hr:c==="hr9"?parseFloat(a.hr9):0;
      const bv=c==="opsGap"?b.opsGap:c==="baGap"?b.baGap:c==="hr9Gap"?b.hr9Gap:c==="opsL"?b.vsL?.ops:c==="opsR"?b.vsR?.ops:c==="baL"?b.vsL?.ba:c==="baR"?b.vsR?.ba:c==="hrL"?b.vsL?.hr:c==="hrR"?b.vsR?.hr:c==="hr9"?parseFloat(b.hr9):0;
      return ((av||0)-(bv||0))*dir;
    });
    const setSort=col=>setPitcherSplitsSort(prev=>({col,dir:prev.col===col&&prev.dir==="desc"?"asc":"desc"}));
    return(
      <div style={{padding:"14px 16px 32px"}}>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:22,fontWeight:900,color:"#c060ff",letterSpacing:2}}>🎯 PITCHER SPLITS</div>
          <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>TODAY'S STARTERS · BIGGEST L vs R GAPS · ATTACK THE WEAK SIDE</div>
        </div>
        {sorted.length===0?(
          <div style={{padding:"40px 20px",textAlign:"center",color:"#3a5070",fontSize:13,fontFamily:"monospace"}}>
            {loadingPitcherSplits?"Loading...":"No qualifying starters yet today (need 30+ batters faced on each side this season)."}
          </div>
        ):(
        <div style={{overflowX:"auto",border:"1px solid #0c1828",borderRadius:6,background:"#080d14"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontFamily:"monospace"}}>
            <thead>
              <tr style={{background:"#0a1220"}}>
                <th style={{...thS,cursor:"default"}}>PITCHER</th>
                <th style={{...thS,cursor:"default"}}>vs SLATE</th>
                <th style={thS} onClick={()=>setSort("baL")}>BA vs L{sortIcon("baL")}</th>
                <th style={thS} onClick={()=>setSort("hrL")}>HR vs L{sortIcon("hrL")}</th>
                <th style={thS} onClick={()=>setSort("opsL")}>OPS vs L{sortIcon("opsL")}</th>
                <th style={thS} onClick={()=>setSort("baR")}>BA vs R{sortIcon("baR")}</th>
                <th style={thS} onClick={()=>setSort("hrR")}>HR vs R{sortIcon("hrR")}</th>
                <th style={thS} onClick={()=>setSort("opsR")}>OPS vs R{sortIcon("opsR")}</th>
                <th style={thS} onClick={()=>setSort("opsGap")}>OPS GAP{sortIcon("opsGap")}</th>
                <th style={thS} onClick={()=>setSort("baGap")}>BA GAP{sortIcon("baGap")}</th>
                <th style={thS} onClick={()=>setSort("hr9Gap")}>HR9 GAP{sortIcon("hr9Gap")}</th>
                <th style={{...thS,cursor:"default"}}>ATTACK</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p,i)=>{
                const fmt3=v=>v==null?"—":v.toFixed(3).replace(/^0/,"");
                const isLVuln=p.vulnerableSide==="L";
                const lOPS=p.vsL?.ops,rOPS=p.vsR?.ops;
                const lOPSColor=lOPS!=null&&rOPS!=null&&lOPS>rOPS?"#ff4060":"#c8d6e8";
                const rOPSColor=lOPS!=null&&rOPS!=null&&rOPS>lOPS?"#ff4060":"#c8d6e8";
                return(
                  <tr key={p.id} style={{borderBottom:"1px solid #0c1828",background:i%2===0?"#080d14":"#0a1018"}}>
                    <td style={{padding:"10px",whiteSpace:"nowrap"}}>
                      <div style={{fontSize:12,fontWeight:800,color:"#fff"}}>{p.name}</div>
                      <div style={{fontSize:9,color:"#3a5070",marginTop:1}}>{p.team} · {p.hand}HP · {p.hr9} HR/9</div>
                    </td>
                    <td style={{padding:"10px",whiteSpace:"nowrap"}}>
                      <div style={{fontSize:11,color:"#5a7090"}}>vs {p.opp}</div>
                      <div style={{fontSize:9,color:"#3a5070",marginTop:1}}>{p.gameTime}</div>
                    </td>
                    <td style={{padding:"10px",fontSize:12,color:"#c8d6e8"}}>{fmt3(p.vsL?.ba)}</td>
                    <td style={{padding:"10px",fontSize:12,color:"#c8d6e8"}}>{p.vsL?.hr??"—"}</td>
                    <td style={{padding:"10px",fontSize:12,fontWeight:700,color:lOPSColor}}>{fmt3(lOPS)}</td>
                    <td style={{padding:"10px",fontSize:12,color:"#c8d6e8"}}>{fmt3(p.vsR?.ba)}</td>
                    <td style={{padding:"10px",fontSize:12,color:"#c8d6e8"}}>{p.vsR?.hr??"—"}</td>
                    <td style={{padding:"10px",fontSize:12,fontWeight:700,color:rOPSColor}}>{fmt3(rOPS)}</td>
                    <td style={{padding:"10px",fontSize:13,fontWeight:800,color:p.opsGap>=0.200?"#ff4060":p.opsGap>=0.100?"#ffb020":"#c8d6e8"}}>{p.opsGap!=null?"+"+p.opsGap.toFixed(3).replace(/^0/,""):"—"}</td>
                    <td style={{padding:"10px",fontSize:12,color:"#c8d6e8"}}>{p.baGap!=null?"+"+p.baGap.toFixed(3).replace(/^0/,""):"—"}</td>
                    <td style={{padding:"10px",fontSize:12,color:"#c8d6e8"}}>{p.hr9Gap!=null?"+"+p.hr9Gap.toFixed(2):"—"}</td>
                    <td style={{padding:"10px",whiteSpace:"nowrap"}}>
                      <div style={{display:"inline-block",padding:"3px 8px",borderRadius:4,fontSize:11,fontWeight:800,background:isLVuln?"#1a0033":"#001a33",color:isLVuln?"#c060ff":"#00c8ff",border:`1px solid ${isLVuln?"#c060ff60":"#00c8ff60"}`}}>
                        🎯 {isLVuln?"LHB":"RHB"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        <div style={{marginTop:10,fontSize:10,color:"#3a5070",fontFamily:"monospace",lineHeight:1.5}}>
          OPS GAP = |OPS vs L − OPS vs R|. Bigger gap = more exploitable matchup.
          The 🎯 ATTACK column flags which side of the plate to target.
          Min 30 batters faced on each side to qualify.
        </div>
      </div>
    );
  };

  const renderBBTargets=()=>{
    const thS={fontSize:9,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"6px 10px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap"};
    return(
      <div style={{padding:"14px 16px 32px"}}>
        <div style={{marginBottom:12}}>
          <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:2}}>🚶 TOP BB (WALK) TARGETS TODAY</div>
          <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>OBP · BB/GAME · LAST 7 BB · OPPOSING SP BB/9 · LINEUP ORDER · SP WHIP</div>
          {bbLoaded?(
            <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:8,padding:"5px 10px",background:"#080e18",border:"1px solid #1a2a3a",borderRadius:4}}>
              <span style={{fontSize:11,color:"#00e676"}}>🔒</span>
              <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>Rankings locked · {games.reduce((a,g)=>{if(g.lineups?.homePlayers?.length)a++;if(g.lineups?.awayPlayers?.length)a++;return a;},0)}/{games.length*2} lineups posted ({games.length} games)</span>
            </div>
          ):(
            bbTargets.length>0&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:8,padding:"5px 10px",background:"#080e18",border:"1px solid #1a2a3a",borderRadius:4}}>
                <span style={{fontSize:11,color:"#ffb020"}}>⏳</span>
                <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>{games.reduce((a,g)=>{if(g.lineups?.homePlayers?.length)a++;if(g.lineups?.awayPlayers?.length)a++;return a;},0)}/{games.length*2} lineups posted ({games.length} games)</span>
              </div>
            )
          )}
        </div>

        {/* Filter tabs */}
        {bbTargets.length>0&&(
          <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
            {[["all","ALL BATTERS"],["upcoming","⏰ NOT STARTED"],["live","● LIVE"],["final","✓ FINAL"],["walked","🚶 WALKED TODAY"]].map(([f,label])=>{
              const isA=bbFilter===f;
              const walkedCnt=f==="walked"?Object.keys(walkResults).length:null;
              return(
                <button key={f} onClick={()=>setBBFilter(f)} style={{padding:"7px 14px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,letterSpacing:1.2,background:isA?"#081828":"#0c1322",border:`1px solid ${isA?"#00c8ff":"#182030"}`,borderRadius:4,cursor:"pointer",color:isA?"#00c8ff":"#3a5070"}}>
                  {label}{walkedCnt!==null&&walkedCnt>0&&<span style={{marginLeft:6,fontFamily:"monospace",fontSize:11,background:"#0a1a28",border:"1px solid #00c8ff30",borderRadius:3,padding:"1px 6px"}}>{walkedCnt}</span>}
                </button>
              );
            })}
          </div>
        )}
        {loadingBB&&<div style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:7,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}><div style={{width:16,height:16,border:"2px solid #1a2540",borderTopColor:"#00c8ff",borderRadius:"50%",animation:"spin 0.8s linear infinite",flexShrink:0}}/><div><div style={{fontSize:12,color:"#4a6080",letterSpacing:1}}>Scanning {games.length} games · {bbTargets.length} players loaded</div><div style={{fontSize:10,color:"#2a3a50",marginTop:3}}>OBP · BB/game · Last 7 walks · Pitcher BB/9</div></div></div>}
        {!loadingBB&&bbTargets.length===0&&<div style={{textAlign:"center",padding:40,color:"#243650",fontSize:13}}>No lineup data available yet</div>}
        {bbTargets.length>0&&(
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:960}}>
              <thead><tr>
                {[["","RNK"],["","Player"],["","Team"],["","vs SP"],["bbScore","Score"],["obp","OBP"],["bbPerGame","BB/G"],["seasonBB","Season BB"],["l7BB","Last 7 BB"],["spBB9","SP BB/9"],["spWHIP","SP WHIP"]].map(([col,label],i)=>(
                  <th key={i} onClick={col?()=>setBBSort(s=>({col,dir:s.col===col&&s.dir==="desc"?"asc":"desc"})):undefined}
                    style={{...thS,cursor:col?"pointer":"default",color:bbSort.col===col?"#00e676":"#2e4460",userSelect:"none"}}>
                    {label}{col?(bbSort.col===col?(bbSort.dir==="desc"?" ▼":" ▲"):" ⇅"):""}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {(()=>{
                  const getBBVal=(p,col)=>{
                    const d=p.bbDetails||{};
                    if(col==="bbScore") return p.bbScore||0;
                    if(col==="obp") return parseFloat(d.obp)||0;
                    if(col==="bbPerGame") return parseFloat(d.bbPerGame)||0;
                    if(col==="seasonBB") return d.seasonBB||0;
                    if(col==="l7BB") return d.l7BB||0;
                    if(col==="spBB9") return parseFloat(d.spBB9)||0;
                    if(col==="spWHIP") return parseFloat(d.spWHIP)||0;
                    return 0;
                  };
                  const applyBBFilter=(p)=>{
                    if(bbFilter==="all") return true;
                    if(bbFilter==="walked"){
                      const r=walkResults[p.id];
                      return r&&r.count>0;
                    }
                    return p.gameSt===bbFilter;
                  };
                  const sorted=[...bbTargets].filter(applyBBFilter).sort((a,b)=>bbSort.dir==="desc"?getBBVal(b,bbSort.col)-getBBVal(a,bbSort.col):getBBVal(a,bbSort.col)-getBBVal(b,bbSort.col));
                  // Unranked walked players (walked today but not in bbTargets)
                  const unrankedWalked=bbFilter==="walked"
                    ?Object.entries(walkResults)
                        .filter(([id])=>!bbTargets.find(p=>String(p.id)===String(id)))
                        .map(([id,r])=>({id,name:r.name,team:r.team,bbScore:0,bbDetails:null,_unranked:true}))
                    :[];
                  const origSorted=[...bbTargets].sort((a,b)=>b.bbScore-a.bbScore);
                  return [...sorted,...unrankedWalked].map((p)=>{
                    if(p._unranked){
                      const r=walkResults[p.id];
                      return(
                        <tr key={p.id+"u"} style={{borderBottom:"1px solid #0a1020"}}>
                          <td style={{padding:"10px 10px",width:40}}><span style={{fontFamily:"monospace",fontSize:11,color:"#2a3a50"}}>—</span></td>
                          <td style={{padding:"10px 8px"}} colSpan={10}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <img src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_60,q_auto:best/v1/people/${p.id}/headshot/67/current`}
                                alt={p.name} style={{width:40,height:40,borderRadius:"50%",border:"2px solid #00c8ff60",objectFit:"cover",flexShrink:0,background:"#0a1020"}}
                                onError={e=>{e.target.style.display="none";}}/>
                              <div>
                                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                  <span style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{p.name}</span>
                                  <span style={{fontFamily:"monospace",fontSize:11,color:"#7090c0"}}>{p.team}</span>
                                  <div style={{display:"flex",alignItems:"center",gap:3,background:"#0a1a28",border:"1px solid #00c8ff50",borderRadius:4,padding:"1px 8px"}}>
                                    <span style={{fontSize:11}}>🚶</span>
                                    <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#00c8ff"}}>{r?.count||1} BB TODAY</span>
                                  </div>
                                  <span style={{fontSize:10,color:"#2a3a50",fontStyle:"italic"}}>not ranked</span>
                                </div>
                                {r?.game&&<div style={{fontSize:9,color:"#00c8ff",marginTop:1,opacity:0.8}}>✅ {r.game}</div>}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    const origRank=origSorted.findIndex(t=>String(t.id)===String(p.id));
                    const d=p.bbDetails||{};
                    const clr=scoreColor(p.bbScore);
                    const medal=origRank===0?"🥇":origRank===1?"🥈":origRank===2?"🥉":null;
                    const obpC=parseFloat(d.obp)>=0.380?"#00e676":parseFloat(d.obp)>=0.330?"#ffb020":"#7090c0";
                    const bb9C=parseFloat(d.spBB9)>=4?"#00e676":parseFloat(d.spBB9)>=3?"#ffb020":"#4a6080";
                    return(
                      <tr key={p.id} style={{borderBottom:"1px solid #0a1020",background:origRank<3?"#0a0f1e":origRank<10?"#090d18":"transparent"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#0c1428"}
                        onMouseLeave={e=>e.currentTarget.style.background=origRank<3?"#0a0f1e":origRank<10?"#090d18":"transparent"}>
                        <td style={{padding:"10px 10px",width:40}}>{medal?<span style={{fontSize:16}}>{medal}</span>:<span style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#5080c0"}}>#{origRank+1}</span>}</td>
                        <td style={{padding:"10px 8px",minWidth:175}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <img src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_60,q_auto:best/v1/people/${p.id}/headshot/67/current`} alt={p.name} style={{width:origRank<3?44:36,height:origRank<3?44:36,borderRadius:"50%",border:`2px solid ${clr}60`,objectFit:"cover",flexShrink:0,background:"#0a1020"}} onError={e=>{e.target.style.display="none";}}/>
                            <div>
                              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                <div style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{p.name}</div>
                                {p.gameSt==="live"&&<span style={{fontSize:8,color:"#00e676",background:"#00e67615",border:"1px solid #00e67640",borderRadius:2,padding:"1px 4px",fontWeight:700}}>🔴 LIVE</span>}
                                {p.gameSt==="final"&&<span style={{fontSize:8,color:"#4a6080",background:"#1a2030",border:"1px solid #2a3040",borderRadius:2,padding:"1px 4px"}}>FINAL</span>}
                                {walkResults[p.id]?.count>0&&(
                                  <div style={{display:"flex",alignItems:"center",gap:3,background:"#0a1a28",border:"1px solid #00c8ff50",borderRadius:4,padding:"1px 8px"}}>
                                    <span style={{fontSize:11}}>🚶</span>
                                    <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#00c8ff"}}>{walkResults[p.id].count} BB TODAY</span>
                                  </div>
                                )}
                              </div>
                              <div style={{fontSize:10,color:"#3a5070",marginTop:1}}>#{d.order} · {p.gameTime}</div>
                              {walkResults[p.id]?.count>0&&<div style={{fontSize:9,color:"#00c8ff",marginTop:1,opacity:0.8}}>✅ {walkResults[p.id].game}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{padding:"10px 8px",width:44}}><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#7090c0"}}>{p.team}</div></td>
                        <td style={{padding:"10px 8px",minWidth:150,whiteSpace:"nowrap"}}>
                          <div style={{fontSize:12,color:"#c8d6e8",fontWeight:600}}>{p.oppSP?.name||"TBD"}</div>
                          <div style={{display:"flex",gap:4,marginTop:3}}>
                            <div style={{background:p.oppSP?.hand==="L"?"#0d2a0d":"#0d1e36",color:p.oppSP?.hand==="L"?"#00e676":"#00c8ff",fontSize:9,padding:"1px 6px",borderRadius:3,fontWeight:800}}>{p.oppSP?.hand==="L"?"LHP":"RHP"}</div>
                            {d.spBB9&&d.spBB9!=="---"&&<div style={{background:"#0d1a28",color:bb9C,fontSize:9,padding:"1px 6px",borderRadius:3,fontWeight:800}}>BB/9: {d.spBB9}</div>}
                          </div>
                        </td>
                        <td style={{padding:"10px 8px",width:54}}>
                          <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:46,height:32,borderRadius:5,background:clr+"20",border:`1px solid ${clr}60`,fontFamily:"monospace",fontSize:17,fontWeight:700,color:clr}}>{p.bbScore}</div>
                        </td>
                        <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:obpC}}>{d.obp||"---"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>OBP</div></td>
                        <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:parseFloat(d.bbPerGame)>=0.8?"#00e676":parseFloat(d.bbPerGame)>=0.5?"#ffb020":"#4a6080"}}>{d.bbPerGame||"---"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>BB/GAME</div></td>
                        <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,color:"#5070a0"}}>{d.seasonBB||"—"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>SEASON</div></td>
                        <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:d.l7BB>=5?"#00e676":d.l7BB>=3?"#ffb020":"#4a6080"}}>{d.l7BB||0}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>LAST 7</div></td>
                        <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:bb9C}}>{d.spBB9||"---"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>SP BB/9</div></td>
                        <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:parseFloat(d.spWHIP)>=1.4?"#00e676":parseFloat(d.spWHIP)>=1.2?"#ffb020":"#4a6080"}}>{d.spWHIP||"---"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>SP WHIP</div></td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };
  const renderTopTargets=()=>{
    const thS={fontSize:9,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"6px 10px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap"};
    return(
      <div style={{padding:"14px 16px 32px"}}>

        <div style={{marginBottom:12}}>
          <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:2}}>🔥 TOP HR TARGETS TODAY</div>
          <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>
            CAREER POWER · LAST 7 HR · PARK FACTOR · WIND · SPLIT MATCHUP · SP HR-PRONENESS
          </div>
          {rankLockedAt?(
            <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:8,padding:"5px 10px",background:"#080e18",border:"1px solid #1a2a3a",borderRadius:4}}>
              <span style={{fontSize:11,color:"#00e676"}}>🔒</span>
              <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>Rankings locked · Generated at {rankLockedAt}</span>
            </div>
          ):(
            topTargets.length>0&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:8,padding:"5px 10px",background:"#080e18",border:"1px solid #1a2a3a",borderRadius:4}}>
                <span style={{fontSize:11,color:"#ffb020"}}>⏳</span>
                <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>
                  {games.reduce((a,g)=>{if(g.lineups?.homePlayers?.length)a++;if(g.lineups?.awayPlayers?.length)a++;return a;},0)} of {games.length*2} lineups posted · Updates automatically
                </span>
              </div>
            )
          )}
        </div>

{/* filter bar moved to main JSX */}

        {/* ── LIVE HR FEED ── */}
        {(hrFeed.length>0||games.some(g=>g.status?.abstractGameState==="Live"))&&(
          <div style={{background:"#08100a",border:"1px solid #0d2010",borderRadius:7,marginBottom:14,overflow:"hidden"}}>
            {/* Feed header */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 14px",borderBottom:"1px solid #0d2010",cursor:"pointer"}} onClick={()=>setShowFeed(f=>!f)}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:"#00e676",boxShadow:"0 0 6px #00e676",animation:"spin 2s linear infinite"}}/>
                <span style={{fontSize:13,fontWeight:800,color:"#00e676",letterSpacing:1.5}}>LIVE HR FEED</span>
                {hrFeed.length>0&&<span style={{fontFamily:"monospace",fontSize:11,color:"#00e676",background:"#0a2010",border:"1px solid #00e67630",borderRadius:3,padding:"1px 7px"}}>{hrFeed.length} HR TODAY</span>}
              </div>
              <span style={{fontSize:11,color:"#2a4a30",letterSpacing:1}}>{showFeed?"▲ HIDE":"▼ SHOW"}</span>
            </div>
            {showFeed&&(
              hrFeed.length===0?(
                <div style={{padding:"12px 14px",fontFamily:"monospace",fontSize:11,color:"#2a4a30",letterSpacing:1}}>
                  No home runs yet today · Updates every 60 seconds
                </div>
              ):(
                <div style={{maxHeight:280,overflowY:"auto"}}>
                  {hrFeed.map((hr,i)=>{
                    const onListRank=topTargets.findIndex(p=>p.id===hr.id);
                    const isOnList=onListRank>=0&&onListRank<15;
                    const anyRank=topTargets.findIndex(p=>p.id===hr.id);
                    return(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:"1px solid #0a1a0c",background:isOnList?"#0c1e0e":"transparent"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#0e2010"}
                        onMouseLeave={e=>e.currentTarget.style.background=isOnList?"#0c1e0e":"transparent"}>
                        {/* HR icon */}
                        <div style={{fontSize:18,flexShrink:0}}>💥</div>
                        {/* Player info */}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                            <span style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{hr.name}</span>
                            <span style={{fontFamily:"monospace",fontSize:11,color:"#00e676",background:"#0a2010",border:"1px solid #00e67630",borderRadius:3,padding:"1px 6px"}}>{hr.team}</span>
                            {anyRank>=0&&<span style={{fontSize:10,color:anyRank<15?"#ffb020":"#5070a0",background:anyRank<15?"#1a1000":"#0a1020",border:`1px solid ${anyRank<15?"#ffb02030":"#2a4060"}`,borderRadius:3,padding:"1px 6px",fontWeight:700}}>{anyRank<15?"⭐":""} #{anyRank+1} RANKED</span>}
                          </div>
                          <div style={{fontSize:10,color:"#3a5a40",marginTop:2}}>
                            vs {hr.pitcher} · {hr.inning} · {hr.gameStr}
                            {hr.awayScore!=null&&<span style={{marginLeft:6,fontFamily:"monospace",color:"#4a7a50"}}>{hr.awayAbb} {hr.awayScore} – {hr.homeAbb} {hr.homeScore}</span>}
                          </div>
                        </div>
                        {/* Time */}
                        <div style={{fontFamily:"monospace",fontSize:10,color:"#2a4a30",flexShrink:0,textAlign:"right"}}>
                          {hr.isFinal?"Final":hr.inning}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}

        {loadingTop&&<div style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:7,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}><div style={{width:16,height:16,border:"2px solid #1a2540",borderTopColor:"#00c8ff",borderRadius:"50%",animation:"spin 0.8s linear infinite",flexShrink:0}}/><div><div style={{fontSize:12,color:"#4a6080",letterSpacing:1}}>Scanning {games.length} games · {topTargets.length} players loaded</div><div style={{fontSize:10,color:"#2a3a50",marginTop:3}}>Career stats · Last 7 · Park + Wind · Pitcher HR rates · Platoon</div></div></div>}
        {!loadingTop&&topTargets.length===0&&(
          <div style={{textAlign:"center",padding:40}}>
            <div style={{fontSize:24,marginBottom:8}}>⏾</div>
            <div style={{fontSize:14,color:"#00c8ff",fontWeight:800,letterSpacing:1,marginBottom:6}}>LINEUPS PENDING</div>
            <div style={{fontSize:11,color:"#2a3a50"}}>Rankings appear once lineups are posted</div>
            <div style={{fontSize:10,color:"#1a2a40",marginTop:4}}>Typically available 3-4 hours before first pitch</div>
          </div>
        )}
        {topTargets.length>0&&topTargets.every(p=>p.preLineup)&&(
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:"#080e1a",border:"1px solid #1a3050",borderRadius:6,marginBottom:12}}>
            <span style={{fontSize:13}}>📋</span>
            <span style={{fontSize:11,color:"#5090c0"}}>Projected rankings based on roster vs probable pitchers · Updates automatically when lineups post</span>
          </div>
        )}
        {topTargets.length>0&&(
          <div style={{overflowX:"auto"}}>
            <div style={{position:"relative"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:960}}>
              <thead><tr>
                <th style={{...thS,width:32}}></th>
                {[["","RNK"],["","Player"],["","Team"],["","vs SP"],["hrScore","Score"],["avgSeasonHR","Career Pwr"],["l7HR","Last 7 HR"],["parkF","Park"],["windScore","Wind"],["platRate","Bat Split"],["pitSplitHRRate","Pit Split"],["pitHR9","SP HR/9"],["vsStarter","vs Starter"],["bullpen","Bullpen"],["pitchMatch","Pitch Match"]].map(([col,label],i)=>(
                  <th key={i} onClick={col?()=>setMlbSort(s=>({col,dir:s.col===col&&s.dir==="desc"?"asc":"desc"})):undefined}
                    style={{...thS,cursor:col?"pointer":"default",color:mlbSort.col===col?"#00e676":"#2e4460",userSelect:"none"}}>
                    {label}{col?(mlbSort.col===col?(mlbSort.dir==="desc"?" ▼":" ▲"):" ⇅"):""}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {(()=>{
                  const getMlbVal=(p,col)=>{
                    const d=p.hrDetails||{};
                    if(col==="hrScore") return Number(p.hrScore)||0;
                    if(col==="avgSeasonHR") return parseFloat(d.avgSeasonHR)||0;
                    if(col==="l7HR") return d.l7HR||0;
                    if(col==="parkF") return d.parkF||100;
                    if(col==="windScore") return d.windScore||0;
                    if(col==="pitHR9") return parseFloat(d.pitHR9)||0;
                    if(col==="platRate") return d.platAB>=10?(d.platHR/d.platAB):0;
                    if(col==="pitSplitHRRate") return parseFloat(d.pitSplitHRRate)||0;
                    if(col==="vsStarter") return d.vsHR>0?(d.vsHR*10+(d.vsAB>=5?d.vsHR/d.vsAB:0)):0;
                    if(col==="bullpen") return d.bullpenFatigue||0;
                    if(col==="pitchMatch") return d.pitchMatchSc||0;
                    return 0;
                  };
                  const allTargets=mlbIsPro?topTargets:topTargets.slice(0,10);
                  const sorted=[...allTargets].sort((a,b)=>mlbSort.dir==="desc"?getMlbVal(b,mlbSort.col)-getMlbVal(a,mlbSort.col):getMlbVal(a,mlbSort.col)-getMlbVal(b,mlbSort.col));
                  const origSorted=[...allTargets].sort((a,b)=>Number(b.hrScore)-Number(a.hrScore));
                  const ranked=sorted.filter(applyHRFilter);
                  const unranked=hrFilter==="hit"
                    ?Object.entries(hrResults)
                        .filter(([id])=>!topTargets.find(p=>String(p.id)===String(id)))
                        .map(([id,r])=>({id,name:r.name,team:r.team,hrScore:0,hrDetails:null,_unranked:true}))
                    :[];
                  return [...ranked,...unranked].map((p)=>{
                    const origRank=origSorted.findIndex(t=>String(t.id)===String(p.id));
                    if(p._unranked){
                      const r=hrResults[String(p.id)];
                      return(
                        <tr key={p.id+"u"} style={{borderBottom:"1px solid #0a1020"}}>
                          <td style={{padding:"10px 10px",width:40}}><span style={{fontFamily:"monospace",fontSize:11,color:"#2a3a50"}}>—</span></td>
                          <td style={{padding:"10px 8px"}} colSpan={10}>
                            <div style={{display:"flex",alignItems:"center",gap:8}}>
                              <img src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_60,q_auto:best/v1/people/${p.id}/headshot/67/current`}
                                alt={p.name} style={{width:40,height:40,borderRadius:"50%",border:"2px solid #00e67660",objectFit:"cover",flexShrink:0,background:"#0a1020"}}
                                onError={e=>{e.target.style.display="none";}}/>
                              <div>
                                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                  <span style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{p.name}</span>
                                  <span style={{fontFamily:"monospace",fontSize:11,color:"#7090c0"}}>{p.team}</span>
                                  <div style={{display:"flex",alignItems:"center",gap:3,background:"#0a2010",border:"1px solid #00e67650",borderRadius:4,padding:"1px 8px"}}>
                                    <span style={{fontSize:12}}>💥</span>
                                    <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#00e676"}}>{r?.count||1} HR TODAY</span>
                                  </div>
                                  <span style={{fontSize:10,color:"#2a3a50",fontStyle:"italic"}}>not ranked</span>
                                </div>
                                {r?.game&&<div style={{fontSize:9,color:"#00e676",marginTop:1,opacity:0.8}}>✅ {r.game}</div>}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  const d=p.hrDetails;const clr=scoreColor(p.hrScore);
                  const medal=origRank===0?"🥇":origRank===1?"🥈":origRank===2?"🥉":null;
                  const tierC=d.careerTier==="ELITE"?"#00e676":d.careerTier==="ABOVE AVG"?"#ffb020":"#7090c0";
                  const windC=d.windScore>0?"#00e676":d.windScore<0?"#ff4060":"#7080a0";
                  const pkC=d.parkF>=120?"#00e676":d.parkF>=108?"#7eff8a":d.parkF>=95?"#ffb020":"#ff4060";
                  return(
                    <tr key={p.id} style={{borderBottom:"1px solid #0a1020",background:origRank<3?"#0a0f1e":origRank<10?"#090d18":"transparent"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#0c1428"}
                      onMouseLeave={e=>e.currentTarget.style.background=origRank<3?"#0a0f1e":origRank<10?"#090d18":"transparent"}>
                      <td style={{padding:"10px 6px",width:32,textAlign:"center"}}>
                        {mlbIsPro&&<span onClick={()=>toggleWatch(p.id)} style={{fontSize:16,cursor:"pointer",opacity:watchlist.has(String(p.id))?1:0.25,transition:"opacity 0.15s"}} title={watchlist.has(String(p.id))?"Remove from My Picks":"Add to My Picks"}>⭐</span>}
                      </td>
                      <td style={{padding:"10px 10px",width:40}}>{medal?<span style={{fontSize:16}}>{medal}</span>:<span style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#5080c0"}}>#{origRank+1}</span>}</td>
                      <td style={{padding:"10px 8px",minWidth:175}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          {origRank<10&&<img loading="lazy" src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_60,q_auto:best/v1/people/${p.id}/headshot/67/current`} alt={p.name} style={{width:origRank<3?44:36,height:origRank<3?44:36,borderRadius:"50%",border:`2px solid ${clr}60`,objectFit:"cover",flexShrink:0,background:"#0a1020"}} onError={e=>{e.target.style.display="none";}}/>}
                          <div>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                              <div style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{p.name}</div>
                              {p.gameSt==="live"&&<span style={{fontSize:8,color:"#00e676",background:"#00e67615",border:"1px solid #00e67640",borderRadius:2,padding:"1px 4px",fontWeight:700}}>🔴 LIVE</span>}
                              {p.gameSt==="final"&&<span style={{fontSize:8,color:"#4a6080",background:"#1a2030",border:"1px solid #2a3040",borderRadius:2,padding:"1px 4px"}}>FINAL</span>}
                              {hrResults[String(p.id)]&&(
                                <div style={{display:"flex",alignItems:"center",gap:3,background:"#0a2010",border:"1px solid #00e67650",borderRadius:4,padding:"1px 8px"}}>
                                  <span style={{fontSize:12}}>💥</span>
                                  <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#00e676"}}>{hrResults[String(p.id)].count} HR TODAY</span>
                                </div>
                              )}
                            </div>
                            <div style={{fontSize:10,color:"#3a5070",marginTop:1}}>#{p.order} · {d.batterHand==="L"?"LHB":"RHB"} · {p.gameTime}</div>
                            {hrResults[String(p.id)]&&<div style={{fontSize:9,color:"#00e676",marginTop:1,opacity:0.8}}>✅ {hrResults[String(p.id)].game}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{padding:"10px 8px",width:44}}><div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#7090c0"}}>{p.team}</div></td>
                      <td style={{padding:"10px 8px",minWidth:150,whiteSpace:"nowrap"}}>
                        <div style={{fontSize:12,color:"#c8d6e8",fontWeight:600}}>{p.oppSP?.name||"TBD"}</div>
                        <div style={{display:"flex",gap:4,marginTop:3}}>
                          <div style={{background:p.oppSP?.hand==="L"?"#0d2a0d":"#0d1e36",color:p.oppSP?.hand==="L"?"#00e676":"#00c8ff",fontSize:9,padding:"1px 6px",borderRadius:3,fontWeight:800}}>{p.oppSP?.hand==="L"?"LHP":"RHP"}</div>
                          {p.oppSP?.hr9&&p.oppSP.hr9!=="---"&&<div style={{background:"#1a1a0d",color:parseFloat(p.oppSP.hr9)>=1.8?"#00e676":parseFloat(p.oppSP.hr9)>=1.2?"#ffb020":"#4a6080",fontSize:9,padding:"1px 6px",borderRadius:3,fontWeight:800}}>HR/9: {p.oppSP.hr9}</div>}
                        </div>
                      </td>
                      <td style={{padding:"10px 8px",width:54}}>
                        <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:46,height:32,borderRadius:5,background:clr+"20",border:`1px solid ${clr}60`,fontFamily:"monospace",fontSize:17,fontWeight:700,color:clr}}>{p.hrScore}</div>
                      </td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:tierC}}>{d.avgSeasonHR}/yr</div><div style={{fontSize:9,color:tierC,opacity:0.8}}>{d.careerTier}</div></td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:d.l7HR>=3?"#00e676":d.l7HR>=2?"#7eff8a":d.l7HR>=1?"#ffb020":"#4a6080"}}>{d.l7HR} HR</div></td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:pkC}}>{d.parkF}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>#{d.parkR}/30</div></td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:windC}}>{p.isDome?"🏟 Dome":d.windLabel}</div><div style={{fontSize:9,color:windC,opacity:0.7,marginTop:1}}>{d.windScore>0?"HR BOOST":d.windScore<0?"SUPPRESS":"NEUTRAL"}</div></td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:d.platAB>=20?(d.platHR/d.platAB>=0.05?"#00e676":d.platHR/d.platAB>=0.03?"#ffb020":"#4a6080"):"#3a5070"}}>{d.platAB>0?`${d.platHR}HR/${d.platAB}AB`:"—"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>BAT {d.batterHand} vs {d.platHand==="vl"?"LHP":"RHP"}</div></td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:d.pitSplitHRRate?(parseFloat(d.pitSplitHRRate)>=5?"#00e676":parseFloat(d.pitSplitHRRate)>=3?"#ffb020":"#4a6080"):"#2a3a50"}}>{d.pitSplitBF>0?`${d.pitSplitHR}HR/${d.pitSplitBF}BF${d.pitSplitBF<10?"*":""}` :"—"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>PIT vs {d.batterHand==="L"?"LHB":"RHB"}{d.pitSplitBF>0&&d.pitSplitBF<10?" · small":""}</div></td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:parseFloat(d.pitHR9)>=1.8?"#00e676":parseFloat(d.pitHR9)>=1.2?"#ffb020":"#4a6080"}}>{d.pitHR9||"—"}</div></td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:d.vsHR>=2?"#00e676":d.vsHR>=1?"#ffb020":"#4a6080"}}>{d.vsAB>0?`${d.vsHR}HR/${d.vsAB}AB`:"—"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>VS STARTER</div></td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}>
                        <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:(d.bullpenFatigue||0)>=6?"#00e676":(d.bullpenFatigue||0)>=3?"#ffb020":"#4a6080"}}>
                          {(d.bullpenFatigue||0)>=6?"🔥 TIRED":(d.bullpenFatigue||0)>=3?"⚡ WORN":"✓ FRESH"}
                        </div>
                        <div style={{fontSize:9,color:"#344560",marginTop:1}}>{d.bullpenFatigue>0?`${d.bullpenFatigue}/10`:""}</div>
                      </td>
                      <td style={{padding:"10px 10px",whiteSpace:"nowrap"}}>
                        <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:(d.pitchMatchSc||0)>=2?"#00e676":(d.pitchMatchSc||0)>=0.5?"#7eff8a":(d.pitchMatchSc||0)>-0.5?"#ffb020":(d.pitchMatchSc||0)>-2?"#ff8060":"#ff4060"}}>
                          {d.pitchMatchSc===undefined||d.pitchMatchSc===null?"—":(d.pitchMatchSc>=2?"✓ GOOD":d.pitchMatchSc>=0.5?"✓ FAIR":d.pitchMatchSc>-0.5?"~ NEUTRAL":d.pitchMatchSc>-2?"✗ TOUGH":"✗ BAD")}
                        </div>
                        <div style={{fontSize:9,color:"#344560",marginTop:1}}>
                          {d.pitchMatchBestPitch?(()=>{
                            const codeMap={FF:"4-Seam",SI:"Sinker",FC:"Cutter",SL:"Slider",ST:"Sweeper",SV:"Slurve",CU:"Curve",KC:"Knuck Curve",CH:"Changeup",FS:"Splitter",SC:"Screwball",KN:"Knuckle",CS:"Slow Curve"};
                            const name=codeMap[d.pitchMatchBestPitch.type]||d.pitchMatchBestPitch.type;
                            return `vs ${name} · SP throws ${d.pitchMatchBestPitch.usage}%`;
                          })():(d.pitchMatchSc!==undefined&&d.pitchMatchSc!==null?"low data":"")}
                        </div>
                      </td>
                    </tr>
                  );
                  });
                })()}
              </tbody>
            </table>
            </div>
            {!mlbIsPro&&(
              <div onClick={()=>user?window.location.href='/account':signInWithGoogle()}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px",marginTop:2,background:"#0e0818",border:"1px solid #c060ff40",borderRadius:6,cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:18}}>🔒</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:"#c8d0e8"}}>Full rankings hidden</div>
                    <div style={{fontSize:11,color:"#7040a0",marginTop:2}}>Pro unlocks every player · all sports</div>
                  </div>
                </div>
                <div style={{flexShrink:0,padding:"9px 22px",background:"linear-gradient(135deg,#7020c0,#c060ff)",borderRadius:5,fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:900,color:"#fff",letterSpacing:1.5,boxShadow:"0 0 14px #c060ff40"}}>
                  {user?"UPGRADE TO PRO →":"SIGN IN TO UNLOCK →"}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if(loading)return<Spinner text="LOADING MLB SCHEDULE..."/>;
  if(err)return<ErrMsg msg={err}/>;

  return(
    <>
      <div style={{display:"flex",gap:6,padding:"8px 10px",overflowX:"auto",background:"#07090e",borderBottom:"1px solid #0f1826",alignItems:"center",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        <button onClick={()=>view==="top"?setView("games"):loadTopTargets()} style={{flexShrink:0,padding:"8px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="top"?"#0a1e10":"#0c1322",border:`1px solid ${view==="top"?"#00e676":"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="top"?"#00e676":"#3a5070"}}>
          {loadingTop?"⏳":"🔥"} TOP HR TARGETS
        </button>
        <button onClick={()=>view==="pitcherSplits"?setView("games"):loadPitcherSplits()} style={{flexShrink:0,padding:"8px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="pitcherSplits"?"#15081a":"#0c1322",border:`1px solid ${view==="pitcherSplits"?"#c060ff":"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="pitcherSplits"?"#c060ff":"#3a5070"}}>
          {loadingPitcherSplits?"⏳":"🎯"} PITCHER SPLITS
        </button>
        <button onClick={()=>{
          if(view==="parlay"){setView("games");return;}
          // Show saved picks for the currently-selected analyst.
          // Auto-save is the canonical source — generate only as fallback.
          const analyst=ANALYSTS[selectedAnalyst];
          const saved=analyst&&todayPicks[analyst.id];
          if(saved?.picks?.length){
            const picks=saved.picks.slice(0,parlaySize);
            setView("parlay");
            setParlay({picks,strategy:analyst.desc,strategyEmoji:analyst.face,strategyDesc:analyst.desc,analystId:analyst.id,parlayNote:`${picks.length}-pick selections${parlayLocked?" · locked":""}`,confidence:null});
            return;
          }
          fetch(`/api/picks?sport=mlb`).then(r=>r.json()).then(d=>{
            const allPicks=d?.picks||[];
            const picksMap={};
            allPicks.forEach(p=>{ picksMap[p.analystId]=p; });
            setTodayPicks(picksMap);
            const fetched=analyst&&picksMap[analyst.id];
            if(fetched?.picks?.length){
              const picks=fetched.picks.slice(0,parlaySize);
              setView("parlay");
              setParlay({picks,strategy:analyst.desc,strategyEmoji:analyst.face,strategyDesc:analyst.desc,analystId:analyst.id,parlayNote:`${picks.length}-pick selections${parlayLocked?" · locked":""}`,confidence:null});
            } else {
              generateParlay(parlaySize,analyst?.strategy);
            }
          }).catch(()=>generateParlay(parlaySize,analyst?.strategy));
        }} style={{flexShrink:0,padding:"8px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="parlay"?"#12051a":"#0c1322",border:`1px solid ${view==="parlay"?"#c060ff":"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="parlay"?"#c060ff":"#3a5070"}}>
          {loadingParlay?"⏳":"🎯"} AI HR PICKER
        </button>
        <button onClick={()=>view==="bb"?setView("games"):loadBBTargets()} style={{flexShrink:0,padding:"8px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="bb"?"#0a1a28":"#0c1322",border:`1px solid ${view==="bb"?"#00c8ff":"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="bb"?"#00c8ff":"#3a5070"}}>
          {loadingBB?"⏳":"🚶"} TOP BB TARGETS
        </button>
        <button onClick={()=>view==="teamhr"?setView("games"):loadTeamHR()} style={{flexShrink:0,padding:"8px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="teamhr"?"#1a0e05":"#0c1322",border:`1px solid ${view==="teamhr"?"#ff9020":"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="teamhr"?"#ff9020":"#3a5070"}}>
          {loadingTeamHR?"⏳":"⚾"} TEAM HR PROJECTIONS
        </button>
        {mlbIsPro&&<button onClick={()=>setView(v=>v==="watchlist"?"games":"watchlist")} style={{flexShrink:0,padding:"8px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="watchlist"?"#1a1200":"#0c1322",border:`1px solid ${view==="watchlist"?"#ffd700":"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="watchlist"?"#ffd700":"#3a5070"}}>
          ⭐ MY PICKS {watchlist.size>0&&`(${watchlist.size})`}
        </button>}
        <div style={{width:1,height:36,background:"#182030",flexShrink:0}}/>
        {games.length===0?<div style={{color:"#3a5070",fontSize:13}}>No games today</div>:[...games].sort((a,b)=>{const o=g=>{const s=g.status?.abstractGameState||'';return s==='Live'?0:s==='Preview'?1:2;};return o(a)-o(b);}).map(g=>{
          const isA=sel?.gamePk===g.gamePk;const st=g.status?.abstractGameState||"Preview";
          const ls=liveScores[g.gamePk]||{};
          const hasScore=ls.away!=null&&ls.home!=null;
          const isLive=st==="Live";
          const isFinal=st==="Final";
          return(
            <div key={g.gamePk} onClick={()=>{setView("games");pickGame(g);}} style={{flexShrink:0,background:isA?"#0a1830":"#0d1322",border:`1px solid ${isA?"#00c8ff":isLive?"#00e67640":"#182030"}`,borderRadius:6,padding:"9px 14px",cursor:"pointer",minWidth:148,transition:"all 0.12s",boxShadow:isA?"0 0 12px rgba(0,200,255,0.1)":isLive?"0 0 8px rgba(0,230,118,0.06)":"none"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:15,fontWeight:800,color:"#fff",letterSpacing:0.5}}>{g.teams.away.team.abbreviation}</div>
                {hasScore&&<div style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:isLive?"#00e676":isFinal?"#c8d6e8":"#5070a0"}}>{ls.away}</div>}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:2}}>
                <div style={{fontSize:15,fontWeight:800,color:"#fff",letterSpacing:0.5}}>{g.teams.home.team.abbreviation}</div>
                {hasScore&&<div style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:isLive?"#00e676":isFinal?"#c8d6e8":"#5070a0"}}>{ls.home}</div>}
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4}}>
                {!hasScore&&<div style={{fontFamily:"monospace",fontSize:10,color:"#3a5070"}}>{fmtTime(g.gameDate)} ET</div>}
                {isLive&&ls.inning&&<div style={{fontFamily:"monospace",fontSize:9,color:"#00e676",opacity:0.8}}>{ls.inningHalf==="Top"?"▲":"▼"} {ls.inning}</div>}
                {isFinal&&<div style={{fontFamily:"monospace",fontSize:9,color:"#4060a0"}}>Final</div>}
                <div style={{display:"inline-block",fontSize:9,padding:"2px 6px",borderRadius:3,fontWeight:700,background:isLive?"#001a0030":"#1a2a4a",color:isLive?"#00e676":"#4a72b0",border:`1px solid ${isLive?"#00e67630":"#1e3060"}`}}>{isLive?"● LIVE":isFinal?"✓":fmtTime(g.gameDate)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {view==="top"&&(
        <div style={{display:"flex",gap:6,padding:"10px 16px 0",flexWrap:"wrap",alignItems:"center",background:"#060910"}}>
          {[["all","ALL HITTERS"],["upcoming","⏰ NOT STARTED"],["live","● LIVE"],["final","✓ FINAL"],["hit","💥 HIT TODAY"]].map(([f,label])=>{
            const isA=hrFilter===f;
            const ac=f==="live"||f==="hit"?"#00e676":f==="final"?"#00c8ff":"#00c8ff";
            const baseC=f==="live"?"#00e676":f==="final"?"#4060a0":f==="upcoming"?"#4a72b0":"#3a5070";
            const cnt=f==="all"?topTargets.length:f==="hit"?Object.keys(hrResults).length:topTargets.filter(p=>getPlayerGameStatus(p)===f).length;
            return(
              <button key={f} onClick={()=>setHrFilter(f)} style={{padding:"7px 14px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,letterSpacing:1.2,background:isA?"#081828":"#0c1322",border:`1px solid ${isA?ac:"#182030"}`,borderRadius:4,cursor:"pointer",color:isA?ac:baseC,transition:"all 0.12s"}}>
                {label} <span style={{fontFamily:"monospace",fontSize:11,opacity:0.7}}>({cnt})</span>
              </button>
            );
          })}
          </div>
        )}
      {view==="teamhr"&&(
        <div style={{padding:"14px 16px 32px"}}>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:2}}>⚾ TEAM HR PROJECTIONS TODAY</div>
            <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>PROJECTED HRS PER TEAM · LINEUP POWER + SP HR-PRONENESS + PLATOON SPLITS + PARK + WIND</div>
            {teamHRLockedAt?(
              <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:8,padding:"5px 10px",background:"#080e18",border:"1px solid #1a2a3a",borderRadius:4}}>
                <span style={{fontSize:11,color:"#00e676"}}>🔒</span>
                <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>Projections locked · Generated at {teamHRLockedAt}</span>
              </div>
            ):(
              teamHR.length>0&&(
                <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:8,padding:"5px 10px",background:"#080e18",border:"1px solid #1a2a3a",borderRadius:4}}>
                  <span style={{fontSize:11,color:"#ffb020"}}>⏳</span>
                  <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>
                    {games.filter(g=>g.lineups?.homePlayers?.length||g.lineups?.awayPlayers?.length).length * 2} of {games.length * 2} lineups posted ({games.length} games) · Updating every 3 min
                  </span>
                </div>
              )
            )}
          </div>
          {loadingTeamHR&&<div style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:7,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}><div style={{width:16,height:16,border:"2px solid #1a2540",borderTopColor:"#ff9020",borderRadius:"50%",animation:"spin 0.8s linear infinite",flexShrink:0}}/><div style={{fontSize:12,color:"#4a6080",letterSpacing:1}}>Projecting HR output for {games.length} games...</div></div>}
          {!loadingTeamHR&&teamHR.length===0&&<div style={{textAlign:"center",padding:40,color:"#243650",fontSize:13}}>No data</div>}
          {teamHR.length>0&&(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {teamHR.map((t,i)=>{
                const clr=t.hrExpected>=2.0?"#00e676":t.hrExpected>=1.4?"#7eff8a":t.hrExpected>=1.0?"#ffb020":"#ff6060";
                const medal=i===0?"🥇":i===1?"🥈":i===2?"🥉":null;
                const pitHR9=parseFloat(t.sp.hr9)||0;
                const pitClr=pitHR9>=1.8?"#ff4060":pitHR9>=1.3?"#ffb020":"#4a6080";
                // Find actual HRs hit by this team today
                const actualHRs=Object.values(hrResults).filter(r=>r.team===t.team);
                const actualHRCount=actualHRs.reduce((sum,r)=>sum+r.count,0);
                // Live score for this game
                const ls=Object.values(liveScores).find(s=>
                  (s.awayAbb===t.team||s.homeAbb===t.team)
                );
                return(
                  <div key={t.team+t.opp} style={{background:i<3?"#0a0f1e":"#090d14",border:`1px solid ${i<3?"#1a2540":"#111c2e"}`,borderRadius:8,padding:"14px 16px"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                      {/* Rank */}
                      <div style={{width:36,textAlign:"center",flexShrink:0}}>
                        {medal?<span style={{fontSize:20}}>{medal}</span>:<span style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:"#3a5070"}}>#{i+1}</span>}
                      </div>
                      {/* Team + matchup */}
                      <div style={{flex:1,minWidth:140}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:20,fontWeight:900,color:"#e8f0ff",letterSpacing:1}}>{t.team}</span>
                          <span style={{fontSize:11,color:"#3a5070"}}>vs</span>
                          <span style={{fontSize:14,fontWeight:700,color:"#7090c0"}}>{t.opp}</span>
                          <span style={{fontSize:10,color:"#3a5070"}}>{t.isHome?"(home)":"(away)"}</span>
                          {!t.hasLineup&&<span style={{fontSize:10,color:"#ffb020",background:"#1a1000",border:"1px solid #ffb02040",borderRadius:3,padding:"1px 6px"}}>⏳ Lineup pending</span>}
                          {t.gameStatus==="final"&&<span style={{fontSize:10,color:"#4a6080",background:"#1a2030",border:"1px solid #2a3040",borderRadius:3,padding:"1px 6px"}}>✅ Final</span>}
                          {t.gameStatus==="live"&&<span style={{fontSize:10,color:"#00e676",background:"#00e67615",border:"1px solid #00e67640",borderRadius:3,padding:"1px 6px"}}>🔴 Live</span>}
                          {t.gameStatus==="upcoming"&&<span style={{fontSize:10,color:"#3a5070",background:"#0c1322",border:"1px solid #182030",borderRadius:3,padding:"1px 6px"}}>⏰ {t.gameTime} ET</span>}
                        </div>
                        <div style={{fontSize:10,color:"#3a5070",marginTop:3}}>{t.park.f>=115?"🚀 HR Park":t.park.f>=105?"✅ Hitter friendly":t.park.f<=90?"🛡 Pitcher park":"⚖ Neutral"} (PF {t.park.f}) · {t.wind.label}</div>
                        {/* Live score */}
                        {ls&&t.gameStatus!=="upcoming"&&(
                          <div style={{fontFamily:"monospace",fontSize:11,color:t.gameStatus==="live"?"#00e676":"#7090c0",marginTop:3}}>
                            {ls.awayAbb} {ls.away} – {ls.homeAbb} {ls.home}
                            {t.gameStatus==="live"&&ls.inning&&<span style={{marginLeft:6,color:"#4a7090"}}>{ls.inningHalf==="Top"?"▲":"▼"}{ls.inning}</span>}
                          </div>
                        )}
                      </div>
                      {/* HR projection + actual */}
                      <div style={{textAlign:"center",flexShrink:0}}>
                        <div style={{fontSize:32,fontWeight:900,color:clr,fontFamily:"monospace",lineHeight:1}}>{t.hrExpected.toFixed(1)}</div>
                        <div style={{fontSize:9,color:clr,opacity:0.8,letterSpacing:1,marginTop:2}}>PROJ HRs</div>
                        {(t.gameStatus==="live"||t.gameStatus==="final")&&(
                          <div style={{marginTop:6,textAlign:"center"}}>
                            <div style={{fontSize:22,fontWeight:900,color:"#00e676",fontFamily:"monospace",lineHeight:1}}>{actualHRCount}</div>
                            <div style={{fontSize:9,color:"#00e676",opacity:0.8,letterSpacing:1,marginTop:1}}>ACTUAL HRs</div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Actual HR hitters */}
                    {actualHRs.length>0&&(
                      <div style={{marginTop:8,paddingLeft:48,display:"flex",gap:6,flexWrap:"wrap"}}>
                        {actualHRs.map((r,j)=>(
                          <div key={j} style={{display:"flex",alignItems:"center",gap:4,background:"#0a2010",border:"1px solid #00e67630",borderRadius:4,padding:"2px 8px"}}>
                            <span style={{fontSize:11}}>💥</span>
                            <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#00e676"}}>{r.name}</span>
                            {r.count>1&&<span style={{fontSize:10,color:"#00e676",opacity:0.8}}>×{r.count}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Details row */}
                    <div style={{display:"flex",gap:16,marginTop:10,flexWrap:"wrap",paddingLeft:48}}>
                      {/* SP */}
                      <div>
                        <div style={{fontSize:9,color:"#344560",letterSpacing:1,marginBottom:2}}>OPP SP</div>
                        <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#c8d6e8"}}>{t.sp.name}</div>
                        <div style={{display:"flex",gap:4,marginTop:2}}>
                          <span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:t.sp.hand==="L"?"#0d2a0d":"#0d1e36",color:t.sp.hand==="L"?"#00e676":"#00c8ff",fontWeight:800}}>{t.sp.hand==="L"?"LHP":"RHP"}</span>
                          <span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:"#1a1a0d",color:pitClr,fontWeight:800}}>HR/9: {t.sp.hr9}</span>
                          {t.pitSplitHR9vsHand&&<span style={{fontSize:9,padding:"1px 5px",borderRadius:3,background:"#150510",color:"#c060c0",fontWeight:800}}>vs lineup: {t.pitSplitHR9vsHand.toFixed(2)} HR/9</span>}
                        </div>
                      </div>
                      {/* Lineup info */}
                      {t.hasLineup&&(
                        <div>
                          <div style={{fontSize:9,color:"#344560",letterSpacing:1,marginBottom:2}}>LINEUP ({t.batterCount} batters)</div>
                          <div style={{display:"flex",gap:4,alignItems:"center"}}>
                            {t.handBreakdown.L>0&&<span style={{fontSize:10,color:"#00e676",background:"#0d2a0d",borderRadius:3,padding:"1px 6px"}}>{t.handBreakdown.L} LHB</span>}
                            {t.handBreakdown.R>0&&<span style={{fontSize:10,color:"#00c8ff",background:"#0d1e36",borderRadius:3,padding:"1px 6px"}}>{t.handBreakdown.R} RHB</span>}
                            {t.teamScore&&<span style={{fontSize:10,color:"#5070a0",fontFamily:"monospace"}}>avg score: {t.teamScore}</span>}
                          </div>
                          {t.topBatters.length>0&&<div style={{fontSize:10,color:"#5070a0",marginTop:3}}>🔥 {t.topBatters.map(b=>`${b.name} (${b.score})`).join(" · ")}</div>}
                        </div>
                      )}
                      {!t.hasLineup&&(
                        <div style={{fontSize:10,color:"#3a5070",fontStyle:"italic"}}>
                          Projection based on team avg · Park · Wind · SP HR/9
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* AI HR Parlay view */}
      {view==="parlay"&&(
        <div style={{padding:"14px 16px 32px"}}>
          {/* Header */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:2}}>🎯 HR PICK ANALYSTS</div>
            <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>PICK AN ANALYST · EACH HAS THEIR OWN STRATEGY · RECORDS AUTO-SETTLE AT 3AM ET</div>
          </div>

          {/* Analyst selector cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8,marginBottom:20}}>
            {ANALYSTS.map((a,i)=>{
              const isLocked=!mlbIsPro&&i>=2;
              const rec=analystRecords[a.id]||{legs:{hit:0,total:0},parlays:{}};
              const hitPct=rec.legs.total>0?Math.round(rec.legs.hit/rec.legs.total*100):null;
              const isSelected=selectedAnalyst===i;
              return(
                <div key={a.id} onClick={()=>{
                  if(isLocked){user?window.location.href='/account':signInWithGoogle();return;}
                  setSelectedAnalyst(i);
                  // Always read from saved picks first. The auto-save loop is the
                  // canonical source of truth for the day's 6 picks per analyst —
                  // generateParlay is a display-only fallback for when nothing is
                  // saved yet (e.g., lineups still being posted).
                  if(todayPicks[a.id]?.picks?.length){
                    const picks=todayPicks[a.id].picks.slice(0,parlaySize);
                    setView("parlay");
                    setParlay({picks,strategy:a.desc,strategyEmoji:a.face,strategyDesc:a.desc,analystId:a.id,parlayNote:`${picks.length}-pick selections${parlayLocked?" · locked":""}`,confidence:null});
                    return;
                  }
                  fetch(`/api/picks?sport=mlb`).then(r=>r.json()).then(d=>{
                    const allPicks=d?.picks||[];
                    const picksMap={};
                    allPicks.forEach(p=>{ picksMap[p.analystId]=p; });
                    setTodayPicks(picksMap);
                    const analystPicks=picksMap[a.id];
                    if(analystPicks?.picks?.length){
                      const picks=analystPicks.picks.slice(0,parlaySize);
                      setView("parlay");
                      setParlay({picks,strategy:a.desc,strategyEmoji:a.face,strategyDesc:a.desc,analystId:a.id,parlayNote:`${picks.length}-pick selections${parlayLocked?" · locked":""}`,confidence:null});
                    } else {
                      // No saved picks — display-only generate (won't POST)
                      generateParlay(parlaySize,a.strategy);
                    }
                  }).catch(()=>generateParlay(parlaySize,a.strategy));
                }}
                  style={{position:"relative",background:isSelected?a.color+"18":"#0c0f1a",border:`2px solid ${isSelected?a.color:"#182030"}`,borderRadius:8,padding:"10px 10px",cursor:"pointer",transition:"all 0.15s",textAlign:"center",overflow:"hidden"}}>
                  <div style={{fontSize:28,marginBottom:4}}>{a.face}</div>
                  <div style={{fontSize:14,fontWeight:900,color:isSelected?a.color:"#c8d6e8",letterSpacing:1}}>{a.name}</div>
                  <div style={{fontSize:9,color:"#3a5070",marginTop:2,letterSpacing:0.5}}>{a.desc}</div>
                  <div style={{marginTop:6,padding:"4px 0",borderTop:"1px solid #182030"}}>
                    {rec.legs.total>0?(
                      <>
                        <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:hitPct>=40?"#00e676":hitPct>=30?"#ffb020":"#ff4060"}}>{rec.legs.hit}/{rec.legs.total}</div>
                        <div style={{fontSize:9,color:hitPct>=40?"#00e676":hitPct>=30?"#ffb020":"#ff4060",fontWeight:700}}>{hitPct}% PICKS HIT</div>
                      </>
                    ):(
                      <div style={{fontSize:9,color:"#2a3a50",fontFamily:"monospace"}}>NO RECORD YET</div>
                    )}
                    {/* Parlay records by size */}
                    {rec.parlays?.['4']&&(
                      <div style={{marginTop:4,display:"flex",justifyContent:"center"}}>
                        <div style={{fontSize:8,fontFamily:"monospace",color:"#4a6080",background:"#0a1020",borderRadius:2,padding:"1px 4px"}}>TOP 4 RECORD: {rec.parlays['4'].hit}/{rec.parlays['4'].total}</div>
                      </div>
                    )}
                  </div>
                  {isLocked&&(
                    <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.7)",borderRadius:8,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,cursor:"pointer"}}>
                      <span style={{fontSize:16}}>🔒</span>
                      <span style={{fontSize:9,fontWeight:800,color:"#c060ff",letterSpacing:1}}>PRO ONLY</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Size selector */}
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,flexWrap:"wrap"}}>
            <span style={{fontSize:11,color:"#3a5070",letterSpacing:1}}>PICK COUNT:</span>
            <div style={{display:"flex",gap:4}}>
              {[2,3,4,5,6].map(n=>{
                const a=ANALYSTS[selectedAnalyst];
                return(
                  <button key={n} onClick={()=>{
                    setParlaySize(n);
                    const analyst=ANALYSTS[selectedAnalyst];
                    if(!analyst) return;
                    // Pure slicing — show top N from the saved 6-pick set.
                    // No regeneration, no extension. The auto-save loop is
                    // the sole producer of canonical picks.
                    const saved=todayPicks[analyst.id];
                    if(saved?.picks?.length){
                      const picks=saved.picks.slice(0,n);
                      setParlay({picks,strategy:analyst.desc,strategyEmoji:analyst.face,strategyDesc:analyst.desc,analystId:analyst.id,parlayNote:`${picks.length}-pick selections${parlayLocked?" · locked":""}`,confidence:null});
                      return;
                    }
                    // Saved picks not in state yet — fetch from Redis
                    fetch(`/api/picks?sport=mlb`).then(r=>r.json()).then(d=>{
                      const allPicks=d?.picks||[];
                      const picksMap={};
                      allPicks.forEach(p=>{ picksMap[p.analystId]=p; });
                      setTodayPicks(picksMap);
                      const fetched=picksMap[analyst.id];
                      if(fetched?.picks?.length){
                        const picks=fetched.picks.slice(0,n);
                        setParlay({picks,strategy:analyst.desc,strategyEmoji:analyst.face,strategyDesc:analyst.desc,analystId:analyst.id,parlayNote:`${picks.length}-pick selections${parlayLocked?" · locked":""}`,confidence:null});
                      } else {
                        // No saved picks yet (lineups still posting) — display-only generate
                        generateParlay(n,analyst?.strategy);
                      }
                    }).catch(()=>generateParlay(n,analyst?.strategy));
                  }} style={{padding:"6px 12px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,background:parlaySize===n?"#12051a":"#0c1322",border:`1px solid ${parlaySize===n?(ANALYSTS[selectedAnalyst]?.color||"#c060ff"):"#182030"}`,borderRadius:4,cursor:"pointer",color:parlaySize===n?(ANALYSTS[selectedAnalyst]?.color||"#c060ff"):"#3a5070"}}>{n}-PICK</button>
                );
              })}
            </div>
            {parlayLocked?(
              <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"#080e18",border:"1px solid #1a2a3a",borderRadius:5}}>
                <span style={{fontSize:11,color:"#00e676"}}>🔒</span>
                <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>PICKS LOCKED · All lineups in</span>
              </div>
            ):(
              topTargets.some(p=>!p.preLineup)&&(
                <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"#080e18",border:"1px solid #1a3020",borderRadius:5}}>
                  <span style={{fontSize:11,color:"#ffb020"}}>⏳</span>
                  <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>PICKS PENDING · Waiting for all lineups</span>
                </div>
              )
            )}
          </div>

          {loadingParlay&&(
            <div style={{background:"#0e0818",border:"1px solid #2a1040",borderRadius:8,padding:"32px",textAlign:"center"}}>
              <div style={{width:20,height:20,border:"2px solid #2a1040",borderTopColor:ANALYSTS[selectedAnalyst]?.color||"#c060ff",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
              <div style={{fontSize:13,color:"#7040a0",letterSpacing:1}}>{ANALYSTS[selectedAnalyst]?.face} {ANALYSTS[selectedAnalyst]?.name} is building picks...</div>
            </div>
          )}
          {parlay?.error&&<div style={{color:"#ff6060",padding:20,textAlign:"center"}}>{parlay.error}</div>}
          {parlay?.picks&&(()=>{
            const analyst=ANALYSTS.find(a=>a.id===parlay.analystId)||ANALYSTS[selectedAnalyst];
            const rec=analystRecords[analyst.id]||{legs:{hit:0,total:0},parlays:{}};
            const hitPct=rec.legs.total>0?Math.round(rec.legs.hit/rec.legs.total*100):null;
            return(
              <div>
                {/* Active analyst banner */}
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12,padding:"10px 14px",background:analyst.color+"12",border:`1px solid ${analyst.color}40`,borderRadius:8}}>
                  <span style={{fontSize:32}}>{analyst.face}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:900,color:analyst.color,letterSpacing:1}}>{analyst.name}'s {parlay.picks.length} PICKS TODAY</div>
                    <div style={{fontSize:10,color:"#5a6080",marginTop:2}}>{analyst.bio}</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    {rec.legs.total>0?(
                      <>
                        <div style={{fontFamily:"monospace",fontSize:18,fontWeight:900,color:hitPct>=40?"#00e676":hitPct>=30?"#ffb020":"#ff4060"}}>{hitPct}%</div>
                        <div style={{fontSize:9,color:"#3a5070"}}>{rec.legs.hit}/{rec.legs.total} PICKS</div>
                      </>
                    ):(
                      <div style={{fontSize:9,color:"#2a3a50",fontFamily:"monospace"}}>NO RECORD YET</div>
                    )}
                  </div>
                  {parlay.confidence&&(
                    <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:4,letterSpacing:1,
                      background:parlay.confidence==="HIGH"?"#001a00":parlay.confidence==="MEDIUM"?"#1a1000":"#1a0000",
                      color:parlay.confidence==="HIGH"?"#00e676":parlay.confidence==="MEDIUM"?"#ffb020":"#ff6060",
                      border:`1px solid ${parlay.confidence==="HIGH"?"#00e67640":parlay.confidence==="MEDIUM"?"#ffb02040":"#ff606040"}`
                    }}>{parlay.confidence}</span>
                  )}
                </div>

                {/* Picks */}
                <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
                  {parlay.picks.map((pick,i)=>{
                    const medals=["🥇","🥈","🥉","4️⃣","5️⃣","6️⃣"];
                    const origPlayer=topTargets.find(p=>String(p.name).toLowerCase()===String(pick.name).toLowerCase());
                    const clr=analyst.color;
                    const hitToday=hrResults[String(origPlayer?.id)]?.count>0;
                    return(
                      <div key={i} style={{background:hitToday?"#0a1e0e":"#0a0c14",border:`1px solid ${hitToday?"#00e676":clr}30`,borderRadius:8,padding:"12px 14px",position:"relative",overflow:"hidden"}}>
                        <div style={{position:"absolute",top:0,left:0,width:3,height:"100%",background:hitToday?"#00e676":clr,borderRadius:"4px 0 0 4px"}}/>
                        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                          <span style={{fontSize:18,flexShrink:0}}>{medals[i]||`${i+1}.`}</span>
                          <img src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_60,q_auto:best/v1/people/${origPlayer?.id||0}/headshot/67/current`} alt={pick.name} style={{width:40,height:40,borderRadius:"50%",border:`2px solid ${hitToday?"#00e676":clr}60`,objectFit:"cover",background:"#0a1020",flexShrink:0}} onError={e=>{e.target.style.display="none";}}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                              <span style={{fontSize:16,fontWeight:900,color:"#e8f0ff"}}>{pick.name}</span>
                              <span style={{fontFamily:"monospace",fontSize:10,color:"#7090c0"}}>{pick.team}</span>
                              <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:clr}}>Score: {pick.score}</span>
                              {hitToday&&<span style={{fontSize:11,fontWeight:800,color:"#00e676",background:"#0a2010",border:"1px solid #00e67650",borderRadius:4,padding:"1px 8px"}}>💥 HIT!</span>}
                            </div>
                            <div style={{fontSize:10,color:"#5a6080",marginTop:3}}>vs {pick.opp} · {pick.gameTime} · #{pick.rank} ranked</div>
                            <div style={{fontSize:10,color:"#4a5060",marginTop:2}}>{pick.reason}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{fontSize:10,color:"#2a1a40",textAlign:"center",marginTop:8}}>Records auto-settle at 3am ET · For entertainment only</div>
              </div>
            );
          })()}
        </div>
      )}
      {view==="top"&&renderTopTargets()}
      {view==="bb"&&renderBBTargets()}
      {view==="pitcherSplits"&&renderPitcherSplits()}
      {(view==="teamhr"||view==="parlay")&&null}
      {view==="watchlist"&&(
        <div style={{padding:"14px 16px 28px"}}>
          <div style={{marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontSize:22,fontWeight:900,color:"#ffd700",letterSpacing:2}}>⭐ MY PICKS</div>
              <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>CLICK ⭐ ON ANY RANKING TO ADD · UPDATES EVERY 2 MIN</div>
            </div>
            {watchlist.size>0&&<button onClick={()=>{setWatchlist(new Set());saveWatchlist(new Set());}} style={{padding:"6px 14px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:12,fontWeight:800,letterSpacing:1,background:"transparent",border:"1px solid #2a3a50",borderRadius:4,cursor:"pointer",color:"#3a5070"}}>CLEAR ALL</button>}
          </div>
          {watchlist.size===0?(
            <div style={{textAlign:"center",padding:"50px 20px"}}>
              <div style={{fontSize:36,marginBottom:12}}>⭐</div>
              <div style={{fontSize:16,fontWeight:800,color:"#c8d0e8",marginBottom:8}}>No players selected yet</div>
              <div style={{fontSize:12,color:"#3a5070"}}>Go to TOP HR TARGETS and click ⭐ next to any player to track them here</div>
            </div>
          ):(
            <>
              {loadingWatchStats&&<div style={{fontSize:11,color:"#3a5070",marginBottom:12,letterSpacing:1}}>⏳ Loading stats...</div>}
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {[...watchlist].map(id=>{
                  const player = topTargets.find(p=>String(p.id)===id);
                  const stats = watchStats[id];
                  const hasGame = !!player;
                  const gameStatus = player?.gameSt||"upcoming";
                  const isLive = gameStatus==="live";
                  const isFinal = gameStatus==="final";
                  const statusColor = isLive?"#00e676":isFinal?"#4a72b0":"#3a5070";
                  const statusLabel = isLive?"● LIVE":isFinal?"✓ FINAL":"⏰ "+( player?.gameTime||"TBD");
                  // Build stat line
                  const hasStats = stats && (stats.ab!=='—'||stats.h!=='—');
                  const statLine = hasStats
                    ? `${stats.h}-${stats.ab}${stats.hr>0?` · ${stats.hr} HR`:""}${stats.bb>0?` · ${stats.bb} BB`:""}${stats.k>0?` · ${stats.k} K`:""}${stats.rbi>0?` · ${stats.rbi} RBI`:""}`
                    : isLive||isFinal?"No AB yet":"Game not started";
                  const hitHR = stats?.hr>0;
                  return(
                    <div key={id} style={{background:hitHR?"#0a1a0a":"#0a0d18",border:`1px solid ${hitHR?"#00e67640":"#111c2e"}`,borderRadius:8,padding:"14px 16px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                      <img src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_60,q_auto:best/v1/people/${id}/headshot/67/current`}
                        alt="" style={{width:44,height:44,borderRadius:"50%",border:`2px solid ${hitHR?"#00e676":"#182030"}`,objectFit:"cover",flexShrink:0,background:"#0a1020"}}
                        onError={e=>e.target.style.display="none"}/>
                      <div style={{flex:1,minWidth:140}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:16,fontWeight:900,color:"#e8f0ff"}}>{player?.name||`Player ${id}`}</span>
                          <span style={{fontFamily:"monospace",fontSize:11,color:"#4a6080"}}>{player?.team||""}</span>
                          {hitHR&&<span style={{fontSize:11,fontWeight:800,color:"#00e676",background:"#00e67615",border:"1px solid #00e67640",borderRadius:3,padding:"1px 8px"}}>💥 HR</span>}
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:10,marginTop:4,flexWrap:"wrap"}}>
                          <span style={{fontSize:11,color:statusColor,fontWeight:700}}>{statusLabel}</span>
                          {player?.oppSP?.name&&<span style={{fontSize:11,color:"#3a5070"}}>vs {player.oppSP.name} ({player.oppSP.hand}HP)</span>}
                        </div>
                        {hasGame&&(
                          <div style={{fontFamily:"monospace",fontSize:14,fontWeight:700,color:hitHR?"#00e676":"#c8d6e8",marginTop:6}}>{statLine}</div>
                        )}
                        {!hasGame&&<div style={{fontSize:11,color:"#2a3a50",marginTop:4,fontStyle:"italic"}}>Not in today's lineup</div>}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                        {player&&<div style={{fontFamily:"monospace",fontSize:13,fontWeight:800,color:"#5080c0"}}>Score: {player.hrScore}</div>}
                        <span onClick={()=>toggleWatch(id)} style={{fontSize:18,cursor:"pointer",opacity:0.5}} title="Remove">✕</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      {view!=="top"&&view!=="bb"&&view!=="teamhr"&&view!=="parlay"&&(lu?(
        <div style={{padding:"14px 16px 28px"}}>
          <div style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:7,padding:"10px 14px",marginBottom:12,display:"flex",gap:20,flexWrap:"wrap",alignItems:"center"}}>
            <div><div style={{fontSize:9,color:"#344560",letterSpacing:1,fontWeight:700}}>TEMP</div><div style={{fontFamily:"monospace",fontSize:14,fontWeight:700,color:"#c8d6e8",marginTop:2}}>{lu.isDome?"Dome":lu.weather?.temp?lu.weather.temp+"°F":"—"}</div></div>
            <div><div style={{fontSize:9,color:"#344560",letterSpacing:1,fontWeight:700}}>SKY</div><div style={{fontFamily:"monospace",fontSize:14,fontWeight:700,color:"#8090b0",marginTop:2}}>{lu.isDome?"Indoor":lu.weather?.condition||"—"}</div></div>
            <div>
              <div style={{fontSize:9,color:"#344560",letterSpacing:1,fontWeight:700}}>WIND</div>
              <div style={{fontFamily:"monospace",fontSize:14,fontWeight:700,color:lu.wind.score>0?"#00e676":lu.wind.score<0?"#ff4060":"#8090b0",marginTop:2}}>{lu.wind.label}</div>
              {lu.wind.score>0&&<div style={{fontSize:9,color:"#00e676",marginTop:1}}>↑ HR BOOST</div>}
              {lu.wind.score<0&&<div style={{fontSize:9,color:"#ff4060",marginTop:1}}>↓ HR SUPPRESS</div>}
            </div>
            <div style={{marginLeft:"auto",background:"#0b1424",borderRadius:6,padding:"8px 14px",textAlign:"center",borderLeft:`3px solid ${lu.park.f>=120?"#00e676":lu.park.f>=108?"#7eff8a":lu.park.f>=95?"#ffb020":"#ff4060"}`}}>
              <div style={{fontSize:9,color:"#344560",letterSpacing:1,fontWeight:700}}>HR PARK FACTOR</div>
              <div style={{fontFamily:"monospace",fontSize:26,fontWeight:700,lineHeight:1,marginTop:2,color:lu.park.f>=120?"#00e676":lu.park.f>=108?"#7eff8a":lu.park.f>=95?"#ffb020":"#ff4060"}}>{lu.park.f}</div>
              <div style={{fontSize:10,color:"#344560",marginTop:2}}>#{lu.park.rank} of 30 parks</div>
            </div>
          </div>
          <div style={{background:"#09101e",border:"1px solid #141e34",borderRadius:7,padding:"12px 16px",marginBottom:12,display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:10,alignItems:"start"}}>
            <div>
              <div style={{fontSize:9,color:"#344560",letterSpacing:1.5,fontWeight:700,marginBottom:3}}>{lu.aa} STARTER</div>
              <div style={{fontSize:18,fontWeight:800,color:"#e8f0ff"}}>{lu.aPD?.name||lu.ap?.fullName||"TBD"}</div>
              {lu.aPD&&<>
                <div style={{fontFamily:"monospace",fontSize:11,color:"#5070a0",marginTop:3}}>ERA {lu.aPD.era} · WHIP {lu.aPD.whip} · {lu.aPD.ip} IP · <span style={{color:parseFloat(lu.aPD.hr9)>=1.8?"#00e676":parseFloat(lu.aPD.hr9)>=1.2?"#ffb020":"#5070a0"}}>HR/9: {lu.aPD.hr9}</span></div>
                <div style={{display:"inline-block",background:lu.aPD.hand==="L"?"#0d2a0d":"#0d1e36",color:lu.aPD.hand==="L"?"#00e676":"#00c8ff",fontSize:10,padding:"2px 8px",borderRadius:3,fontWeight:800,letterSpacing:1.5,marginTop:5}}>{lu.aPD.hand==="L"?"LHP":"RHP"}</div>
                {lu.aPD.splits?.vsL&&<div style={{marginTop:5,fontSize:10,color:"#3a5070"}}>vs LHB: ERA {lu.aPD.splits.vsL.era||"---"} · OPS {lu.aPD.splits.vsL.ops||"---"} · {lu.aPD.splits.vsL.homeRuns||0} HR / {lu.aPD.splits.vsL.atBats||0} AB</div>}
                {lu.aPD.splits?.vsR&&<div style={{fontSize:10,color:"#3a5070",marginTop:2}}>vs RHB: ERA {lu.aPD.splits.vsR.era||"---"} · OPS {lu.aPD.splits.vsR.ops||"---"} · {lu.aPD.splits.vsR.homeRuns||0} HR / {lu.aPD.splits.vsR.atBats||0} AB</div>}
              </>}
            </div>
            <div style={{textAlign:"center",color:"#1a2a44",fontSize:12,fontWeight:900,letterSpacing:3,paddingTop:4}}>VS</div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:9,color:"#344560",letterSpacing:1.5,fontWeight:700,marginBottom:3}}>{lu.ha} STARTER</div>
              <div style={{fontSize:18,fontWeight:800,color:"#e8f0ff"}}>{lu.hPD?.name||lu.hp?.fullName||"TBD"}</div>
              {lu.hPD&&<>
                <div style={{fontFamily:"monospace",fontSize:11,color:"#5070a0",marginTop:3}}>ERA {lu.hPD.era} · WHIP {lu.hPD.whip} · {lu.hPD.ip} IP · <span style={{color:parseFloat(lu.hPD.hr9)>=1.8?"#00e676":parseFloat(lu.hPD.hr9)>=1.2?"#ffb020":"#5070a0"}}>HR/9: {lu.hPD.hr9}</span></div>
                <div style={{display:"inline-block",background:lu.hPD.hand==="L"?"#0d2a0d":"#0d1e36",color:lu.hPD.hand==="L"?"#00e676":"#00c8ff",fontSize:10,padding:"2px 8px",borderRadius:3,fontWeight:800,letterSpacing:1.5,marginTop:5}}>{lu.hPD.hand==="L"?"LHP":"RHP"}</div>
                {lu.hPD.splits?.vsL&&<div style={{marginTop:5,fontSize:10,color:"#3a5070",textAlign:"right"}}>vs LHB: ERA {lu.hPD.splits.vsL.era||"---"} · OPS {lu.hPD.splits.vsL.ops||"---"} · {lu.hPD.splits.vsL.homeRuns||0} HR / {lu.hPD.splits.vsL.atBats||0} AB</div>}
                {lu.hPD.splits?.vsR&&<div style={{fontSize:10,color:"#3a5070",marginTop:2,textAlign:"right"}}>vs RHB: ERA {lu.hPD.splits.vsR.era||"---"} · OPS {lu.hPD.splits.vsR.ops||"---"} · {lu.hPD.splits.vsR.homeRuns||0} HR / {lu.hPD.splits.vsR.atBats||0} AB</div>}
              </>}
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10,flexWrap:"wrap"}}>
            <div style={{display:"flex",gap:3}}>{["HR","H","RBI","XBH","SB","BB"].map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:"7px 16px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,letterSpacing:1.5,background:tab===t?"#081828":"#0c1322",border:`1px solid ${tab===t?"#00c8ff":"#182030"}`,borderRadius:4,cursor:"pointer",color:tab===t?"#00c8ff":"#3a5070"}}>{t}</button>)}</div>
            <div style={{marginLeft:"auto",display:"flex",gap:12}}>
              {[["#00e676","60+","PRIME"],["#ffb020","42-59","SOLID"],["#ff4060","<42","FADE"]].map(([c,r,l])=><div key={r} style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:7,height:7,borderRadius:2,background:c,boxShadow:c==="#00e676"?`0 0 4px ${c}`:"none"}}/><span style={{fontFamily:"monospace",fontSize:10,color:c}}>{r}</span><span style={{fontSize:10,color:"#2a3a50"}}>{l}</span></div>)}
            </div>
          </div>
          {/* Scoring Plays Panel */}
          {(scoringPlays.length>0||(sel&&sel.status?.abstractGameState!=="Preview"))&&(
            <div style={{background:"#080c14",border:"1px solid #111c2e",borderRadius:7,marginBottom:12,overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 14px",borderBottom:"1px solid #0c1828",cursor:"pointer"}} onClick={()=>setShowScoring(s=>!s)}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13,fontWeight:800,color:"#00c8ff",letterSpacing:1.5}}>⚾ SCORING PLAYS</span>
                  {scoringPlays.length>0&&<span style={{fontFamily:"monospace",fontSize:11,color:"#00c8ff",background:"#081828",border:"1px solid #00c8ff30",borderRadius:3,padding:"1px 7px"}}>{scoringPlays.length} PLAYS</span>}
                </div>
                <span style={{fontSize:11,color:"#2a3a50"}}>{showScoring?"▲ HIDE":"▼ SHOW"}</span>
              </div>
              {showScoring&&(
                scoringPlays.length===0?(
                  <div style={{padding:"12px 14px",fontFamily:"monospace",fontSize:11,color:"#243650"}}>
                    {sel?.status?.abstractGameState==="Preview"?"Game hasn't started yet":"No scoring plays yet"}
                  </div>
                ):(
                  <div style={{maxHeight:300,overflowY:"auto"}}>
                    {scoringPlays.map((p,i)=>{
                      const halfLabel=p.half==="top"?"▲":"▼";
                                      return(
                        <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 14px",borderBottom:"1px solid #0a1020"}}
                          onMouseEnter={e=>e.currentTarget.style.background="#0c1428"}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          {/* Inning */}
                          <div style={{fontFamily:"monospace",fontSize:11,color:"#3a5070",flexShrink:0,width:38,paddingTop:2}}>{halfLabel}{p.inning}</div>
                          {/* Description */}
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,color:"#c8d6e8",lineHeight:1.4}}>{p.desc}</div>
                            {p.rbi>0&&<div style={{fontFamily:"monospace",fontSize:10,color:"#ffb020",marginTop:2}}>{p.rbi} RBI</div>}
                          </div>
                          {/* Score */}
                          {p.awayScore!=null&&(
                            <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#c8d6e8",flexShrink:0,paddingTop:2}}>
                              {p.away} {p.awayScore} – {p.home} {p.homeScore}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          )}

          {loadingP?<Spinner text="FETCHING PLAYER DATA + PITCHER SPLITS..."/>:<>
            {renderLineup(lu.aLU,lu.park,lu.wind,`▲ AWAY — ${(lu.awayTeam?.name||lu.aa).toUpperCase()}`,lu.hPD?.hand,lu.hPD?.hr9)}
            {renderLineup(lu.hLU,lu.park,lu.wind,`▼ HOME — ${(lu.homeTeam?.name||lu.ha).toUpperCase()}`,lu.aPD?.hand,lu.aPD?.hr9)}
          </>}
        </div>
      ):(
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:300,flexDirection:"column",gap:10}}>
          <div style={{fontSize:13,color:"#243650",letterSpacing:1}}>← Click a game to load analytics</div>
          <div style={{fontSize:11,color:"#1a2a3a"}}>or hit 🔥 Top HR Targets for today's best picks</div>
        </div>
      ))}
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// NHL SECTION
// ════════════════════════════════════════════════════════════════
function NHLSection({selDate=getDateStr()}){
  const { isPro, user, signInWithGoogle } = useAuth();
  const nhlIsPro = isPro();
  const[games,setGames]=useState([]);
  const[loading,setLoading]=useState(true);
  const[err,setErr]=useState(null);
  const[sel,setSel]=useState(null);
  const[gameData,setGameData]=useState({});
  const skaterStatsCache=useRef({}); // freeze skater stats at first fetch
  const hasLoadedRef=useRef(false);  // prevent double-loading rankings
  const gamesRef=useRef([]);         // stable ref for pollLive interval
  const[loadingG,setLoadingG]=useState(false);
  const[view,setView]=useState("top"); // "games" | "top"
  const[topScorers,setTopScorers]=useState([]);
  const[loadingTop,setLoadingTop]=useState(false);
  const[topLoaded,setTopLoaded]=useState(false);
  const[nhlFilter,setNhlFilter]=useState("all"); // "all" | "upcoming" | "live" | "final"
  const[nhlSort,setNhlSort]=useState({col:"total",dir:"desc"});
  const[teamDef,setTeamDef]=useState({}); // abbrev → {gaPerGame, shotAttAgainst, pkPct}
  const[nhlLive,setNhlLive]=useState({});
  const[nhlLeaders,setNhlLeaders]=useState(null);
  const[goalFeed,setGoalFeed]=useState([]);
  const[goalResults,setGoalResults]=useState({}); // scorerId → {count, game, name, team}
  const[showGoalFeed,setShowGoalFeed]=useState(true);
  const[nhlParlay,setNhlParlay]=useState(null);
  const[nhlTodayPicks,setNhlTodayPicks]=useState({});
  const[nhlParlaySize,setNhlParlaySize]=useState(4);
  const[nhlParlayStratIdx,setNhlParlayStratIdx]=useState(0);
  const[loadingNhlParlay,setLoadingNhlParlay]=useState(false);
  const[nhlAnalystRecords,setNhlAnalystRecords]=useState({});
  const[nhlSelectedAnalyst,setNhlSelectedAnalyst]=useState(0);
  const[nhlParlayLocked,setNhlParlayLocked]=useState(false);

  const NHL_ANALYSTS=[
    {id:"puck",name:"Puck",face:"🏒",color:"#00e676",strategy:0,desc:"Best Overall Score",bio:"Pure numbers. If the score says shoot, Puck shoots."},
    {id:"blitz",name:"Blitz",face:"⚡",color:"#ffb020",strategy:4,desc:"Power Play Threat",bio:"PP specialists on weak PKs. Man advantage wins games."},
    {id:"snipe",name:"Snipe",face:"🎯",color:"#00c8ff",strategy:3,desc:"Sniper",bio:"Goals per game doesn't lie. Back the best scorers."},
    {id:"grinder",name:"Grinder",face:"🦁",color:"#ff4060",strategy:1,desc:"Hot Streak",bio:"Momentum is everything. Ride the hot stick."},
    {id:"scout",name:"Scout",face:"🦉",color:"#c060ff",strategy:2,desc:"Weak Defense",bio:"Attack the soft underbelly. Bad teams give up goals."},
  ];

  // Load today's schedule + rankings + scores in a single feed call
  useEffect(()=>{
    const init=async()=>{
      setGames([]);setNhlLive({});setGoalFeed([]);setGoalResults({});setTopLoaded(false);setTopScorers([]);
      setNhlParlayLocked(false);skaterStatsCache.current={};
      // Single feed call — gets schedule, rankings, scores, goals all at once
      try{
        const d=await feedFetch(`/api/feed?sport=nhl&date=${selDate}`);
        const nhl=d?.nhl||{};
        // Set games from cache
        if(nhl.games?.length) setGames(nhl.games);
        // Hydrate scores immediately
        if(nhl.scores&&Object.keys(nhl.scores).length) setNhlLive(nhl.scores);
        // Hydrate goals
        if(nhl.goals?.length){
          setGoalFeed(nhl.goals);
          const goalMap={};
          nhl.goals.forEach(g=>{if(!g.scorer)return;const k=String(g.scorer);if(!goalMap[k])goalMap[k]={count:0,game:g.gameStr,name:g.name,team:g.team};goalMap[k].count++;});
          setGoalResults(goalMap);
        }
        // Hydrate rankings if available
        if(nhl.rankings?.length){
          if(nhl.teamDef&&Object.keys(nhl.teamDef).length) setTeamDef(nhl.teamDef);
          setTopScorers(nhl.rankings);setTopLoaded(true);setNhlParlayLocked(true);
        }
        // Hydrate leaders for no-games display
        if(nhl.leaders) setNhlLeaders(nhl.leaders);
        // If we got games from feed, we're done
        if(nhl.games?.length){setLoading(false);return;}
      }catch(e){}
      // Feed not ready yet — show no games message
      setLoading(false);
    };
    hasLoadedRef.current=false;
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selDate]);

  // teamDef loaded from feed in loadTopScorers — no direct NHL API calls

  // Load NHL analyst records + auto-settle yesterday
  useEffect(()=>{
    // Load NHL analyst records from Redis
    const loadNHLRecords=async()=>{
      try{
        const d=await fetch(`/api/picks?sport=nhl`).then(r=>r.json());
        if(d?.records&&Object.keys(d.records).length) setNhlAnalystRecords(d.records);
      }catch{}
    };
    loadNHLRecords();
    const interval=setInterval(loadNHLRecords,5*60*1000);
    return()=>clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // NHL settlement handled server-side in refresh.js cron

  // Poll live scores every 45s
  useEffect(()=>{
    const pollLive=async()=>{
      if(document.hidden) return;
      // Use feed endpoint — server-side cron keeps it warm
      try{
        const d=await feedFetch(`/api/feed?sport=nhl&date=${selDate}`);
        if(d?.nhl?.scores&&Object.keys(d.nhl.scores).length){
          setNhlLive(prev=>{
            const merged={...prev};
            Object.entries(d.nhl.scores).forEach(([id,s])=>{
              const existing=prev[id];
              const stateRank={OFF:4,FINAL:4,CRIT:3,LIVE:2,PRE:1,FUT:0};
              if(!existing||((stateRank[s.state]||0)>=(stateRank[existing.state]||0))){
                merged[id]={...s,away:Math.max(s.away||0,existing?.away||0),home:Math.max(s.home||0,existing?.home||0)};
              }
            });
            return merged;
          });
          setGames(prev=>prev.map(g=>{
            const s=d.nhl.scores[g.id];
            if(!s) return g;
            const stateRank={OFF:4,FINAL:4,CRIT:3,LIVE:2,PRE:1,FUT:0};
            const curRank=stateRank[g.gameState]||0;
            const newRank=stateRank[s.state]||0;
            return{...g,gameState:newRank>=curRank?s.state:g.gameState};
          }));
        }
        if(d?.nhl?.goals?.length){
          setGoalFeed(prev=>{
            const existingIds=new Set(prev.map(g=>g.id));
            const newGoals=(d.nhl.goals||[]).filter(g=>!existingIds.has(g.id));
            return [...newGoals,...prev].sort((a,b)=>(b.realWorldMs||0)-(a.realWorldMs||0));
          });
          const goalMap={};
          (d.nhl.goals||[]).forEach(g=>{
            if(!g.scorer) return;
            const key=String(g.scorer);
            if(!goalMap[key]) goalMap[key]={count:0,game:g.gameStr,name:g.name,team:g.team};
            goalMap[key].count++;
          });
          setGoalResults(goalMap);
          return; // got goals from feed, no need for fallback
        }
        if(d?.nhl?.scores&&Object.keys(d.nhl.scores).length) return;
      }catch(e){}
    };
    if(gamesRef.current.length>0){pollLive();}
    const interval=setInterval(()=>{if(gamesRef.current.some(g=>g.gameState==="LIVE"||g.gameState==="CRIT"||g.gameState==="OFF"))pollLive();},120000);
    return()=>clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selDate]);

  // Keep gamesRef in sync — lets pollLive read latest games without being a dep
  useEffect(()=>{gamesRef.current=games;},[games]);

  // ── scGoal: score 0-100 likelihood of scoring tonight ──────────
  // Factors:
  // 1. Goals/game season rate      (22pts) — baseline output
  // 2. Last 10 games hot streak    (22pts) — current form
  // 3. Shots/game                  (16pts) — volume = opportunity
  // 4. PP goals                    (12pts) — power play threat
  // 5. Opp defense weakness        (15pts) — GA/game + PK% + possession
  // 6. vs opponent this season     ( 8pts) — matchup history
  // 7. Points/game                  (5pts) — overall involvement
  function scGoal(p,oppDef,isHome=null){
    if(!p)return null;
    const gpg=p.goals/Math.max(1,p.gamesPlayed);
    const spg=p.shots/Math.max(1,p.gamesPlayed);
    const ptspg=p.points/Math.max(1,p.gamesPlayed);
    const l10gpg=p.last10.g/Math.max(1,p.last10.gms);

    // 1. Season goals/game (22pts)
    const gpgSc=Math.min(22,gpg*44);

    // 2. Last 10 hot streak (22pts)
    const hotSc=Math.min(22,l10gpg*22);

    // 3. Shots/game (16pts)
    const sogSc=Math.min(16,spg*3.2);

    // 4. PP goals (12pts)
    const ppSc=Math.min(12,p.ppGoals*1.2);

    // 5. Opposing team defense (15pts)
    let defSc=7;
    if(oppDef){
      const gaScore=Math.min(8,Math.max(0,(oppDef.gaPerGame-2.4)/1.4*8));
      const pkScore=Math.min(4,Math.max(0,(82-oppDef.pkPct)/8*4));
      const posScore=Math.min(3,Math.max(0,(50-oppDef.faceoffWinPct)/6*3));
      defSc=Math.min(15,Math.round(gaScore+pkScore+posScore));
    }

    // 6. vs this opponent (8pts)
    const vsSc=p.vsOppGames>=3
      ?Math.min(8,(p.vsOppGoals/p.vsOppGames)*12)
      :p.vsOppGoals>0?4:2;

    // 7. Points/game (5pts)
    const ptsSc=Math.min(5,ptspg*5);

    // 8. Home/away split bonus (5pts)
    // If player scores significantly better at home vs away or vice versa, boost accordingly
    let homeSplitSc=0;
    if(isHome!==null&&p.homeGpg!==null&&p.awayGpg!==null){
      const relevantGpg=isHome?p.homeGpg:p.awayGpg;
      const otherGpg=isHome?p.awayGpg:p.homeGpg;
      if(relevantGpg>gpg) homeSplitSc=Math.min(5,(relevantGpg-gpg)*10); // better than season avg in this split
      else if(relevantGpg<otherGpg*0.7) homeSplitSc=-2; // significantly worse in this split
    }

    const total=Math.min(100,Math.round(gpgSc+hotSc+sogSc+ppSc+defSc+vsSc+ptsSc+homeSplitSc));
    const oppDefStr=oppDef?`${oppDef.gaPerGame.toFixed(2)} GA/g`:null;
    const splitLabel=isHome===null?null:isHome?"Home":"Away";
    const splitGpg=isHome===null?null:isHome?p.homeGpg:p.awayGpg;
    return{total,gpg:gpg.toFixed(2),l10G:p.last10.g,l10Pts:p.last10.pts,spg:spg.toFixed(1),ppGoals:p.ppGoals,points:p.points,goals:p.goals,gp:p.gamesPlayed,vsOppGoals:p.vsOppGoals,vsOppGames:p.vsOppGames,oppDefStr,defSc,homeSplitSc,splitLabel,splitGpg:splitGpg!=null?splitGpg.toFixed(2):null};
  }

  // ── loadTopScorers: fetch all players for today's games ────────
  const generateNHLParlay=(size,forceStratIdx)=>{
    if(!topScorers.length){setView("parlay");setLoadingNhlParlay(false);return;}
    const n=size||nhlParlaySize;
    setLoadingNhlParlay(true);setNhlParlay(null);setView("parlay");
    const strategies=[
      {name:"Best Overall Score",desc:"Top composite goal scores across all factors",emoji:"🔥",
        sort:(a,b)=>b.goalScore-a.goalScore,filter:p=>p.goalScore>=35},
      {name:"Hot Streak",desc:"Players on fire — most goals in last 10 games",emoji:"📈",
        sort:(a,b)=>(b.goalDetails?.l10G||0)-(a.goalDetails?.l10G||0)||(b.goalScore-a.goalScore),
        filter:p=>(p.goalDetails?.l10G||0)>=2},
      {name:"Weak Defense",desc:"Playing against teams allowing the most goals (last 10 games)",emoji:"🛡",
        sort:(a,b)=>{const gaA=teamDef[a.opp]?.gaPerGame||0;const gaB=teamDef[b.opp]?.gaPerGame||0;return gaB-gaA||(b.goalScore-a.goalScore);},
        filter:p=>(teamDef[p.opp]?.gaPerGame||0)>=2.5||(p.goalDetails?.defSc||0)>=3},
      {name:"Sniper",desc:"Highest goals/game rate this season",emoji:"🎯",
        sort:(a,b)=>parseFloat(b.goalDetails?.gpg||0)-parseFloat(a.goalDetails?.gpg||0)||(b.goalScore-a.goalScore),
        filter:p=>parseFloat(p.goalDetails?.gpg||0)>=0.25},
      {name:"Power Play Threat",desc:"Top PP goal scorers in favorable matchups",emoji:"⚡",
        sort:(a,b)=>(b.goalDetails?.ppGoals||0)-(a.goalDetails?.ppGoals||0)||(b.goalScore-a.goalScore),
        filter:p=>(p.goalDetails?.ppGoals||0)>=5},
    ];
    const analyst=NHL_ANALYSTS[forceStratIdx!==undefined?forceStratIdx:nhlSelectedAnalyst];
    const stratIdx=analyst.strategy;
    const strat=strategies[stratIdx%strategies.length];
    try{
      // Eligible — passes strategy filter, game not final
      const eligible=[...topScorers].filter(p=>p.gameStatus!=="final"&&strat.filter(p)).sort(strat.sort);
      // Fallback — full pool sorted by same criteria (no strategy filter)
      const fallback=[...topScorers].filter(p=>p.gameStatus!=="final"&&!eligible.find(e=>e.id===p.id)).sort(strat.sort);
      const pool=[...eligible,...fallback];
      const usedGames=new Set();
      const usedPlayers=new Set();
      const picks=[];
      const makePick=(p)=>{
        const d=p.goalDetails||{};
        const reasons=[];
        if(d.l10G>=3) reasons.push(`${d.l10G}G last 10`);
        if(parseFloat(d.gpg||0)>=0.35) reasons.push(`${d.gpg} G/game`);
        if(d.ppGoals>=8) reasons.push(`${d.ppGoals} PP goals`);
        if((teamDef[p.opp]?.gaPerGame||0)>=2.8||d.defSc>=5) reasons.push(`opp allows ${(teamDef[p.opp]?.gaPerGame||0).toFixed(2)} GA/g`);
        if(d.vsOppGoals>=2) reasons.push(`${d.vsOppGoals}G vs ${p.opp}`);
        const origRank=topScorers.findIndex(t=>t.id===p.id)+1;
        return{rank:origRank,name:p.name,team:p.team,opp:p.opp,id:p.id,score:p.goalScore,reason:reasons.length?reasons.join(" · "):"Strong overall matchup",gameTime:p.gt};
      };
      // First pass — one player per game
      for(const p of pool){
        if(picks.length>=n) break;
        const gameKey=[p.team,p.opp].sort().join("-");
        if(!usedGames.has(gameKey)&&!usedPlayers.has(String(p.id))){
          usedGames.add(gameKey);
          usedPlayers.add(String(p.id));
          picks.push(makePick(p));
        }
      }
      // Second pass — fill remaining, allow multiple per game, no duplicate players
      if(picks.length<n){
        for(const p of pool){
          if(picks.length>=n) break;
          if(!usedPlayers.has(String(p.id))){
            usedPlayers.add(String(p.id));
            picks.push(makePick(p));
          }
        }
      }
      if(!picks.length){setNhlParlay({error:"No eligible players — try a different strategy."});setLoadingNhlParlay(false);return;}
      const avgScore=Math.round(picks.reduce((a,p)=>a+p.score,0)/picks.length);
      const confidence=avgScore>=65?"HIGH":avgScore>=50?"MEDIUM":"LOW";
      const note=nhlParlayLocked?`${n}-pick selections · locked`:`${n}-pick selections · ${usedGames.size} games · updates as games start`;
      setNhlParlay({picks,strategy:strat.name,strategyEmoji:strat.emoji,strategyDesc:strat.desc,analystId:analyst.id,parlayNote:note,confidence});
      // Only save to Redis once picks are locked
      try{
        setNhlTodayPicks(prev=>({...prev,[analyst.id]:{analystId:analyst.id,size:n,picks}}));
        if(nhlParlayLocked&&!nhlTodayPicks[analyst.id]){
          fetch(`/api/picks?sport=nhl`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({analystId:analyst.id,size:n,picks})}).catch(()=>{});
        }
      }catch{}
    }catch(e){setNhlParlay({error:"Failed to generate. Try again."});}
    setLoadingNhlParlay(false);
  };

  const loadTopScorers=useCallback(async()=>{
    if(topLoaded){setView("top");return;}
    setView("top");setLoadingTop(true);setTopScorers([]);
    // Try feed — rankings pre-computed by server cron
    try{
      const d=await feedFetch(`/api/feed?sport=nhl&date=${selDate}`);
      console.log('[NHL] feed date:', selDate, 'rankings:', d?.nhl?.rankings?.length, 'error:', d?.error);
      if(d?.nhl?.rankings?.length){
        if(d.nhl.teamDef&&Object.keys(d.nhl.teamDef).length) setTeamDef(d.nhl.teamDef);
        setTopScorers(d.nhl.rankings);
        setTopLoaded(true);
        setLoadingTop(false);
        // Auto-save analyst picks
        const existingNhlPicks=await fetch(`/api/picks?sport=nhl`).then(r=>r.json()).catch(()=>({picks:[]}));
        if(!existingNhlPicks?.picks?.length){
          const all=d.nhl.rankings;
          const freshTeamDef=d.nhl.teamDef||{};
          const strategies=[
            {sort:(a,b)=>b.goalScore-a.goalScore,filter:p=>p.goalScore>=35},
            {sort:(a,b)=>(b.goalDetails?.l10G||0)-(a.goalDetails?.l10G||0)||(b.goalScore-a.goalScore),filter:p=>(p.goalDetails?.l10G||0)>=2},
            {sort:(a,b)=>{const gaA=freshTeamDef[a.opp]?.gaPerGame||0;const gaB=freshTeamDef[b.opp]?.gaPerGame||0;return gaB-gaA||(b.goalScore-a.goalScore);},filter:p=>(freshTeamDef[p.opp]?.gaPerGame||0)>=2.5},
            {sort:(a,b)=>parseFloat(b.goalDetails?.gpg||0)-parseFloat(a.goalDetails?.gpg||0)||(b.goalScore-a.goalScore),filter:p=>parseFloat(p.goalDetails?.gpg||0)>=0.25},
            {sort:(a,b)=>(b.goalDetails?.ppGoals||0)-(a.goalDetails?.ppGoals||0)||(b.goalScore-a.goalScore),filter:p=>(p.goalDetails?.ppGoals||0)>=5},
          ];
          const autoSave=[];
          NHL_ANALYSTS.forEach(analyst=>{
            try{
              const strat=strategies[analyst.strategy];
              const n=6;
              const eligible=[...all].filter(p=>p.gameStatus!=="final"&&strat.filter(p)).sort(strat.sort);
              const fallback=[...all].filter(p=>!eligible.find(e=>e.id===p.id)).sort(strat.sort);
              const pool=[...eligible,...fallback];
              const usedGames=new Set();
              const usedPlayers=new Set();
              const picks=[];
              // First pass — one player per game
              for(const p of pool){
                if(picks.length>=n) break;
                const gameKey=[p.team,p.opp].sort().join("-");
                if(!usedGames.has(gameKey)&&!usedPlayers.has(String(p.id))){
                  usedGames.add(gameKey);
                  usedPlayers.add(String(p.id));
                  const dd=p.goalDetails||{};
                  const reasons=[];
                  if(dd.l10G>=3) reasons.push(`${dd.l10G}G last 10`);
                  if(parseFloat(dd.gpg||0)>=0.35) reasons.push(`${dd.gpg} G/game`);
                  if(dd.ppGoals>=8) reasons.push(`${dd.ppGoals} PP goals`);
                  if((teamDef[p.opp]?.gaPerGame||0)>=2.8) reasons.push(`opp allows ${(teamDef[p.opp]?.gaPerGame||0).toFixed(2)} GA/g`);
                  const origRank=all.findIndex(t=>t.id===p.id)+1;
                  picks.push({rank:origRank,name:p.name,team:p.team,opp:p.opp,id:p.id,score:p.goalScore,reason:reasons.length?reasons.join(" · "):"Strong overall matchup",gameTime:p.gt});
                }
              }
              // Second pass — fill remaining slots, allow multiple per game
              if(picks.length<n){
                for(const p of pool){
                  if(picks.length>=n) break;
                  if(!usedPlayers.has(String(p.id))){
                    usedPlayers.add(String(p.id));
                    const dd=p.goalDetails||{};
                    const reasons=[];
                    if(dd.l10G>=3) reasons.push(`${dd.l10G}G last 10`);
                    if(parseFloat(dd.gpg||0)>=0.35) reasons.push(`${dd.gpg} G/game`);
                    if(dd.ppGoals>=8) reasons.push(`${dd.ppGoals} PP goals`);
                    if((teamDef[p.opp]?.gaPerGame||0)>=2.8) reasons.push(`opp allows ${(teamDef[p.opp]?.gaPerGame||0).toFixed(2)} GA/g`);
                    const origRank=all.findIndex(t=>t.id===p.id)+1;
                    picks.push({rank:origRank,name:p.name,team:p.team,opp:p.opp,id:p.id,score:p.goalScore,reason:reasons.length?reasons.join(" · "):"Strong overall matchup",gameTime:p.gt});
                  }
                }
              }
              if(picks.length>0) autoSave.push({analystId:analyst.id,size:n,picks});
            }catch(e){}
          });
          if(autoSave.length>0){
            const picksMap={};
            autoSave.forEach(({analystId,size,picks})=>{
              picksMap[analystId]={analystId,size,picks};
              fetch(`/api/picks?sport=nhl`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({analystId,size,picks})}).catch(()=>{});
            });
            setNhlTodayPicks(picksMap);
          }
        }
        setNhlParlayLocked(true);
        return;
      }
    }catch(e){}
    // Feed not ready — show updating message
    setLoadingTop(false);
    setTopLoaded(false);
    setTopScorers([]);
  },[topLoaded,selDate]);

  // ── pickGame: load roster for single game click ────────────────
  const pickGame=useCallback(async g=>{
    const id=g.id;
    if(sel===id){setSel(null);return;}
    setSel(id);if(gameData[id])return;setLoadingG(true);
    try{
      const awayA=g.awayTeam?.abbrev;const homeA=g.homeTeam?.abbrev;
      const awayPlayers=topScorers.filter(p=>p.team===awayA);
      const homePlayers=topScorers.filter(p=>p.team===homeA);
      setGameData(prev=>({...prev,[id]:{awayTeam:g.awayTeam,homeTeam:g.homeTeam,awayPlayers,homePlayers}}));
    }catch(e){console.error(e);}
    setLoadingG(false);
  },[sel,gameData,topScorers]);

  const fmtNHLTime=dt=>{try{return new Date(dt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",timeZone:"America/New_York"});}catch{return "";}};

  // ── renderNHLRow ───────────────────────────────────────────────
  const renderNHLRow=(p,rank)=>{
    // Special render for unranked goal scorers (appeared in goal feed but not in top scorers list)
    if(p._unrankedScorer){
      const gr=goalResults[String(p.id)];
      return(
        <tr key={p.id+"us"} style={{borderBottom:"1px solid #0a1020"}}>
          <td style={{padding:"9px 8px",width:40}}><span style={{fontFamily:"monospace",fontSize:11,color:"#2a3a50"}}>—</span></td>
          <td style={{padding:"9px 8px",minWidth:160}} colSpan={10}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <img src={`https://assets.nhle.com/mugs/nhl/20252026/${p.team}/${p.id}.png`}
                alt={p.name} style={{width:40,height:40,borderRadius:"50%",border:"2px solid #00e67660",objectFit:"cover",flexShrink:0,background:"#0a1020"}}
                onError={e=>{e.target.style.display="none";}}/>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                  <span style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{p.name}</span>
                  <span style={{fontFamily:"monospace",fontSize:11,color:"#7090c0",background:"#0d1322",borderRadius:3,padding:"1px 6px"}}>{p.team}</span>
                  <div style={{display:"flex",alignItems:"center",gap:3,background:"#0a2010",border:"1px solid #00e67650",borderRadius:4,padding:"1px 8px"}}>
                    <span style={{fontSize:12}}>🚨</span>
                    <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#00e676"}}>{gr?.count||1} GOAL{(gr?.count||1)>1?"S":""} TODAY</span>
                  </div>
                  <span style={{fontSize:10,color:"#2a3a50",fontStyle:"italic"}}>not ranked</span>
                </div>
                {gr?.game&&<div style={{fontSize:9,color:"#00e676",marginTop:1,opacity:0.8}}>✅ {gr.game}</div>}
              </div>
            </div>
          </td>
        </tr>
      );
    }
    const oppDef=teamDef[p.opp]||null;
    const sc=scGoal(p,oppDef);if(!sc)return null;
    const clr=scoreColor(sc.total);
    const medal=rank===0?"🥇":rank===1?"🥈":rank===2?"🥉":null;
    const thS={fontSize:9,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"6px 10px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap"};
    // Game status badge
    const statusBadge=p.gameStatus==="live"
      ?<span style={{fontSize:8,color:"#00e676",background:"#00e67615",border:"1px solid #00e67640",borderRadius:2,padding:"1px 4px",marginLeft:4}}>LIVE</span>
      :p.gameStatus==="final"
      ?<span style={{fontSize:8,color:"#4a6080",background:"#1a2030",border:"1px solid #2a3040",borderRadius:2,padding:"1px 4px",marginLeft:4}}>FINAL</span>
      :null;
    // Opp defense display
    const gaC=oppDef?(oppDef.gaPerGame>=3.3?"#ff4060":oppDef.gaPerGame>=2.9?"#ffb020":"#4a6080"):"#4a6080";
    return(
      <tr key={p.id} style={{borderBottom:"1px solid #0a1020",background:rank<3?"#0a0f1e":rank<10?"#090d18":"transparent"}}
        onMouseEnter={e=>e.currentTarget.style.background="#0c1428"}
        onMouseLeave={e=>e.currentTarget.style.background=rank<3?"#0a0f1e":rank<10?"#090d18":"transparent"}>
        <td style={{padding:"9px 8px",width:40}}>{medal?<span style={{fontSize:16}}>{medal}</span>:<span style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:rank<10?"#5080c0":"#2e4060"}}>#{rank+1}</span>}</td>
        <td style={{padding:"9px 8px",minWidth:160}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {rank<10&&<img loading="lazy" src={`https://assets.nhle.com/mugs/nhl/20252026/${p.team}/${p.id}.png`} alt={p.name} style={{width:rank<3?44:36,height:rank<3?44:36,borderRadius:"50%",border:`2px solid ${clr}60`,objectFit:"cover",flexShrink:0,background:"#0a1020"}} onError={e=>{e.target.style.display="none";}}/>}
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                <span style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{p.name}</span>
                {statusBadge}
                {goalResults[String(p.id)]&&(
                  <div style={{display:"flex",alignItems:"center",gap:3,background:"#0a2010",border:"1px solid #00e67650",borderRadius:4,padding:"1px 8px"}}>
                    <span style={{fontSize:12}}>🚨</span>
                    <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#00e676"}}>{goalResults[String(p.id)].count} GOAL{goalResults[String(p.id)].count>1?"S":""} TODAY</span>
                  </div>
                )}
              </div>
              <div style={{fontSize:10,color:"#3a5070",marginTop:1}}>{p.pos} · {p.team} · {p.isHome?"🏠 Home":"✈ Away"}{sc.splitGpg?<span style={{color:sc.homeSplitSc>0?"#00e676":sc.homeSplitSc<0?"#ff4060":"#3a5070",marginLeft:4}}>{sc.splitGpg}G/g {p.isHome?"@home":"away"}</span>:""} · {p.gt}</div>
              {goalResults[String(p.id)]&&<div style={{fontSize:9,color:"#00e676",marginTop:1,opacity:0.8}}>✅ {goalResults[String(p.id)].game}</div>}
            </div>
          </div>
        </td>
        <td style={{padding:"9px 8px",width:44}}><div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#5070a0"}}>vs {p.opp}</div></td>
        <td style={{padding:"9px 8px",width:54}}><div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:46,height:32,borderRadius:5,background:clr+"20",border:`1px solid ${clr}60`,fontFamily:"monospace",fontSize:17,fontWeight:700,color:clr}}>{sc.total}</div></td>
        <td style={{padding:"9px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:parseFloat(sc.gpg)>=0.40?"#00e676":parseFloat(sc.gpg)>=0.25?"#ffb020":"#4a6080"}}>{sc.gpg}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>G/GAME</div></td>
        <td style={{padding:"9px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:sc.l10G>=5?"#00e676":sc.l10G>=3?"#ffb020":"#4a6080"}}>{sc.l10G}G · {sc.l10Pts}PTS</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>LAST 10</div></td>
        <td style={{padding:"9px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:parseFloat(sc.spg)>=3.5?"#00e676":parseFloat(sc.spg)>=2.5?"#ffb020":"#4a6080"}}>{sc.spg}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>SHOTS/G</div></td>
        <td style={{padding:"9px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:sc.ppGoals>=8?"#00e676":sc.ppGoals>=4?"#ffb020":"#4a6080"}}>{sc.ppGoals}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>PP GOALS</div></td>
        <td style={{padding:"9px 10px",whiteSpace:"nowrap"}}>
          <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:gaC}}>{oppDef?`${oppDef.gaPerGame.toFixed(2)} GA/g`:"—"}</div>
          <div style={{fontSize:9,color:"#344560",marginTop:1}}>{oppDef&&oppDef.pkPct<79?<span style={{color:"#ff7040"}}>PK {oppDef.pkPct.toFixed(0)}% WEAK</span>:"L10 GA/G AVG"}</div>
        </td>
        <td style={{padding:"9px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:sc.vsOppGoals>=2?"#00e676":sc.vsOppGoals>=1?"#ffb020":"#4a6080"}}>{sc.vsOppGames>0?`${sc.vsOppGoals}G / ${sc.vsOppGames}GP`:"—"}</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>SEASON vs {p.opp}</div></td>
        <td style={{padding:"9px 10px",whiteSpace:"nowrap"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#7090c0"}}>{sc.goals}G · {sc.points}PTS</div><div style={{fontSize:9,color:"#344560",marginTop:1}}>SEASON · {sc.gp}GP</div></td>
      </tr>
    );
  };

  // ── renderGameRoster: single game view ─────────────────────────
  const renderGameRoster=(gd)=>{
    if(!gd)return<Spinner text="LOADING ROSTERS..."/>;
    const thS={fontSize:9,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"5px 8px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap"};
    const renderTeam=(players,label,oppAbbrev)=>{
      const oppDef=teamDef[oppAbbrev]||null;
      const sorted=[...players].map(p=>({...p,sc:scGoal(p,oppDef)})).filter(p=>p.sc).sort((a,b)=>b.sc.total-a.sc.total);
      return(
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,letterSpacing:2,fontWeight:700,color:"#3a5570",padding:"5px 2px",borderBottom:"1px solid #101c30",marginBottom:6}}>{label}</div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
              <thead><tr>{["Player","Pos","Score","G/Game","Last 10","Shots/G","PP Goals","vs Opp","Season"].map((h,i)=><th key={i} style={thS}>{h}</th>)}</tr></thead>
              <tbody>{sorted.map(p=>{const{sc}=p;const clr=scoreColor(sc.total);return(
                <tr key={p.id} style={{borderBottom:"1px solid #0a1020"}}>
                  <td style={{padding:"8px 6px",minWidth:150}}><div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:6,height:6,borderRadius:"50%",background:clr,flexShrink:0,boxShadow:sc.total>=60?`0 0 5px ${clr}`:"none"}}/><span style={{fontSize:14,fontWeight:700,color:"#d0dff0"}}>{p.name}</span></div></td>
                  <td style={{padding:"8px 6px",width:36}}><span style={{fontFamily:"monospace",fontSize:11,color:"#5070a0"}}>{p.pos}</span></td>
                  <td style={{padding:"8px 6px",width:46}}><div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:40,height:26,borderRadius:4,background:clr+"18",border:`1px solid ${clr}55`,fontFamily:"monospace",fontSize:14,fontWeight:700,color:clr}}>{sc.total}</div></td>
                  <td style={{padding:"8px 8px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:parseFloat(sc.gpg)>=0.40?"#00e676":parseFloat(sc.gpg)>=0.25?"#ffb020":"#4a6080"}}>{sc.gpg}</div></td>
                  <td style={{padding:"8px 8px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:sc.l10G>=5?"#00e676":sc.l10G>=3?"#ffb020":"#4a6080"}}>{sc.l10G}G / {sc.l10Pts}PTS</div></td>
                  <td style={{padding:"8px 8px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:parseFloat(sc.spg)>=3.5?"#00e676":parseFloat(sc.spg)>=2.5?"#ffb020":"#4a6080"}}>{sc.spg}</div></td>
                  <td style={{padding:"8px 8px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:sc.ppGoals>=8?"#00e676":sc.ppGoals>=4?"#ffb020":"#4a6080"}}>{sc.ppGoals}</div></td>
                  <td style={{padding:"8px 8px"}}><div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:sc.vsOppGoals>=2?"#00e676":sc.vsOppGoals>=1?"#ffb020":"#4a6080"}}>{sc.vsOppGames>0?`${sc.vsOppGoals}G/${sc.vsOppGames}GP`:"—"}</div></td>
                  <td style={{padding:"8px 8px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:"#7090c0"}}>{sc.goals}G / {sc.points}PTS in {sc.gp}GP</div></td>
                </tr>
              );})}</tbody>
            </table>
          </div>
        </div>
      );
    };
    return(
      <div style={{padding:"14px 16px 28px"}}>
        {renderTeam(gd.awayPlayers,`▲ AWAY — ${(gd.awayTeam?.placeName?.default||gd.awayTeam?.abbrev||"").toUpperCase()} ${(gd.awayTeam?.commonName?.default||"").toUpperCase()}`,gd.homeTeam?.abbrev)}
        {renderTeam(gd.homePlayers,`▼ HOME — ${(gd.homeTeam?.placeName?.default||gd.homeTeam?.abbrev||"").toUpperCase()} ${(gd.homeTeam?.commonName?.default||"").toUpperCase()}`,gd.awayTeam?.abbrev)}
      </div>
    );
  };

  if(loading)return<Spinner text="LOADING NHL SCHEDULE..."/>;
  if(err)return<ErrMsg msg={err}/>;

  const thS={fontSize:9,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"6px 10px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap"};

  return(
    <>
      {/* Rail */}
      <div style={{display:"flex",gap:6,padding:"8px 10px",overflowX:"auto",background:"#07090e",borderBottom:"1px solid #0f1826",alignItems:"center",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        <button onClick={()=>view==="top"?setView("games"):loadTopScorers()} style={{flexShrink:0,padding:"8px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="top"?"#0a1e10":"#0c1322",border:`1px solid ${view==="top"?"#00e676":"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="top"?"#00e676":"#3a5070"}}>
          {loadingTop?"⏳":"🏒"} TOP GOAL SCORERS
        </button>
        <button onClick={()=>{
          if(view==="parlay"){setView("games");return;}
          if(!topScorers.length){loadTopScorers().then(()=>setView("parlay"));}
          else{generateNHLParlay();}
        }} style={{flexShrink:0,padding:"8px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="parlay"?"#12051a":"#0c1322",border:`1px solid ${view==="parlay"?"#c060ff":"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="parlay"?"#c060ff":"#3a5070"}}>
          {loadingNhlParlay?"⏳":"🎯"} AI GOAL PICKER
        </button>
        <div style={{width:1,height:36,background:"#182030",flexShrink:0}}/>
        {games.length===0?<div style={{color:"#3a5070",fontSize:13}}>No NHL games today</div>:[...games].sort((a,b)=>{const o=g=>{const s=g.gameState||"FUT";return s==="LIVE"||s==="CRIT"?0:s==="FUT"||s==="PRE"?1:2;};return o(a)-o(b);}).map(g=>{
          const isA=sel===g.id;const st=g.gameState||"FUT";
          const live=st==="LIVE"||st==="CRIT";const final=st==="OFF";
          const score=nhlLive[g.id];
          return(
            <div key={g.id} onClick={()=>{setView("games");pickGame(g);}} style={{flexShrink:0,background:isA?"#0a1e30":"#0d1322",border:`1px solid ${isA?"#00c8ff":live?"#00e67640":"#182030"}`,borderRadius:6,padding:"9px 14px",cursor:"pointer",minWidth:145,transition:"all 0.12s",boxShadow:isA?"0 0 12px rgba(0,200,255,0.1)":"none"}}>
              <div style={{fontSize:16,fontWeight:800,color:"#fff",letterSpacing:1}}>{g.awayTeam?.abbrev||"?"} @ {g.homeTeam?.abbrev||"?"}</div>
              {score&&(live||final)?
                <div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:live?"#00e676":"#c8d6e8",marginTop:2}}>{score.away} – {score.home}{live?` · P${score.period} ${score.clock}`:""}</div>:
                <div style={{fontFamily:"monospace",fontSize:10,color:"#3a5070",marginTop:3}}>{fmtNHLTime(g.startTimeUTC)} ET</div>
              }
              <div style={{display:"inline-block",fontSize:9,padding:"2px 6px",borderRadius:3,marginTop:4,fontWeight:700,background:live?"#001a0030":"#1a2a4a",color:live?"#00e676":final?"#4a72b0":"#3a5070",border:`1px solid ${live?"#00e67630":final?"#1e3060":"#182030"}`}}>
                {live?"● LIVE":final?"Final":"Upcoming"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Game status filter tabs — above goal feed */}
      <div style={{display:"flex",gap:4,padding:"8px 14px",background:"#07090e",borderBottom:"1px solid #0f1826",flexWrap:"wrap"}}>
        {[["all","ALL GAMES"],["upcoming","⏰ UPCOMING"],["live","🔴 LIVE"],["final","✅ FINAL"],["scored","🚨 GOAL SCORERS"]].map(([f,label])=>{
          const cnt=f==="all"?topScorers.length
            :f==="scored"?Object.keys(goalResults).length
            :topScorers.filter(p=>p.gameStatus===f).length;
          return(
            <button key={f} onClick={()=>setNhlFilter(f)} style={{padding:"5px 14px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:700,letterSpacing:1,background:nhlFilter===f?"#081828":"#0c1322",border:`1px solid ${nhlFilter===f?"#00c8ff":"#182030"}`,borderRadius:4,cursor:"pointer",color:nhlFilter===f?"#00c8ff":"#3a5070"}}>
              {label}{cnt>0?<span style={{fontSize:10,opacity:0.7,marginLeft:4}}>({cnt})</span>:null}
            </button>
          );
        })}
      </div>

      {/* Goal feed - below filter tabs */}
      {(goalFeed.length>0||games.some(g=>g.gameState==="LIVE"||g.gameState==="CRIT"))&&(
        <div style={{background:"#06100a",border:"1px solid #0d2010",borderRadius:7,margin:"10px 14px 0",overflow:"hidden"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 14px",borderBottom:"1px solid #0d2010",cursor:"pointer"}} onClick={()=>setShowGoalFeed(v=>!v)}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:"#00e676",boxShadow:"0 0 6px #00e676",animation:"spin 2s linear infinite"}}/>
              <span style={{fontSize:13,fontWeight:800,color:"#00e676",letterSpacing:1.5}}>LIVE GOAL FEED</span>
              {goalFeed.length>0&&<span style={{fontFamily:"monospace",fontSize:11,color:"#00e676",background:"#0a2010",border:"1px solid #00e67630",borderRadius:3,padding:"1px 7px"}}>{goalFeed.length} GOAL{goalFeed.length!==1?"S":""} TODAY</span>}
            </div>
            <span style={{fontSize:11,color:"#2a4a30",letterSpacing:1}}>{showGoalFeed?"▲ HIDE":"▼ SHOW"}</span>
          </div>
          {showGoalFeed&&(
            goalFeed.length===0?(
              <div style={{padding:"12px 14px",fontFamily:"monospace",fontSize:11,color:"#2a4a30",letterSpacing:1}}>No goals yet today · Updates every 45 seconds</div>
            ):(
              <div style={{maxHeight:280,overflowY:"auto"}}>
                {goalFeed.map((g,i)=>{
                  const onList=topScorers.findIndex(p=>String(p.id)===String(g.scorer));
                  const isRanked=onList>=0;
                  return(
                    <div key={g.id||i} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:"1px solid #0a1a0c",background:isRanked?"#0c1e0e":"transparent"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#0e2010"}
                      onMouseLeave={e=>e.currentTarget.style.background=isRanked?"#0c1e0e":"transparent"}>
                      <div style={{fontSize:18,flexShrink:0}}>🚨</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          <span style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{g.name}</span>
                          <span style={{fontFamily:"monospace",fontSize:11,color:"#00e676",background:"#0a2010",border:"1px solid #00e67630",borderRadius:3,padding:"1px 6px"}}>{g.team}</span>
                          {isRanked&&<span style={{fontSize:10,color:onList<15?"#ffb020":"#5070a0",background:onList<15?"#1a1000":"#0a1020",border:`1px solid ${onList<15?"#ffb02030":"#2a4060"}`,borderRadius:3,padding:"1px 6px",fontWeight:700}}>{onList<15?"⭐":""} #{onList+1} RANKED</span>}
                        </div>
                        <div style={{fontSize:10,color:"#3a5a40",marginTop:2}}>
                          {g.period} {g.time} · {g.gameStr}
                          <span style={{marginLeft:6,fontFamily:"monospace",color:"#4a7a50"}}>{g.awayAbb} {g.awayScore}–{g.homeScore} {g.homeAbb}</span>
                        </div>
                      </div>
                      <div style={{fontFamily:"monospace",fontSize:10,color:"#2a4a30",flexShrink:0}}>{g.state==="OFF"?"Final":g.period}</div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}

      {/* Top Goal Scorers view */}
      {view==="top"&&(
        <div style={{padding:"14px 16px 32px"}}>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:2}}>🏒 TOP GOAL SCORERS TODAY</div>
            <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>RANKED BY SCORE · G/GAME · LAST 10 FORM · SHOTS/GAME · PP GOALS · OPP DEFENSE · VS OPPONENT</div>
          </div>

          {loadingTop&&(
            <div style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:7,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:16,height:16,border:"2px solid #1a2540",borderTopColor:"#00e676",borderRadius:"50%",animation:"spin 0.8s linear infinite",flexShrink:0}}/>
              <div>
                <div style={{fontSize:12,color:"#4a6080",letterSpacing:1}}>Scanning {games.length} games · {topScorers.length} players ranked so far...</div>
                <div style={{fontSize:10,color:"#2a3a50",marginTop:3}}>Fetching rosters + season stats + last 10 games + opponent defense</div>
              </div>
            </div>
          )}
          {!loadingTop&&topScorers.length===0&&(
            games.length===0?(
              <div>
                <div style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:7,padding:"16px 20px",marginBottom:16,textAlign:"center"}}>
                  <div style={{fontSize:20,marginBottom:6}}>🏒</div>
                  <div style={{fontSize:14,fontWeight:800,color:"#00e676",letterSpacing:1,marginBottom:4}}>NO NHL GAMES TODAY</div>
                  <div style={{fontSize:11,color:"#2a3a50"}}>Check back on a game day</div>
                </div>
                {nhlLeaders&&(
                  <div>
                    <div style={{marginBottom:16}}>
                      <div style={{fontSize:20,fontWeight:900,color:"#fff",letterSpacing:2}}>🏒 NHL SEASON LEADERS</div>
                      <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>CURRENT SEASON RANKINGS</div>
                    </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
                  {[["goals","🚨 GOALS"],["assists","🎯 ASSISTS"],["points","⭐ POINTS"]].map(([cat,label])=>(
                    <div key={cat} style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:8,overflow:"hidden"}}>
                      <div style={{padding:"10px 14px",background:"#0a1020",borderBottom:"1px solid #111c2e"}}>
                        <div style={{fontSize:13,fontWeight:900,color:"#00c8ff",letterSpacing:2}}>{label}</div>
                      </div>
                      {(nhlLeaders[cat]||[]).map((p,i)=>(
                        <div key={p.name} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:"1px solid #0a1020",background:i<3?"#090d18":"transparent"}}>
                          <span style={{fontFamily:"monospace",fontSize:12,color:"#3a5070",width:20,textAlign:"right",flexShrink:0}}>
                            {i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}
                          </span>
                          {p.id&&i<10&&<img src={`https://assets.nhle.com/mugs/nhl/20252026/${p.team}/${p.id}.png`} alt={p.name}
                            style={{width:28,height:28,borderRadius:"50%",objectFit:"cover",background:"#0a1020",flexShrink:0,border:"1px solid #00c8ff40"}}
                            onError={e=>{e.target.style.display="none";}}/>}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:"#e8f0ff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.shortName}</div>
                            <div style={{fontSize:10,color:"#3a5070"}}>{p.team}</div>
                          </div>
                          <div style={{fontFamily:"monospace",fontSize:16,fontWeight:900,color:i===0?"#00c8ff":i<3?"#7eb8f7":"#5080c0",flexShrink:0}}>{p.displayValue}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
                )}
              </div>
            ):(
              <div style={{textAlign:"center",padding:40}}>
                <div style={{fontSize:24,marginBottom:8}}>⏳</div>
                <div style={{fontSize:14,color:"#00e676",fontWeight:800,letterSpacing:1,marginBottom:6}}>RANKINGS UPDATING</div>
                <div style={{fontSize:11,color:"#2a3a50"}}>Server is computing rankings · Ready within 30 min</div>
                <div style={{fontSize:10,color:"#1a2a40",marginTop:4}}>Refreshes automatically when ready</div>
              </div>
            )
          )}
          {topScorers.length>0&&(()=>{
            const allScorers=nhlFilter==="all"?topScorers
              :nhlFilter==="scored"
                ?[...topScorers.filter(p=>goalResults[String(p.id)]),...Object.entries(goalResults)
                    .filter(([id])=>!topScorers.find(p=>String(p.id)===id))
                    .map(([id,gr])=>({id,name:gr.name,team:gr.team,goalScore:0,goalDetails:null,gameStatus:"final",opp:"",gt:"",goals:0,last10:{g:0,pts:0,gms:1},gamesPlayed:1,ppGoals:0,shots:0,points:0,vsOppGoals:0,vsOppGames:0,_unrankedScorer:true}))]
              :topScorers.filter(p=>p.gameStatus===nhlFilter);
            const filtered=nhlIsPro?allScorers:allScorers.slice(0,10);
            if(filtered.length===0) return <div style={{textAlign:"center",padding:40,color:"#243650",fontSize:13}}>{nhlFilter==="scored"?"No goals scored yet today":`No ${nhlFilter} games`}</div>;
            const getNHLVal=(p,col)=>{
              const sc=scGoal(p,teamDef[p.opp]||null)||{};
              if(col==="total") return sc.total||0;
              if(col==="gpg") return parseFloat(sc.gpg)||0;
              if(col==="l10G") return sc.l10G||0;
              if(col==="spg") return parseFloat(sc.spg)||0;
              if(col==="ppGoals") return sc.ppGoals||0;
              if(col==="gaPerGame") return teamDef[p.opp]?.gaPerGame||0;
              return 0;
            };
            const nhlSorted=[...filtered].sort((a,b)=>nhlSort.dir==="desc"?getNHLVal(b,nhlSort.col)-getNHLVal(a,nhlSort.col):getNHLVal(a,nhlSort.col)-getNHLVal(b,nhlSort.col));
            const NHLSortTh=({col,label})=>{
              const active=nhlSort.col===col;
              return <th onClick={()=>setNhlSort(s=>({col,dir:s.col===col&&s.dir==="desc"?"asc":"desc"}))} style={{...thS,cursor:"pointer",color:active?"#00c8ff":"#2e4460",userSelect:"none"}}>{label}{active?(nhlSort.dir==="desc"?" ▼":" ▲"):" ⇅"}</th>;
            };
            return(
              <div>
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:860}}>
                    <thead><tr>
                      <th style={thS}>RNK</th><th style={thS}>Player</th><th style={thS}>vs</th>
                      <NHLSortTh col="total" label="Score"/>
                      <NHLSortTh col="gpg" label="G/Game"/>
                      <NHLSortTh col="l10G" label="Last 10"/>
                      <NHLSortTh col="spg" label="Shots/G"/>
                      <NHLSortTh col="ppGoals" label="PP Goals"/>
                      <NHLSortTh col="gaPerGame" label="Opp DEF"/>
                      <th style={thS}>vs Opp</th><th style={thS}>Season</th>
                    </tr></thead>
                    <tbody>{nhlSorted.map((p,i)=>renderNHLRow(p,i))}</tbody>
                  </table>
                </div>
                {!nhlIsPro&&(
                  <div onClick={()=>user?window.location.href='/account':signInWithGoogle()}
                    style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px",marginTop:2,background:"#0e0818",border:"1px solid #c060ff40",borderRadius:6,cursor:"pointer"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:18}}>🔒</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:800,color:"#c8d0e8"}}>Full rankings hidden</div>
                        <div style={{fontSize:11,color:"#7040a0",marginTop:2}}>Pro unlocks every player · all sports</div>
                      </div>
                    </div>
                    <div style={{flexShrink:0,padding:"9px 22px",background:"linear-gradient(135deg,#7020c0,#c060ff)",borderRadius:5,fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:900,color:"#fff",letterSpacing:1.5,boxShadow:"0 0 14px #c060ff40"}}>
                      {user?"UPGRADE TO PRO →":"SIGN IN TO UNLOCK →"}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Game view */}
      {view==="games"&&(sel&&gameData[sel]?renderGameRoster(gameData[sel]):
      sel&&loadingG?<Spinner text="LOADING ROSTERS + STATS..."/>:(
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:300,flexDirection:"column",gap:10}}>
          <div style={{fontSize:13,color:"#243650",letterSpacing:1}}>← Click a game to view goal scoring predictions</div>
          <div style={{fontSize:11,color:"#1a2a3a"}}>or hit 🏒 Top Goal Scorers for today's best picks across all games</div>
        </div>
      ))}

      {/* NHL Parlay view */}
      {view==="parlay"&&(
        <div style={{padding:"14px 16px 32px"}}>
          {/* Header */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:2}}>🎯 AI GOAL PICKER</div>
            <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>PICK AN ANALYST · EACH HAS THEIR OWN STRATEGY · RECORDS AUTO-SETTLE AT 3AM ET</div>
          </div>

          {/* Analyst cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8,marginBottom:20}}>
            {NHL_ANALYSTS.map((a,i)=>{
              const isLocked=!nhlIsPro&&i>=2;
              const rec=nhlAnalystRecords[a.id]||{legs:{hit:0,total:0},parlays:{}};
              const hitPct=rec.legs.total>0?Math.round(rec.legs.hit/rec.legs.total*100):null;
              const isSelected=nhlSelectedAnalyst===i;
              return(
                <div key={a.id} onClick={()=>{
                  if(isLocked){user?window.location.href='/account':signInWithGoogle();return;}
                  setNhlSelectedAnalyst(i);
                  if(nhlParlayLocked){
                    // Locked — load from cache
                    if(nhlTodayPicks[a.id]){
                      const cached=nhlTodayPicks[a.id];
                      const picks=cached.picks.slice(0,nhlParlaySize);
                      setView("parlay");
                      setNhlParlay({picks,strategy:a.desc,strategyEmoji:a.face,strategyDesc:a.desc,analystId:a.id,parlayNote:`${picks.length}-pick selections · locked`,confidence:null});
                      return;
                    }
                    fetch(`/api/picks?sport=nhl`).then(r=>r.json()).then(d=>{
                      const allPicks=d?.picks||[];
                      const picksMap={};
                      allPicks.forEach(p=>{ picksMap[p.analystId]=p; });
                      setNhlTodayPicks(picksMap);
                      const analystPicks=picksMap[a.id];
                      if(analystPicks){
                        const picks=analystPicks.picks.slice(0,nhlParlaySize);
                        setView("parlay");
                        setNhlParlay({picks,strategy:a.desc,strategyEmoji:a.face,strategyDesc:a.desc,analystId:a.id,parlayNote:`${picks.length}-pick selections · locked`,confidence:null});
                      } else {
                        if(!topScorers.length) loadTopScorers().then(()=>generateNHLParlay(nhlParlaySize,a.strategy));
                        else generateNHLParlay(nhlParlaySize,a.strategy);
                      }
                    }).catch(()=>{
                      if(!topScorers.length) loadTopScorers().then(()=>generateNHLParlay(nhlParlaySize,a.strategy));
                      else generateNHLParlay(nhlParlaySize,a.strategy);
                    });
                  } else {
                    // Not locked — always generate fresh
                    if(!topScorers.length) loadTopScorers().then(()=>generateNHLParlay(nhlParlaySize,a.strategy));
                    else generateNHLParlay(nhlParlaySize,a.strategy);
                  }
                }}
                  style={{position:"relative",background:isSelected?a.color+"18":"#0c0f1a",border:`2px solid ${isSelected?a.color:"#182030"}`,borderRadius:8,padding:"10px 10px",cursor:"pointer",transition:"all 0.15s",textAlign:"center",overflow:"hidden"}}>
                  <div style={{fontSize:28,marginBottom:4}}>{a.face}</div>
                  <div style={{fontSize:14,fontWeight:900,color:isSelected?a.color:"#c8d6e8",letterSpacing:1}}>{a.name}</div>
                  <div style={{fontSize:9,color:"#3a5070",marginTop:2,letterSpacing:0.5}}>{a.desc}</div>
                  <div style={{marginTop:6,padding:"4px 0",borderTop:"1px solid #182030"}}>
                    {rec.legs.total>0?(
                      <>
                        <div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:hitPct>=40?"#00e676":hitPct>=30?"#ffb020":"#ff4060"}}>{rec.legs.hit}/{rec.legs.total}</div>
                        <div style={{fontSize:9,color:hitPct>=40?"#00e676":hitPct>=30?"#ffb020":"#ff4060",fontWeight:700}}>{hitPct}% PICKS HIT</div>
                      </>
                    ):(
                      <div style={{fontSize:9,color:"#2a3a50",fontFamily:"monospace"}}>NO RECORD YET</div>
                    )}
                    {rec.parlays?.['4']&&(
                      <div style={{marginTop:4,display:"flex",justifyContent:"center"}}>
                        <div style={{fontSize:8,fontFamily:"monospace",color:"#4a6080",background:"#0a1020",borderRadius:2,padding:"1px 4px"}}>TOP 4 RECORD: {rec.parlays['4'].hit}/{rec.parlays['4'].total}</div>
                      </div>
                    )}
                  </div>
                  {isLocked&&(
                    <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.7)",borderRadius:8,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,cursor:"pointer"}}>
                      <span style={{fontSize:16}}>🔒</span>
                      <span style={{fontSize:9,fontWeight:800,color:"#c060ff",letterSpacing:1}}>PRO ONLY</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Size selector */}
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,flexWrap:"wrap"}}>
            <span style={{fontSize:11,color:"#3a5070",letterSpacing:1}}>PICK COUNT:</span>
            <div style={{display:"flex",gap:4}}>
              {[2,3,4,5,6].map(n=>{
                const a=NHL_ANALYSTS[nhlSelectedAnalyst];
                return(
                  <button key={n} onClick={()=>{
                    setNhlParlaySize(n);
                    const allPicks=nhlTodayPicks[a?.id]?.picks||[];
                    if(nhlParlayLocked&&allPicks.length>=n){
                      setNhlParlay({picks:allPicks.slice(0,n),strategy:a?.desc,strategyEmoji:a?.face,strategyDesc:a?.desc,analystId:a?.id,parlayNote:`${n}-pick selections · locked`,confidence:null});
                    } else {
                      generateNHLParlay(n,a?.strategy);
                    }
                  }}
                    style={{padding:"6px 12px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,
                      background:nhlParlaySize===n?"#12051a":"#0c1322",
                      border:`1px solid ${nhlParlaySize===n?(NHL_ANALYSTS[nhlSelectedAnalyst]?.color||"#c060ff"):"#182030"}`,
                      borderRadius:4,cursor:"pointer",
                      color:nhlParlaySize===n?(NHL_ANALYSTS[nhlSelectedAnalyst]?.color||"#c060ff"):"#3a5070"}}>{n}-PICK</button>
                );
              })}
            </div>
            {nhlParlayLocked&&(
              <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"#080e18",border:"1px solid #1a2a3a",borderRadius:5}}>
                <span style={{fontSize:11,color:"#00e676"}}>🔒</span>
                <span style={{fontSize:11,color:"#4a7090",fontFamily:"monospace"}}>PICKS LOCKED · Top scorers loaded</span>
              </div>
            )}
          </div>

          {loadingNhlParlay&&(
            <div style={{background:"#0e0818",border:"1px solid #2a1040",borderRadius:8,padding:"32px",textAlign:"center"}}>
              <div style={{width:20,height:20,border:"2px solid #2a1040",borderTopColor:NHL_ANALYSTS[nhlSelectedAnalyst]?.color||"#c060ff",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
              <div style={{fontSize:13,color:"#7040a0",letterSpacing:1}}>{NHL_ANALYSTS[nhlSelectedAnalyst]?.face} {NHL_ANALYSTS[nhlSelectedAnalyst]?.name} is building picks...</div>
            </div>
          )}
          {nhlParlay?.error&&<div style={{color:"#ff6060",padding:20,textAlign:"center"}}>{nhlParlay.error}</div>}
          {nhlParlay?.picks&&(()=>{
            const analyst=NHL_ANALYSTS.find(a=>a.id===nhlParlay.analystId)||NHL_ANALYSTS[nhlSelectedAnalyst];
            const rec=nhlAnalystRecords[analyst.id]||{legs:{hit:0,total:0},parlays:{}};
            const hitPct=rec.legs.total>0?Math.round(rec.legs.hit/rec.legs.total*100):null;
            return(
              <div>
                {/* Active analyst banner */}
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12,padding:"10px 14px",background:analyst.color+"12",border:`1px solid ${analyst.color}40`,borderRadius:8}}>
                  <span style={{fontSize:32}}>{analyst.face}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:16,fontWeight:900,color:analyst.color,letterSpacing:1}}>{analyst.name}'s {nhlParlay.picks.length} PICKS TODAY</div>
                    <div style={{fontSize:10,color:"#5a6080",marginTop:2}}>{analyst.bio}</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    {rec.legs.total>0?(
                      <>
                        <div style={{fontFamily:"monospace",fontSize:18,fontWeight:900,color:hitPct>=40?"#00e676":hitPct>=30?"#ffb020":"#ff4060"}}>{hitPct}%</div>
                        <div style={{fontSize:9,color:"#3a5070"}}>{rec.legs.hit}/{rec.legs.total} PICKS</div>
                      </>
                    ):(
                      <div style={{fontSize:9,color:"#2a3a50",fontFamily:"monospace"}}>NO RECORD YET</div>
                    )}
                  </div>
                  {nhlParlay.confidence&&(
                    <span style={{fontSize:11,fontWeight:800,padding:"3px 10px",borderRadius:4,letterSpacing:1,
                      background:nhlParlay.confidence==="HIGH"?"#001a00":nhlParlay.confidence==="MEDIUM"?"#1a1000":"#1a0000",
                      color:nhlParlay.confidence==="HIGH"?"#00e676":nhlParlay.confidence==="MEDIUM"?"#ffb020":"#ff6060",
                      border:`1px solid ${nhlParlay.confidence==="HIGH"?"#00e67640":nhlParlay.confidence==="MEDIUM"?"#ffb02040":"#ff606040"}`
                    }}>{nhlParlay.confidence}</span>
                  )}
                </div>

                {/* Picks */}
                <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
                  {nhlParlay.picks.map((pick,i)=>{
                    const medals=["🥇","🥈","🥉","4️⃣","5️⃣","6️⃣"];
                    const clr=analyst.color;
                    const scoredToday=goalResults[String(pick.id)]?.count>0;
                    return(
                      <div key={i} style={{background:scoredToday?"#0a1e0e":"#0a0c14",border:`1px solid ${scoredToday?"#00e676":clr}30`,borderRadius:8,padding:"12px 14px",position:"relative",overflow:"hidden"}}>
                        <div style={{position:"absolute",top:0,left:0,width:3,height:"100%",background:scoredToday?"#00e676":clr,borderRadius:"4px 0 0 4px"}}/>
                        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                          <span style={{fontSize:18,flexShrink:0}}>{medals[i]||`${i+1}.`}</span>
                          <img src={`https://assets.nhle.com/mugs/nhl/20252026/${pick.team}/${pick.id}.png`}
                            alt={pick.name} style={{width:40,height:40,borderRadius:"50%",border:`2px solid ${scoredToday?"#00e676":clr}60`,objectFit:"cover",background:"#0a1020",flexShrink:0}}
                            onError={e=>{e.target.style.display="none";}}/>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                              <span style={{fontSize:16,fontWeight:900,color:"#e8f0ff"}}>{pick.name}</span>
                              <span style={{fontFamily:"monospace",fontSize:10,color:"#7090c0"}}>{pick.team}</span>
                              <span style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:clr}}>Score: {pick.score}</span>
                              {scoredToday&&<span style={{fontSize:11,fontWeight:800,color:"#00e676",background:"#0a2010",border:"1px solid #00e67650",borderRadius:4,padding:"1px 8px"}}>🚨 SCORED!</span>}
                            </div>
                            <div style={{fontSize:10,color:"#5a6080",marginTop:3}}>vs {pick.opp} · {pick.gameTime} · #{pick.rank} ranked</div>
                            <div style={{fontSize:10,color:"#4a5060",marginTop:2}}>{pick.reason}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{fontSize:10,color:"#2a1a40",textAlign:"center",marginTop:8}}>Records auto-settle at 3am ET · For entertainment only</div>
              </div>
            );
          })()}
        </div>
      )}
    </>
  );
}

export default function App(){
  return(
    <BrowserRouter>
      <AuthProvider>
        <AppInner/>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AccountSection({user, userProfile, isPro, logout}){
  const [upgrading, setUpgrading] = useState(false);
  if (!user) return null;
  const pro = isPro();

  const startCheckout = async (plan) => {
    if (!user) return;
    setUpgrading(true);
    try {
      const res = await fetch('/api/stripe?action=checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan,
          userId: user.uid,
          email: user.email,
          stripeCustomerId: userProfile?.stripeCustomerId || null,
        }),
      });
      const data = await res.json();
      if (data.url) {
        // Save customerId to Firestore before redirecting
        if (data.customerId && !userProfile?.stripeCustomerId) {
          const { doc: firestoreDoc, setDoc: firestoreSetDoc } = await import('firebase/firestore');
          const { db: firestoreDb } = await import('./firebase');
          await firestoreSetDoc(firestoreDoc(firestoreDb, 'users', user.uid), { stripeCustomerId: data.customerId }, { merge: true }).catch(() => {});
        }
        window.location.href = data.url;
      } else if (data.error === 'already_subscribed') {
        alert('You already have an active Pro subscription. Manage it below.');
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch(e) {
      alert('Something went wrong. Please try again.');
    }
    setUpgrading(false);
  };
  return(
    <div style={{padding:"20px 16px",maxWidth:480,margin:"0 auto"}}>
      {/* Profile card */}
      <div style={{background:"#0c0f1a",border:"1px solid #182030",borderRadius:12,padding:"20px",marginBottom:16,display:"flex",alignItems:"center",gap:16}}>
        <img src={user?.photoURL} alt="avatar" style={{width:56,height:56,borderRadius:"50%",border:`3px solid ${pro?"#00e676":"#182030"}`}} onError={e=>e.target.style.display="none"}/>
        <div style={{flex:1}}>
          <div style={{fontSize:18,fontWeight:900,color:"#e8f0ff"}}>{user?.displayName}</div>
          <div style={{fontSize:11,color:"#3a5070",marginTop:2}}>{user?.email}</div>
          <div style={{marginTop:6}}>
            {pro?(
              <span style={{fontSize:11,fontWeight:800,color:"#00e676",background:"#001a00",border:"1px solid #00e67640",borderRadius:4,padding:"3px 10px",letterSpacing:1}}>✓ PRO MEMBER</span>
            ):(
              <span style={{fontSize:11,color:"#3a5070",background:"#0a1020",border:"1px solid #182030",borderRadius:4,padding:"3px 10px",letterSpacing:1}}>FREE PLAN</span>
            )}
          </div>
        </div>
      </div>

      {/* Plan details */}
      {pro?(
        <div style={{background:"#0a1e0e",border:"1px solid #00e67630",borderRadius:10,padding:"16px",marginBottom:16}}>
          <div style={{fontSize:14,fontWeight:800,color:"#00e676",letterSpacing:1,marginBottom:10}}>PRO PLAN — ACTIVE</div>
          <div style={{fontSize:12,color:"#4a7060",lineHeight:1.8}}>
            ✓ All 5 analyst cards unlocked<br/>
            ✓ Full rankings (all players)<br/>
            ✓ Full HR + goal feeds<br/>
            ✓ Full MMA analytics + AI picks<br/>
            ✓ NBA stat tracker<br/>
            ✓ All future features
          </div>
          {userProfile?.subscriptionEnd&&(
            <div style={{fontSize:10,color:"#2a4a30",marginTop:10,fontFamily:"monospace"}}>
              Renews: {new Date(userProfile.subscriptionEnd?.toDate?userProfile.subscriptionEnd.toDate():userProfile.subscriptionEnd).toLocaleDateString()}
            </div>
          )}
          <button onClick={async()=>{
              if(!userProfile?.stripeCustomerId){alert('No subscription found.');return;}
              try{
                const res=await fetch('/api/stripe?action=portal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({stripeCustomerId:userProfile.stripeCustomerId})});
                const data=await res.json();
                if(data.url) window.location.href=data.url;
                else alert('Could not open billing portal. Please try again.');
              }catch(e){alert('Could not open billing portal. Please try again.');}
            }} style={{marginTop:12,width:"100%",padding:"10px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:1,background:"#0c1a10",border:"1px solid #00e67640",borderRadius:6,cursor:"pointer",color:"#00e676"}}>
            MANAGE SUBSCRIPTION →
          </button>
        </div>
      ):(
        <div style={{background:"#0e0818",border:"1px solid #c060ff40",borderRadius:10,padding:"16px",marginBottom:16}}>
          <div style={{fontSize:14,fontWeight:800,color:"#c060ff",letterSpacing:1,marginBottom:6}}>UPGRADE TO PRO</div>
          <div style={{fontSize:12,color:"#5a4070",lineHeight:1.8,marginBottom:12}}>
            Unlock all 5 analyst cards, full rankings,<br/>
            complete MMA analytics, and every future feature.
          </div>
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            <div style={{flex:1,background:"#0a0510",border:"1px solid #2a1040",borderRadius:8,padding:"12px",textAlign:"center"}}>
              <div style={{fontSize:20,fontWeight:900,color:"#fff"}}>$10</div>
              <div style={{fontSize:10,color:"#5a4070"}}>per month</div>
            </div>
            <div style={{flex:1,background:"#0a0510",border:"1px solid #c060ff60",borderRadius:8,padding:"12px",textAlign:"center",position:"relative"}}>
              <div style={{position:"absolute",top:-8,left:"50%",transform:"translateX(-50%)",fontSize:9,fontWeight:800,color:"#c060ff",background:"#12051a",border:"1px solid #c060ff40",borderRadius:3,padding:"2px 6px",letterSpacing:1,whiteSpace:"nowrap"}}>BEST VALUE</div>
              <div style={{fontSize:20,fontWeight:900,color:"#fff"}}>$100</div>
              <div style={{fontSize:10,color:"#5a4070"}}>per year</div>
              <div style={{fontSize:9,color:"#c060ff",marginTop:2}}>Save $20</div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginBottom:0}}>
            <button onClick={()=>startCheckout('monthly')} disabled={upgrading} style={{flex:1,padding:"12px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:2,background:"#0a0510",border:"1px solid #c060ff",borderRadius:6,cursor:"pointer",color:"#c060ff"}}>
              {upgrading?'...':'MONTHLY →'}
            </button>
            <button onClick={()=>startCheckout('yearly')} disabled={upgrading} style={{flex:1,padding:"12px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:2,background:"#c060ff",border:"none",borderRadius:6,cursor:"pointer",color:"#fff"}}>
              {upgrading?'...':'YEARLY → BEST VALUE'}
            </button>
          </div>
        </div>
      )}

      {/* Sign out */}
      <button onClick={logout} style={{width:"100%",padding:"10px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:1,background:"#0c1322",border:"1px solid #182030",borderRadius:6,cursor:"pointer",color:"#3a5070"}}>
        SIGN OUT
      </button>
    </div>
  );
}

const ESPN_NBA="https://site.api.espn.com/apis/site/v2/sports/basketball/nba";
const ESPN_NBA_CORE="https://sports.core.api.espn.com/v2/sports/basketball/nba";

function NBASection({selDate=getDateStr()}){
  const NBA_API="/api/nba";
  const { isPro, user, signInWithGoogle } = useAuth();
  const nbaIsPro = isPro();
  const[games,setGames]=useState([]);
  const[loading,setLoading]=useState(true);
  const[err,setErr]=useState(null);
  const[sel,setSel]=useState(null);
  const[boxScore,setBoxScore]=useState(null);
  const[loadingBox,setLoadingBox]=useState(false);
  const[liveScores,setLiveScores]=useState({});
  const[view,setView]=useState("top");
  const[topTargets,setTopTargets]=useState([]);
  const[loadingTop,setLoadingTop]=useState(false);
  const[topLoaded,setTopLoaded]=useState(false);
  const[statLines,setStatLines]=useState([]);
  const[topScorersToday,setTopScorersToday]=useState([]);
  const[statLineTab,setStatLineTab]=useState("top");
  const[nbaSort,setNbaSort]=useState({col:"ptsScore",dir:"desc"});
  const[nbaDef,setNbaDef]=useState({}); // teamId → {ptsAllowed, rank}
  const[leagueLeaders,setLeagueLeaders]=useState(null); // {pts,reb,ast}

  const fmtTime=dt=>{try{return new Date(dt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",timeZone:"America/New_York"});}catch{return "";}};

  // jFetch wrapper for NBA proxy
  // ESPN URLs go through jFetch (already handles site.api.espn.com → /api/espn)
  const nbaFetch=async(url)=>jFetch(url);
  // nbaapi.com goes through /api/nba proxy
  const nbaApiFetch=async(endpoint,params={})=>{
    const qs=new URLSearchParams({base:"nbaapi",path:endpoint,...params}).toString();
    const r=await fetch(`${NBA_API}?${qs}`);
    if(!r.ok) throw new Error(r.status);
    return r.json();
  };

  // Load schedule
  useEffect(()=>{
    setGames([]);setSel(null);setBoxScore(null);setLiveScores({});
    setTopLoaded(false);setTopTargets([]);setStatLines([]);setLoading(true);
    const dateStr=selDate.replace(/-/g,"");
    feedFetch(`/api/feed?sport=nba&date=${selDate}`)
      .then(d=>{
        const nba=d?.nba||{};
        if(nba.games?.length) setGames(nba.games);
        if(nba.scores&&Object.keys(nba.scores).length) setLiveScores(nba.scores);
        setLoading(false);
        checkStatLines(true);
      })
      .catch(e=>{setErr("NBA schedule unavailable");setLoading(false);});
  },[selDate]);

  const nbaGamesRef=useRef([]);
  useEffect(()=>{nbaGamesRef.current=games;},[games]);

  // Load league leaders when no games today
  useEffect(()=>{
    if(loading||games.length>0||leagueLeaders) return;
    const load=async()=>{
      try{
        const cats=[
          {key:"scoring",  label:"🔥 SCORING",  sort:"points.pointsPerGame%3Adesc",    valKey:"points",    dispFn:v=>v+" PTS"},
          {key:"assists",  label:"🎯 ASSISTS",   sort:"assists.assistsPerGame%3Adesc",  valKey:"assists",   dispFn:v=>v+" AST"},
          {key:"rebounds", label:"💪 REBOUNDS",  sort:"rebounds.avgRebounds%3Adesc",    valKey:"rebounds",  dispFn:v=>v+" REB"},
        ];
        const results={};
        await Promise.all(cats.map(async({key,sort})=>{
          try{
            const url=`https://site.web.api.espn.com/apis/common/v3/sports/basketball/nba/statistics/byathlete?region=us&lang=en&contentorigin=espn&isqualified=true&page=1&limit=10&category=${key}&sort=${sort}&season=2026&seasontype=2`;
            const d=await nbaFetch(url);
            const categories=d?.categories||[];
            const athletes=d?.athletes||[];
            // Find the primary stat index from categories array
            const primaryIdx=categories.findIndex(c=>c.name===key||c.displayName?.toLowerCase().includes(key.slice(0,3)));
            results[key]=athletes.map(a=>{
              const stats=a.statistics?.splits?.categories?.[0]?.stats||a.statistics?.[0]?.stats||a.stats||[];
              const val=primaryIdx>=0&&stats[primaryIdx]!=null?parseFloat(stats[primaryIdx]).toFixed(1):a.value?.toFixed(1)||"?";
              return{
                id:a.athlete?.id||null,
                name:a.athlete?.displayName||"?",
                shortName:a.athlete?.shortName||a.athlete?.displayName||"?",
                team:a.team?.abbreviation||"?",
                displayValue:val,
              };
            }).filter(p=>p.name!=="?");
          }catch(e){}
        }));
        if(results.scoring?.length) setLeagueLeaders(results);
      }catch(e){}
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[loading,games.length]);

  // Fetch team defensive rankings — once when games first load
  useEffect(()=>{
    if(!games.length) return;
    const fetchDef=async()=>{
      try{
        const d=await nbaFetch(`${ESPN_NBA}/teams?limit=30`);
        const teams=d?.sports?.[0]?.leagues?.[0]?.teams||[];
        const defMap={};
        await Promise.all(teams.slice(0,30).map(async t=>{
          const abbrev=t.team?.abbreviation;
          const id=t.team?.id;
          if(!abbrev||!id) return;
          try{
            const s=await nbaFetch(`${ESPN_CORE}/seasons/2026/types/2/teams/${id}/statistics`);
            const cats=s?.splits?.categories||[];
            const allStats=cats.flatMap(c=>c.stats||[]);
            const get=n=>parseFloat((allStats.find(s=>s.name===n||s.abbreviation===n)||{}).value||0)||0;
            const pa=get("avgPointsAllowed")||get("opponentAvgPoints")||0;
            if(pa>0) defMap[abbrev]={ptsAllowed:pa,id};
          }catch{}
        }));
        const sorted=Object.entries(defMap).sort((a,b)=>a[1].ptsAllowed-b[1].ptsAllowed);
        sorted.forEach(([abb],i)=>{defMap[abb].rank=i+1;});
        setNbaDef(defMap);
      }catch{}
    };
    fetchDef();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selDate]);

  // Poll live scores every 60s
  useEffect(()=>{
    const poll=async()=>{
      if(document.hidden) return;
      if(!nbaGamesRef.current.length) return;
      try{
        const d=await feedFetch(`/api/feed?sport=nba&date=${selDate}`);
        if(d?.nba?.scores&&Object.keys(d.nba.scores).length){
          setLiveScores(prev=>({...prev,...d.nba.scores}));
        }
        checkStatLines();
      }catch{}
    };
    if(nbaGamesRef.current.length>0){poll();checkStatLines(true);}
    const interval=setInterval(poll,60000);
    return()=>clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selDate]);

  // Auto-load top targets when games ready
  useEffect(()=>{
    if(games.length>0&&!topLoaded&&!loadingTop) loadTopTargets();
  },[games.length]);

  // Score a player 0-100
  const scPlayer=(p)=>{
    if(!p) return null;
    const pts=p.pts||0,reb=p.reb||0,ast=p.ast||0;
    const usg=p.usg||0,l10pts=p.l10pts||0,l10reb=p.l10reb||0,l10ast=p.l10ast||0;
    const oppRank=p.oppRank||15,oppPA=p.oppPA||112;
    const gp=Math.max(1,p.gp||1);
    // Trend: recent vs season
    const ptsTrend=l10pts>0?l10pts-pts:0;
    const rebTrend=l10reb>0?l10reb-reb:0;
    const astTrend=l10ast>0?l10ast-ast:0;

    // 1. Season PTS/game (22pts)
    const ptsSc=Math.min(22,pts*0.55);
    // 2. Last 10 form — pts (18pts)
    const l10Sc=l10pts>0?Math.min(18,(l10pts/Math.max(1,pts))*18):9;
    // 3. Usage rate (15pts) — more usage = more opportunity
    const usgSc=Math.min(15,Math.max(0,(usg-15)/20*15));
    // 4. Opponent defense (20pts) — rank 30=worst defense=easiest scoring
    const defSc=Math.min(20,Math.max(0,(oppRank-1)/29*20));
    // 5. REB + AST contribution (10pts)
    const bonusSc=Math.min(10,(reb*0.5+ast*0.6));
    // 6. Recent trend (10pts) — improving = boost
    const trendSc=Math.min(10,Math.max(-5,(ptsTrend*0.5+rebTrend*0.3+astTrend*0.3)));
    // 7. Double-double potential (5pts)
    const ddSc=(pts>=15&&reb>=7)?4:(pts>=15&&ast>=7)?4:(reb>=8&&ast>=7)?3:0;

    const total=Math.min(100,Math.max(0,Math.round(ptsSc+l10Sc+usgSc+defSc+bonusSc+trendSc+ddSc)));
    return{total,pts,reb,ast,usg:usg.toFixed(1),l10pts,l10reb,l10ast,oppRank,oppPA,ptsTrend:ptsTrend.toFixed(1),gp};
  };

  // Stat lines come from feed — no per-user box score fetching
  const lastTDCheckRef=useRef(0);
  const checkStatLines=useCallback(async(force=false)=>{
    const now=Date.now();
    if(!force&&now-lastTDCheckRef.current<120000) return;
    lastTDCheckRef.current=now;
    // Try feed first
    try{
      const d=await feedFetch(`/api/feed?sport=nba&date=${selDate}`);
      if(d?.nba?.statLines?.length){
        setStatLines(d.nba.statLines);
        if(d?.nba?.topScorers?.length) setTopScorersToday(d.nba.topScorers);
        return;
      }
    }catch{}
    // Feed empty — fall back to direct box score fetch
    const activeGames=games.filter(g=>g.competitions?.[0]?.status?.type?.state==="in"||g.competitions?.[0]?.status?.type?.state==="post");
    if(!activeGames.length) return;
    const tds=[];
    await Promise.all(activeGames.map(async g=>{
      try{
        const d=await nbaFetch(`${ESPN_NBA}/summary?event=${g.id}`);
        const comp=g.competitions?.[0];
        const away=comp?.competitors?.find(c=>c.homeAway==="away")?.team?.abbreviation||"?";
        const home=comp?.competitors?.find(c=>c.homeAway==="home")?.team?.abbreviation||"?";
        const isFinal=comp?.status?.type?.state==="post";
        (d?.boxscore?.players||[]).forEach(team=>{
          const teamAbb=team.team?.abbreviation||"?";
          const group=(team.statistics||[])[0];
          if(!group) return;
          const headers=group.labels||[];
          const ptsIdx=headers.indexOf("PTS");
          const rebIdx=headers.indexOf("REB");
          const astIdx=headers.indexOf("AST");
          if(ptsIdx<0||rebIdx<0||astIdx<0) return;
          (group.athletes||[]).forEach(a=>{
            const stats=a.stats||[];
            const pts=parseInt(stats[ptsIdx])||0;
            const reb=parseInt(stats[rebIdx])||0;
            const ast=parseInt(stats[astIdx])||0;
            if(pts===0&&reb===0&&ast===0) return;
            const base={id:a.athlete?.id,name:a.athlete?.displayName||"?",shortName:a.athlete?.shortName||"?",team:teamAbb,pts,reb,ast,gameStr:`${away} @ ${home}`,isFinal};
            if(pts>=10&&reb>=10&&ast>=10) tds.push({...base,type:"td"});
            else if((pts>=10&&reb>=10)||(pts>=10&&ast>=10)||(reb>=10&&ast>=10)) tds.push({...base,type:"dd"});
            // 30+ point standalone tab removed — those players are already in
            // topScorersToday (sorted by points), so a separate tab was redundant.
          });
        });
      }catch{}
    }));
    if(tds.length>0) setStatLines(prev=>{
      const merged=[...prev];
      tds.forEach(td=>{
        const existing=merged.findIndex(p=>String(p.id)===String(td.id)&&p.type===td.type);
        if(existing>=0) merged[existing]={...merged[existing],...td};
        else merged.push(td);
      });
      return merged;
    });
  },[games,selDate]);

  const loadTopTargets=async()=>{
    if(topLoaded){setView("top");return;}
    setView("top");setLoadingTop(true);setTopTargets([]);
    // Try feed first — rankings pre-computed by server cron
    try{
      const d=await feedFetch(`/api/feed?sport=nba&date=${selDate}`);
      if(d?.nba?.rankings?.length){
        setTopTargets(d.nba.rankings);
        setTopLoaded(true);
        setLoadingTop(false);
        return;
      }
    }catch(e){}
    // Feed not ready — retry in 30s
    setTopTargets([]);
    setLoadingTop(false);
    setTopLoaded(false);
    setTimeout(()=>{ if(!topLoaded) loadTopTargets(); }, 30000);
    return;
    // eslint-disable-next-line no-unreachable
    const all=[];
    const currentYear=2025; // NBA 2024-25 season

    // Fetch season stats from nbaapi.com — all players sorted by pts
    let seasonMap={};
    try{
      const d=await nbaApiFetch("playertotals",{season:String(currentYear),pageSize:"500",sortBy:"points"});
      (d?.data||[]).forEach(p=>{
        const gp=Math.max(1,p.games||1);
        seasonMap[p.playerName?.toLowerCase()]={
          pts:p.points/gp,reb:p.totalRb/gp,ast:p.assists/gp,
          stl:p.steals/gp,blk:p.blocks/gp,
          usg:p.usgPercent||0,gp,team:p.team,
        };
      });
    }catch(e){console.warn("nbaapi season stats failed",e);}

    // Advanced stats for USG%
    try{
      const d=await nbaApiFetch("playeradvancedstats",{season:String(currentYear),pageSize:"500"});
      (d?.data||[]).forEach(p=>{
        const key=p.playerName?.toLowerCase();
        if(seasonMap[key]) seasonMap[key].usg=p.usagePercent||p.usagePct||p.usg||seasonMap[key].usg||0;
      });
    }catch{}

    for(const g of games){
      // Yield to browser between each game fetch — keeps UI responsive on mobile
      await new Promise(r=>setTimeout(r,0));
      const comp=g.competitions?.[0];
      const away=comp?.competitors?.find(c=>c.homeAway==="away");
      const home=comp?.competitors?.find(c=>c.homeAway==="home");
      if(!away||!home) continue;
      const awayAbb=away.team?.abbreviation||"?";
      const homeAbb=home.team?.abbreviation||"?";
      const status=comp?.status?.type?.state||"pre";
      const gameSt=status==="in"?"live":status==="post"?"final":"upcoming";
      const gt=fmtTime(g.date);

      // Get rosters from ESPN box score (always has players even pre-game)
      try{
        const bs=await nbaFetch(`${ESPN_NBA}/summary?event=${g.id}`);
        const allPlayers=[];
        (bs?.boxscore?.players||[]).forEach(team=>{
          const isAway=team.team?.abbreviation===awayAbb;
          const teamAbb=isAway?awayAbb:homeAbb;
          const oppAbb=isAway?homeAbb:awayAbb;
          const group=(team.statistics||[])[0];
          if(!group) return;
          (group.athletes||[]).slice(0,10).forEach(a=>{
            if(a.athlete?.id) allPlayers.push({id:a.athlete.id,name:a.athlete.displayName||"?",shortName:a.athlete.shortName||"?",team:teamAbb,opp:oppAbb});
          });
        });

        // Use season stats only — no per-player eventlog fetches
        allPlayers.forEach(p=>{
          const nameKey=p.name?.toLowerCase();
          const ss=seasonMap[nameKey]||{};
          if(!ss.pts&&!ss.reb&&!ss.ast) return;
          const oppDef=nbaDef[p.opp]||{};
          const player={
            ...p,
            pts:ss.pts||0,reb:ss.reb||0,ast:ss.ast||0,
            stl:ss.stl||0,blk:ss.blk||0,
            usg:ss.usg||0,gp:ss.gp||1,
            l10pts:ss.pts||0,l10reb:ss.reb||0,l10ast:ss.ast||0,
            oppRank:oppDef.rank||15,
            oppPA:oppDef.ptsAllowed||112,
            gt,gameSt,gamePk:g.id,
          };
          const sc=scPlayer(player);
          if(!sc||sc.total<15) return;
          all.push({...player,score:sc.total,details:sc});
        });
        setTopTargets([...all].sort((a,b)=>b.score-a.score));
      }catch(e){console.warn("game fetch failed",g.id,e);}
    }

    const deduped=[...new Map(all.map(p=>[p.id,p])).values()];
    setTopTargets([...deduped].sort((a,b)=>b.score-a.score));
    setLoadingTop(false);setTopLoaded(true);
    // Check stat lines when top targets finishes loading
    checkStatLines();
  };

  // Load box score for selected game
  const pickGame=useCallback(async g=>{
    if(sel===g.id){setSel(null);setBoxScore(null);return;}
    setSel(g.id);setLoadingBox(true);setBoxScore(null);
    try{
      const d=await nbaFetch(`${ESPN_NBA}/summary?event=${g.id}`);
      setBoxScore(d);
    }catch(e){}
    setLoadingBox(false);
  },[sel]);

  if(loading) return <Spinner text="LOADING NBA SCHEDULE..."/>;
  if(err) return <ErrMsg msg={err}/>;

  const thS={fontSize:9,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"6px 8px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap"};
  const orange="#ff6b35";

  const renderBoxScore=()=>{
    if(loadingBox) return <Spinner text="LOADING BOX SCORE..."/>;
    if(!boxScore) return null;
    const teams=boxScore?.boxscore?.players||[];
    return(
      <div style={{padding:"14px 16px 32px"}}>
        {(()=>{
            const tabs=[["top","⭐","Top Scorers"],["td","🏀 TD","Triple Doubles"],["dd","💪 DD","Double Doubles"],["tdwatch","👀","TD Watch"]];
            const filtered=statLineTab==="top"?topScorersToday:statLines.filter(p=>p.type===statLineTab);
            const tdCount=statLines.filter(p=>p.type==="td").length;
            const ddCount=statLines.filter(p=>p.type==="dd").length;
            const counts={"td":tdCount,"dd":ddCount,"top":topScorersToday.length,"tdwatch":statLines.filter(p=>p.type==="tdwatch").length};
            const colors={"td":"#00c8ff","dd":"#ffb020","top":"#00e676","tdwatch":"#c060ff"};
            return(
              <div style={{background:"#081828",border:`1px solid ${colors[statLineTab]}40`,borderRadius:8,padding:"12px 14px",marginBottom:16}}>
                <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                  {tabs.map(([key,emoji,label])=>{
                    const isA=statLineTab===key;
                    const c=colors[key];
                    return(
                      <button key={key} onClick={()=>setStatLineTab(key)} style={{padding:"10px 16px",minHeight:44,fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,letterSpacing:1,background:isA?c+"18":"#0a1020",border:`1px solid ${isA?c:"#182030"}`,borderRadius:4,cursor:"pointer",color:isA?c:"#3a5070",WebkitTapHighlightColor:"transparent",touchAction:"manipulation"}}>
                        {emoji} {label}{counts[key]>0?<span style={{marginLeft:5,fontFamily:"monospace",fontSize:10,opacity:0.8}}>({counts[key]})</span>:null}
                      </button>
                    );
                  })}
                </div>
                {filtered.length===0?(
                  <div style={{fontSize:11,color:"#3a5070",fontFamily:"monospace"}}>None yet today · Updates every 10 min</div>
                ):(
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {filtered.map((p,i)=>(
                      <div key={p.id+p.type} style={{background:"#0a1020",border:`1px solid ${colors[p.type]}30`,borderRadius:6,padding:"8px 12px",minWidth:130}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{fontSize:14,fontWeight:800,color:"#e8f0ff"}}>{p.shortName||p.name}</div>{statLineTab==="top"&&(()=>{const rank=topTargets.findIndex(t=>String(t.id)===String(p.id));return rank>=0?<span style={{fontSize:9,fontWeight:800,color:"#5080c0",background:"#1a2a4a",border:"1px solid #2a3a5a",borderRadius:2,padding:"1px 4px"}}>#{rank+1}</span>:null;})()}</div>
                        <div style={{fontFamily:"monospace",fontSize:12,color:colors[p.type],marginTop:2}}>{p.pts}PTS · {p.reb}REB · {p.ast}AST</div>
                        <div style={{fontSize:9,color:"#3a5070",marginTop:2}}>{p.team} · {p.gameStr}{p.isFinal?" · Final":""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        {teams.map((team,ti)=>{
          const group=(team.statistics||[])[0];
          if(!group) return null;
          const headers=group.labels||[];
          const players=(group.athletes||[]).filter(a=>a.starter||a.active);
          return(
            <div key={ti} style={{marginBottom:24}}>
              <div style={{fontSize:14,fontWeight:800,color:orange,letterSpacing:2,marginBottom:8,padding:"4px 0",borderBottom:`1px solid ${orange}30`}}>
                {(team.team?.displayName||team.team?.abbreviation||"Team").toUpperCase()}
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",minWidth:500}}>
                  <thead><tr>
                    <th style={{...thS,minWidth:140}}>Player</th>
                    {headers.map((h,i)=><th key={i} style={thS}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {players.map((a,i)=>{
                      const stats=a.stats||[];
                      const ptsIdx=headers.indexOf("PTS");
                      const rebIdx=headers.indexOf("REB");
                      const astIdx=headers.indexOf("AST");
                      const pts=parseInt(stats[ptsIdx])||0;
                      const reb=parseInt(stats[rebIdx])||0;
                      const ast=parseInt(stats[astIdx])||0;
                      const isTD=pts>=10&&reb>=10&&ast>=10;
                      return(
                        <tr key={i} style={{borderBottom:"1px solid #0a1020",background:isTD?"#081828":a.starter?"#090d18":"transparent"}}
                          onMouseEnter={e=>e.currentTarget.style.background="#0c1428"}
                          onMouseLeave={e=>e.currentTarget.style.background=isTD?"#081828":a.starter?"#090d18":"transparent"}>
                          <td style={{padding:"8px 8px",minWidth:140}}>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              {isTD&&<span style={{fontSize:10}}>🏀</span>}
                              {!isTD&&a.starter&&<div style={{width:4,height:4,borderRadius:"50%",background:orange,flexShrink:0}}/>}
                              <div>
                                <div style={{fontSize:13,fontWeight:700,color:isTD?"#00c8ff":"#e8f0ff"}}>{a.athlete?.shortName||a.athlete?.displayName||"?"}</div>
                                <div style={{fontSize:9,color:"#3a5070"}}>{a.position?.abbreviation||""}{a.starter?" · STARTER":""}</div>
                              </div>
                            </div>
                          </td>
                          {stats.map((s,j)=>{
                            const h=headers[j];
                            const val=parseInt(s)||0;
                            const highlight=(h==="PTS"&&val>=20)?orange:(h==="REB"&&val>=10)?orange:(h==="AST"&&val>=10)?orange:(h==="PTS"&&val>=10)?orange+"99":null;
                            return(
                              <td key={j} style={{padding:"8px 8px",whiteSpace:"nowrap"}}>
                                <span style={{fontFamily:"monospace",fontSize:12,fontWeight:highlight?700:400,color:highlight||"#7090c0"}}>{s||"—"}</span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        {boxScore?.leaders?.length>0&&(
          <div style={{marginTop:8,padding:"12px 14px",background:"#090d18",border:"1px solid #182030",borderRadius:8}}>
            <div style={{fontSize:11,fontWeight:800,color:orange,letterSpacing:2,marginBottom:10}}>GAME LEADERS</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {boxScore.leaders.flatMap(team=>team.leaders||[]).map((lead,i)=>{
                const top=lead.leaders?.[0];
                if(!top) return null;
                return(
                  <div key={i} style={{background:"#0c1322",border:"1px solid #182030",borderRadius:6,padding:"8px 12px",minWidth:130}}>
                    <div style={{fontSize:9,color:"#3a5070",letterSpacing:1,marginBottom:4}}>{lead.displayName||lead.name}</div>
                    <div style={{fontSize:13,fontWeight:800,color:"#e8f0ff"}}>{top.athlete?.shortName||top.athlete?.displayName}</div>
                    <div style={{fontFamily:"monospace",fontSize:14,fontWeight:700,color:orange,marginTop:2}}>{top.displayValue}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTopTargets=()=>(
    <div style={{padding:"14px 16px 32px"}}>
      {/* Triple double tracker */}
      {(()=>{
            const tabs=[["top","⭐","Top Scorers"],["td","🏀 TD","Triple Doubles"],["dd","💪 DD","Double Doubles"],["tdwatch","👀","TD Watch"]];
            const filtered=statLineTab==="top"?topScorersToday:statLines.filter(p=>p.type===statLineTab);
            const tdCount=statLines.filter(p=>p.type==="td").length;
            const ddCount=statLines.filter(p=>p.type==="dd").length;
            const counts={"td":tdCount,"dd":ddCount,"top":topScorersToday.length,"tdwatch":statLines.filter(p=>p.type==="tdwatch").length};
            const colors={"td":"#00c8ff","dd":"#ffb020","top":"#00e676","tdwatch":"#c060ff"};
            return(
              <div style={{background:"#081828",border:`1px solid ${colors[statLineTab]}40`,borderRadius:8,padding:"12px 14px",marginBottom:16}}>
                <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                  {tabs.map(([key,emoji,label])=>{
                    const isA=statLineTab===key;
                    const c=colors[key];
                    return(
                      <button key={key} onClick={()=>setStatLineTab(key)} style={{padding:"10px 16px",minHeight:44,fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,letterSpacing:1,background:isA?c+"18":"#0a1020",border:`1px solid ${isA?c:"#182030"}`,borderRadius:4,cursor:"pointer",color:isA?c:"#3a5070",WebkitTapHighlightColor:"transparent",touchAction:"manipulation"}}>
                        {emoji} {label}{counts[key]>0?<span style={{marginLeft:5,fontFamily:"monospace",fontSize:10,opacity:0.8}}>({counts[key]})</span>:null}
                      </button>
                    );
                  })}
                </div>
                {filtered.length===0?(
                  <div style={{fontSize:11,color:"#3a5070",fontFamily:"monospace"}}>None yet today · Updates every 10 min</div>
                ):(
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {filtered.map((p,i)=>(
                      <div key={p.id+p.type} style={{background:"#0a1020",border:`1px solid ${colors[p.type]}30`,borderRadius:6,padding:"8px 12px",minWidth:130}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{fontSize:14,fontWeight:800,color:"#e8f0ff"}}>{p.shortName||p.name}</div>{statLineTab==="top"&&(()=>{const rank=topTargets.findIndex(t=>String(t.id)===String(p.id));return rank>=0?<span style={{fontSize:9,fontWeight:800,color:"#5080c0",background:"#1a2a4a",border:"1px solid #2a3a5a",borderRadius:2,padding:"1px 4px"}}>#{rank+1}</span>:null;})()}</div>
                        <div style={{fontFamily:"monospace",fontSize:12,color:colors[p.type],marginTop:2}}>{p.pts}PTS · {p.reb}REB · {p.ast}AST</div>
                        <div style={{fontSize:9,color:"#3a5070",marginTop:2}}>{p.team} · {p.gameStr}{p.isFinal?" · Final":""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
      <div style={{marginBottom:12}}>
        <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:2}}>🏀 TOP NBA TARGETS TODAY</div>
        <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>RANKED BY SCORING PROP · L5 FORM · 3PM · OPP DEFENSE · TREND</div>
      </div>
      {loadingTop&&(
        <div style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:7,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:16,height:16,border:"2px solid #1a2540",borderTopColor:orange,borderRadius:"50%",animation:"spin 0.8s linear infinite",flexShrink:0}}/>
          <div style={{fontSize:12,color:"#4a6080",letterSpacing:1}}>Fetching season stats + recent form for {games.length} games...</div>
        </div>
      )}
      {!loadingTop&&(topTargets.length===0?(
        <div>
          {games.length===0&&leagueLeaders?(
            <div>
              <div style={{marginBottom:16}}>
                <div style={{fontSize:20,fontWeight:900,color:"#fff",letterSpacing:2}}>🏀 NBA SEASON LEADERS</div>
                <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>NO GAMES TODAY · CURRENT SEASON RANKINGS</div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
                {[["scoring","🔥 SCORING"],["rebounds","💪 REBOUNDS"],["assists","🎯 ASSISTS"]].map(([cat,label])=>(
                  <div key={cat} style={{background:"#080c18",border:"1px solid #111c2e",borderRadius:8,overflow:"hidden"}}>
                    <div style={{padding:"10px 14px",background:"#0a1020",borderBottom:"1px solid #111c2e"}}>
                      <div style={{fontSize:13,fontWeight:900,color:orange,letterSpacing:2}}>{label}</div>
                    </div>
                    {(leagueLeaders[cat]||[]).map((p,i)=>(
                      <div key={p.name} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:"1px solid #0a1020",background:i<3?"#090d18":"transparent"}}>
                        <span style={{fontFamily:"monospace",fontSize:12,color:"#3a5070",width:20,textAlign:"right",flexShrink:0}}>
                          {i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}
                        </span>
                        {p.id&&<img src={`https://a.espncdn.com/i/headshots/nba/players/full/${p.id}.png`} alt={p.name}
                          style={{width:28,height:28,borderRadius:"50%",objectFit:"cover",background:"#0a1020",flexShrink:0,border:`1px solid ${orange}40`}}
                          onError={e=>{e.target.style.display="none";}}/>}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:700,color:"#e8f0ff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.shortName}</div>
                          <div style={{fontSize:10,color:"#3a5070"}}>{p.team}</div>
                        </div>
                        <div style={{fontFamily:"monospace",fontSize:16,fontWeight:900,color:i===0?orange:i<3?"#ffb020":"#5080c0",flexShrink:0}}>{p.displayValue}</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ):(
            <div style={{textAlign:"center",padding:40,color:"#243650",fontSize:13}}>No player data available</div>
          )}
        </div>
      ):(()=>{
        const nbaTab=nbaSort.col==="astScore"?"ast":nbaSort.col==="rebScore"?"reb":"pts";
        const setTab=t=>setNbaSort({col:t==="ast"?"astScore":t==="reb"?"rebScore":"ptsScore",dir:"desc"});
        const SortTh=({col,label})=>{
          const active=nbaSort.col===col;
          return <th onClick={()=>setNbaSort(s=>({col,dir:s.col===col&&s.dir==="desc"?"asc":"desc"}))} style={{...thS,cursor:"pointer",color:active?orange:"#2e4460",userSelect:"none",WebkitTapHighlightColor:"transparent",touchAction:"manipulation",padding:"10px 8px"}}>{label}{active?(nbaSort.dir==="desc"?" ▼":" ▲"):" ⇅"}</th>;
        };
        const getVal=(p,col)=>{
          if(col==="ptsScore"||col==="score") return p.details?.ptsScore||0;
          if(col==="astScore") return p.details?.astScore||0;
          if(col==="rebScore") return p.details?.rebScore||0;
          if(col==="pts") return p.pts||0;
          if(col==="reb") return p.reb||0;
          if(col==="ast") return p.ast||0;
          if(col==="threepm") return p.threepm||0;
          if(col==="l5pts") return p.details?.l5pts||0;
          if(col==="l1pts") return p.details?.l1pts??0;
          if(col==="l1ast") return p.details?.l1ast??0;
          if(col==="l1reb") return p.details?.l1reb??0;
          if(col==="l5reb") return p.details?.l5reb||0;
          if(col==="l5ast") return p.details?.l5ast||0;
          if(col==="oppRank") return p.details?.oppRank||15;
          if(col==="ptsTrend") return parseFloat(p.details?.ptsTrend)||0;
          if(col==="astTrend") return parseFloat(p.details?.astTrend)||0;
          if(col==="rebTrend") return parseFloat(p.details?.rebTrend)||0;
          return 0;
        };
        const sorted=[...topTargets].sort((a,b)=>nbaSort.dir==="desc"?getVal(b,nbaSort.col)-getVal(a,nbaSort.col):getVal(a,nbaSort.col)-getVal(b,nbaSort.col));
        const origSorted=[...topTargets].sort((a,b)=>getVal(b,nbaTab==="pts"?"ptsScore":nbaTab==="ast"?"astScore":"rebScore")-getVal(a,nbaTab==="pts"?"ptsScore":nbaTab==="ast"?"astScore":"rebScore"));
        const visibleSorted=nbaIsPro?sorted:sorted.slice(0,10);
        return(
          <div>
            {/* Tab switcher */}
            <div style={{display:"flex",gap:6,marginBottom:12}}>
              <button onClick={()=>setTab("pts")} style={{padding:"7px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:1.5,background:nbaTab==="pts"?"#1a0d05":"#0c1322",border:`1px solid ${nbaTab==="pts"?orange:"#182030"}`,borderRadius:5,cursor:"pointer",color:nbaTab==="pts"?orange:"#3a5070"}}>
                🔥 POINTS
              </button>
              <button onClick={()=>setTab("ast")} style={{padding:"7px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:1.5,background:nbaTab==="ast"?"#051a14":"#0c1322",border:`1px solid ${nbaTab==="ast"?"#00e676":"#182030"}`,borderRadius:5,cursor:"pointer",color:nbaTab==="ast"?"#00e676":"#3a5070"}}>
                🎯 ASSISTS
              </button>
              <button onClick={()=>setTab("reb")} style={{padding:"7px 18px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:1.5,background:nbaTab==="reb"?"#0a0a1f":"#0c1322",border:`1px solid ${nbaTab==="reb"?"#c060ff":"#182030"}`,borderRadius:5,cursor:"pointer",color:nbaTab==="reb"?"#c060ff":"#3a5070"}}>
                💪 REBOUNDS
              </button>
              <div style={{marginLeft:"auto",display:"flex",alignItems:"center",padding:"4px 12px",background:"#080c18",border:"1px solid #111c2e",borderRadius:5}}>
                <span style={{fontSize:10,color:"#3a5070",letterSpacing:1}}>
                  {nbaTab==="pts"?"RANKED BY: PTS/G · L5 · L1 · OPP DEF":nbaTab==="ast"?"RANKED BY: AST/G · L5 · L1 · OPP DEF":"RANKED BY: REB/G · L5 · L1 · OPP DEF"}
                </span>
              </div>
            </div>
            <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:800}}>
              <thead><tr>
                <th style={thS}>RNK</th>
                <th style={thS}>Player</th>
                <th style={thS}>vs</th>
                <SortTh col="score" label="Score"/>
                {nbaTab==="pts"?(<>
                  <SortTh col="pts" label="PTS/G"/>
                  <SortTh col="l5pts" label="L5 PTS"/>
                  <SortTh col="l1pts" label="L1 PTS"/>
                  <SortTh col="reb" label="REB"/>
                  <SortTh col="ast" label="AST"/>
                  <SortTh col="threepm" label="3PM/G"/>
                  <SortTh col="oppRank" label="OPP DEF"/>
                  <SortTh col="ptsTrend" label="TREND"/>
                </>):nbaTab==="ast"?(<>
                  <SortTh col="ast" label="AST/G"/>
                  <SortTh col="l5ast" label="L5 AST"/>
                  <SortTh col="l1ast" label="L1 AST"/>
                  <SortTh col="pts" label="PTS/G"/>
                  <SortTh col="reb" label="REB"/>
                  <SortTh col="threepm" label="3PM/G"/>
                  <SortTh col="oppRank" label="OPP DEF"/>
                  <SortTh col="astTrend" label="TREND"/>
                </>):(<>
                  <SortTh col="reb" label="REB/G"/>
                  <SortTh col="l5reb" label="L5 REB"/>
                  <SortTh col="l1reb" label="L1 REB"/>
                  <SortTh col="pts" label="PTS/G"/>
                  <SortTh col="ast" label="AST/G"/>
                  <SortTh col="oppRank" label="OPP DEF"/>
                  <SortTh col="rebTrend" label="TREND"/>
                </>)}
              </tr></thead>
              <tbody>
                {(()=>{
                  // Build results map from statlines + topScorers for badge display
                  const playerResults = {};
                  [...statLines, ...topScorersToday].forEach(r => {
                    if (!r.id) return;
                    const existing = playerResults[r.id];
                    if (!existing || r.pts > (existing.pts||0)) {
                      playerResults[r.id] = r;
                    }
                  });
                  const getBadges = (pid) => {
                    const r = playerResults[pid];
                    if (!r) return null;
                    const {pts=0, reb=0, ast=0} = r;
                    const ptsBadge = pts>=70?"70 PTS":pts>=60?"60 PTS":pts>=50?"50 PTS":pts>=40?"40 PTS":pts>=30?"30 PTS":pts>=20?"20 PTS":null;
                    const isTD = pts>=10&&reb>=10&&ast>=10;
                    const isDD = !isTD&&((pts>=10&&reb>=10)||(pts>=10&&ast>=10)||(reb>=10&&ast>=10));
                    const statBadge = isTD?"TD":isDD?"DD":null;
                    return {ptsBadge, statBadge};
                  };
                  return visibleSorted.map((p,i)=>{
                  const origRank=origSorted.findIndex(t=>t.id===p.id)+1;
                  const d=p.details||{};
                  const tabScore=nbaTab==="pts"?(d.ptsScore||0):nbaTab==="ast"?(d.astScore||0):(d.rebScore||0);
                  const clr=tabScore>=70?"#00e676":tabScore>=50?orange:"#4a6080";
                  const medal=origRank===1?"🥇":origRank===2?"🥈":origRank===3?"🥉":null;
                  const trendC=nbaTab==="pts"?(parseFloat(d.ptsTrend)>2?"#00e676":parseFloat(d.ptsTrend)<-2?"#ff4060":"#7090c0"):(parseFloat(d.astTrend)>1?"#00e676":parseFloat(d.astTrend)<-1?"#ff4060":"#7090c0");
                  const defC=d.oppRank>=25?"#ff4060":d.oppRank>=20?"#ffb020":"#4a6080";
                  const badges=getBadges(p.id);
                  return(
                    <tr key={p.id} style={{borderBottom:"1px solid #0a1020",background:origRank<3?"#0a0f1e":origRank<10?"#090d18":"transparent"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#0c1428"}
                      onMouseLeave={e=>e.currentTarget.style.background=origRank<3?"#0a0f1e":origRank<10?"#090d18":"transparent"}>
                      <td style={{padding:"9px 8px",width:40}}>{medal?<span style={{fontSize:16}}>{medal}</span>:<span style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#5080c0"}}>#{origRank}</span>}</td>
                      <td style={{padding:"9px 8px",minWidth:160}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          {origRank<=10&&<img src={`https://a.espncdn.com/i/headshots/nba/players/full/${p.id}.png`} alt={p.name}
                            style={{width:origRank<3?40:32,height:origRank<3?40:32,borderRadius:"50%",border:`2px solid ${clr}60`,objectFit:"cover",background:"#0a1020",flexShrink:0}}
                            onError={e=>{e.target.style.display="none";}}/>}
                          <div>
                            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                              <span style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{p.shortName||p.name}</span>
                              {p.gameSt==="live"&&<span style={{fontSize:8,color:"#00e676",background:"#00e67615",border:"1px solid #00e67640",borderRadius:2,padding:"1px 4px",fontWeight:700}}>🔴 LIVE</span>}
                              {p.gameSt==="final"&&<span style={{fontSize:8,color:"#4a6080",background:"#1a2030",border:"1px solid #2a3040",borderRadius:2,padding:"1px 4px"}}>FINAL</span>}
                              {badges?.ptsBadge&&<span style={{fontSize:8,fontWeight:800,color:"#ff6b35",background:"#ff6b3518",border:"1px solid #ff6b3550",borderRadius:2,padding:"1px 5px",letterSpacing:0.5}}>{badges.ptsBadge}</span>}
                              {badges?.statBadge&&<span style={{fontSize:8,fontWeight:800,color:badges.statBadge==="TD"?"#00c8ff":"#ffb020",background:badges.statBadge==="TD"?"#00c8ff15":"#ffb02015",border:`1px solid ${badges.statBadge==="TD"?"#00c8ff50":"#ffb02050"}`,borderRadius:2,padding:"1px 5px",letterSpacing:0.5}}>{badges.statBadge}</span>}
                            </div>
                            <div style={{fontSize:10,color:"#3a5070",marginTop:1}}>{p.team} · {p.gt}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{padding:"9px 8px",width:44}}><div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:"#5070a0"}}>vs {p.opp}</div></td>
                      <td style={{padding:"9px 8px",width:54}}><div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:46,height:32,borderRadius:5,background:clr+"20",border:`1px solid ${clr}60`,fontFamily:"monospace",fontSize:17,fontWeight:700,color:clr}}>{tabScore}</div></td>
                      {nbaTab==="pts"?(<>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:14,fontWeight:900,color:p.pts>=25?"#00e676":p.pts>=18?orange:"#4a6080"}}>{p.pts?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:d.l5pts>=25?"#00e676":d.l5pts>=18?orange:"#4a6080"}}>{d.l5pts?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:d.l1pts>=25?"#00e676":d.l1pts>=15?orange:d.l1pts>=10?"#5080c0":"#3a5070"}}>{d.l1pts!=null?d.l1pts:"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:p.reb>=8?"#00e676":p.reb>=5?orange:"#5070a0"}}>{p.reb?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:p.ast>=7?"#00e676":p.ast>=4?orange:"#5070a0"}}>{p.ast?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:p.threepm>=3?"#00e676":p.threepm>=1.5?orange:"#4a6080"}}>{p.threepm?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}>
                          <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:defC}}>#{d.oppRank||"?"}/30</div>
                          <div style={{fontSize:9,color:"#344560",marginTop:1}}>{d.oppPA?`${d.oppPA.toFixed(1)} PA/G`:""}</div>
                        </td>
                        <td style={{padding:"9px 10px"}}>
                          <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:trendC}}>{parseFloat(d.ptsTrend)>0?"+":""}{d.ptsTrend||"—"}</div>
                          <div style={{fontSize:9,color:"#344560",marginTop:1}}>L5 vs SSN</div>
                        </td>
                      </>):nbaTab==="ast"?(<>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:14,fontWeight:900,color:p.ast>=8?"#00e676":p.ast>=5?orange:"#4a6080"}}>{p.ast?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:d.l5ast>=8?"#00e676":d.l5ast>=5?orange:"#4a6080"}}>{d.l5ast?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:d.l1ast>=10?"#00e676":d.l1ast>=7?orange:d.l1ast>=4?"#5080c0":"#3a5070"}}>{d.l1ast!=null?d.l1ast:"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:p.pts>=25?"#00e676":p.pts>=18?orange:"#5070a0"}}>{p.pts?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:p.reb>=8?"#00e676":p.reb>=5?orange:"#5070a0"}}>{p.reb?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:p.threepm>=3?"#00e676":p.threepm>=1.5?orange:"#4a6080"}}>{p.threepm?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}>
                          <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:defC}}>#{d.oppRank||"?"}/30</div>
                          <div style={{fontSize:9,color:"#344560",marginTop:1}}>{d.oppPA?`${d.oppPA.toFixed(1)} PA/G`:""}</div>
                        </td>
                        <td style={{padding:"9px 10px"}}>
                          <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:trendC}}>{parseFloat(d.astTrend)>0?"+":""}{d.astTrend||"—"}</div>
                          <div style={{fontSize:9,color:"#344560",marginTop:1}}>L5 vs SSN</div>
                        </td>
                      </>):(<>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:14,fontWeight:900,color:p.reb>=12?"#00e676":p.reb>=8?"#c060ff":"#4a6080"}}>{p.reb?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:d.l5reb>=12?"#00e676":d.l5reb>=8?"#c060ff":"#4a6080"}}>{d.l5reb?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,fontWeight:700,color:d.l1reb>=15?"#00e676":d.l1reb>=10?"#c060ff":d.l1reb>=6?"#5080c0":"#3a5070"}}>{d.l1reb!=null?d.l1reb:"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:p.pts>=25?"#00e676":p.pts>=18?orange:"#5070a0"}}>{p.pts?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}><div style={{fontFamily:"monospace",fontSize:12,color:p.ast>=7?"#00e676":p.ast>=4?orange:"#5070a0"}}>{p.ast?.toFixed(1)||"—"}</div></td>
                        <td style={{padding:"9px 10px"}}>
                          <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:defC}}>#{d.oppRank||"?"}/30</div>
                          <div style={{fontSize:9,color:"#344560",marginTop:1}}>{d.oppPA?`${d.oppPA.toFixed(1)} PA/G`:""}</div>
                        </td>
                        <td style={{padding:"9px 10px"}}>
                          <div style={{fontFamily:"monospace",fontSize:11,fontWeight:700,color:trendC}}>{parseFloat(d.rebTrend)>0?"+":""}{d.rebTrend||"—"}</div>
                          <div style={{fontSize:9,color:"#344560",marginTop:1}}>L5 vs SSN</div>
                        </td>
                      </>)}
                    </tr>
                  );
                  });
                })()}
              </tbody>
            </table>
            </div>
            {!nbaIsPro&&(
              <div onClick={()=>user?window.location.href='/account':signInWithGoogle()}
                style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 20px",marginTop:2,background:"#0e0818",border:"1px solid #c060ff40",borderRadius:6,cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:18}}>🔒</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:800,color:"#c8d0e8"}}>Full rankings hidden</div>
                    <div style={{fontSize:11,color:"#7040a0",marginTop:2}}>Pro unlocks every player · all sports</div>
                  </div>
                </div>
                <div style={{flexShrink:0,padding:"9px 22px",background:"linear-gradient(135deg,#7020c0,#c060ff)",borderRadius:5,fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:900,color:"#fff",letterSpacing:1.5,boxShadow:"0 0 14px #c060ff40"}}>
                  {user?"UPGRADE TO PRO →":"SIGN IN TO UNLOCK →"}
                </div>
              </div>
            )}
          </div>
        );
      })())}
    </div>
  );

  return(
    <>
      {/* Game rail */}
      <div style={{display:"flex",gap:6,padding:"8px 10px",overflowX:"auto",background:"#07090e",borderBottom:"1px solid #0f1826",alignItems:"center",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        <button onClick={()=>view==="top"?setView("games"):loadTopTargets()} style={{flexShrink:0,padding:"8px 16px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:800,letterSpacing:1.5,background:view==="top"?"#1a0d05":"#0c1322",border:`1px solid ${view==="top"?orange:"#182030"}`,borderRadius:5,cursor:"pointer",color:view==="top"?orange:"#3a5070"}}>
          {loadingTop?"⏳":"🏀"} TOP TARGETS
        </button>
        <div style={{width:1,height:36,background:"#182030",flexShrink:0}}/>
        {games.length===0
          ?<div style={{color:"#3a5070",fontSize:13}}>No NBA games today</div>
          :[...games].sort((a,b)=>{const o=g=>{const s=g.competitions?.[0]?.status?.type?.state||"pre";return s==="in"?0:s==="pre"?1:2;};return o(a)-o(b);}).map(g=>{
            const comp=g.competitions?.[0];
            const away=comp?.competitors?.find(c=>c.homeAway==="away");
            const home=comp?.competitors?.find(c=>c.homeAway==="home");
            const sc=liveScores[g.id];
            const status=sc?.status||comp?.status?.type?.state||"pre";
            const live=status==="in";
            const final=status==="post";
            const isA=sel===g.id&&view==="games";
            return(
              <div key={g.id} onClick={()=>{setView("games");pickGame(g);}} style={{flexShrink:0,background:isA?"#1a0d05":"#0d1322",border:`1px solid ${isA?orange:live?"#ff6b3540":"#182030"}`,borderRadius:6,padding:"9px 14px",cursor:"pointer",minWidth:148,transition:"all 0.12s"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:15,fontWeight:800,color:"#fff"}}>{away?.team?.abbreviation||"?"}</div>
                  {(live||final)&&<div style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:live?orange:"#c8d6e8"}}>{sc?.away||away?.score||0}</div>}
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:2}}>
                  <div style={{fontSize:15,fontWeight:800,color:"#fff"}}>{home?.team?.abbreviation||"?"}</div>
                  {(live||final)&&<div style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:live?orange:"#c8d6e8"}}>{sc?.home||home?.score||0}</div>}
                </div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4}}>
                  {!live&&!final&&<div style={{fontFamily:"monospace",fontSize:10,color:"#3a5070"}}>{fmtTime(g.date)} ET</div>}
                  {live&&sc?.detail&&<div style={{fontFamily:"monospace",fontSize:9,color:orange,opacity:0.9}}>{sc.detail}</div>}
                  {final&&<div style={{fontFamily:"monospace",fontSize:9,color:"#4060a0"}}>Final</div>}
                  <div style={{fontSize:9,padding:"2px 6px",borderRadius:3,fontWeight:700,background:live?"#1a0800":"#1a2a4a",color:live?orange:"#4a72b0",border:`1px solid ${live?"#ff6b3530":"#1e3060"}`}}>{live?"● LIVE":final?"✓":fmtTime(g.date)}</div>
                </div>
              </div>
            );
          })
        }
      </div>

      {view==="top"&&renderTopTargets()}
      {view==="games"&&sel&&renderBoxScore()}
      {view==="games"&&!sel&&(
        <div>
          {(()=>{
          const tabs=[["top","⭐","Top Scorers"],["td","🏀 TD","Triple Doubles"],["dd","💪 DD","Double Doubles"],["tdwatch","👀","TD Watch"]];
          const filtered=statLineTab==="top"?topScorersToday:statLines.filter(p=>p.type===statLineTab);
          const counts={"td":statLines.filter(p=>p.type==="td").length,"dd":statLines.filter(p=>p.type==="dd").length,"top":topScorersToday.length,"tdwatch":statLines.filter(p=>p.type==="tdwatch").length};
          const colors={"td":"#00c8ff","dd":"#ffb020","top":"#00e676","tdwatch":"#c060ff"};
          return(
            <div style={{padding:"14px 16px 0"}}>
              <div style={{background:"#081828",border:`1px solid ${colors[statLineTab]}40`,borderRadius:8,padding:"12px 14px",marginBottom:16}}>
                <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                  {tabs.map(([key,emoji,label])=>{
                    const isA=statLineTab===key;
                    const c=colors[key];
                    return(
                      <button key={key} onClick={()=>setStatLineTab(key)} style={{padding:"10px 16px",minHeight:44,fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,letterSpacing:1,background:isA?c+"18":"#0a1020",border:`1px solid ${isA?c:"#182030"}`,borderRadius:4,cursor:"pointer",color:isA?c:"#3a5070",WebkitTapHighlightColor:"transparent",touchAction:"manipulation"}}>
                        {emoji} {label}{counts[key]>0?<span style={{marginLeft:5,fontFamily:"monospace",fontSize:10,opacity:0.8}}>({counts[key]})</span>:null}
                      </button>
                    );
                  })}
                </div>
                {filtered.length===0?(
                  <div style={{fontSize:11,color:"#3a5070",fontFamily:"monospace"}}>None yet today · Updates every 2 min</div>
                ):(
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {filtered.map((p,i)=>(
                      <div key={p.id+p.type} style={{background:"#0a1020",border:`1px solid ${colors[p.type]}30`,borderRadius:6,padding:"8px 12px",minWidth:130}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}><div style={{fontSize:14,fontWeight:800,color:"#e8f0ff"}}>{p.shortName||p.name}</div>{statLineTab==="top"&&(()=>{const rank=topTargets.findIndex(t=>String(t.id)===String(p.id));return rank>=0?<span style={{fontSize:9,fontWeight:800,color:"#5080c0",background:"#1a2a4a",border:"1px solid #2a3a5a",borderRadius:2,padding:"1px 4px"}}>#{rank+1}</span>:null;})()}</div>
                        <div style={{fontFamily:"monospace",fontSize:12,color:colors[p.type],marginTop:2}}>{p.pts}PTS · {p.reb}REB · {p.ast}AST</div>
                        <div style={{fontSize:9,color:"#3a5070",marginTop:2}}>{p.team} · {p.gameStr}{p.isFinal?" · Final":""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:200,flexDirection:"column",gap:10}}>
            <div style={{fontSize:13,color:"#243650",letterSpacing:1}}>← Click a game to view box score</div>
            <div style={{fontSize:11,color:"#1a2a3a"}}>or 🏀 Top Targets for today's best picks</div>
          </div>
        </div>
      )}
    </>
  );
}





// ════════════════════════════════════════════════════════════════
// NFL SECTION
// ════════════════════════════════════════════════════════════════
function NFLSection(){
  const[loading,setLoading]=useState(true);
  const[schedule,setSchedule]=useState([]);
  const[scores,setScores]=useState({});
  const[rankings,setRankings]=useState(null);
  const[tab,setTab]=useState("passing"); // "passing"|"rushing"|"receiving"
  const[err,setErr]=useState(null);

  const nflOrange="#ff6b00";

  useEffect(()=>{
    const load=async()=>{
      try{
        const d=await feedFetch("/api/feed?sport=nfl");
        setSchedule(d?.nfl?.schedule||[]);
        setScores(d?.nfl?.scores||{});
        setRankings(d?.nfl?.rankings||null);
      }catch(e){setErr("NFL data unavailable");}
      setLoading(false);
    };
    load();
    const iv=setInterval(()=>{
      if(document.hidden) return;
      feedFetch("/api/feed?sport=nfl").then(d=>{
        if(d?.nfl?.scores) setScores(d.nfl.scores);
        if(d?.nfl?.schedule) setSchedule(d.nfl.schedule);
      }).catch(()=>{});
    },60000);
    return()=>clearInterval(iv);
  },[]);

  if(loading) return <Spinner text="LOADING NFL..."/>;
  if(err) return <ErrMsg msg={err}/>;

  const thS={fontSize:10,letterSpacing:1,color:"#2e4460",fontWeight:700,padding:"8px 10px",borderBottom:"1px solid #0c1828",textAlign:"left",whiteSpace:"nowrap"};

  const catLabels={passing:"🏈 PASSING",rushing:"🏃 RUSHING",receiving:"🙌 RECEIVING"};
  const catColor={passing:nflOrange,rushing:"#00e676",receiving:"#c060ff"};

  const players=rankings?.[tab]||[];

  return(
    <div style={{padding:"0 0 60px"}}>
      {/* Game scoreboard */}
      {schedule.length>0&&(
        <div style={{overflowX:"auto",background:"#07090e",borderBottom:"1px solid #0f1826",padding:"8px 10px",display:"flex",gap:8,WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
          {[...schedule].sort((a,b)=>{
            const s=g=>{const st=g.status?.type?.state||"pre";return st==="in"?0:st==="post"?2:1;};
            return s(a)-s(b);
          }).map(g=>{
            const sc=scores[g.id];
            const away=g.competitors?.find(c=>c.homeAway==="away");
            const home=g.competitors?.find(c=>c.homeAway==="home");
            const status=g.status?.type?.state||"pre";
            const live=status==="in";
            const final=status==="post";
            const detail=g.status?.type?.shortDetail||"";
            return(
              <div key={g.id} style={{flexShrink:0,background:"#0d1322",border:`1px solid ${live?"#ff6b0040":"#182030"}`,borderRadius:6,padding:"9px 14px",minWidth:152}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:15,fontWeight:800,color:"#fff"}}>{away?.team?.abbreviation||"?"}</div>
                  {(live||final)&&<div style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:live?nflOrange:"#c8d6e8"}}>{sc?.away?.score??away?.score??0}</div>}
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:2}}>
                  <div style={{fontSize:15,fontWeight:800,color:"#fff"}}>{home?.team?.abbreviation||"?"}</div>
                  {(live||final)&&<div style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:live?nflOrange:"#c8d6e8"}}>{sc?.home?.score??home?.score??0}</div>}
                </div>
                <div style={{marginTop:5,fontSize:9,fontFamily:"monospace",color:live?nflOrange:final?"#4060a0":"#3a5070",letterSpacing:0.5}}>
                  {live?detail:final?"FINAL":detail||new Date(g.date).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
                </div>
                {g.broadcast&&<div style={{fontSize:8,color:"#2a3a50",marginTop:2,letterSpacing:1}}>{g.broadcast}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div style={{padding:"14px 16px"}}>
        {/* Header */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:22,fontWeight:900,color:"#fff",letterSpacing:2}}>🏈 NFL PLAYER RANKINGS</div>
          <div style={{fontSize:11,color:"#3a5570",letterSpacing:1,marginTop:4}}>SEASON LEADERS · YARDS · TDs · PER GAME</div>
        </div>

        {/* Coming soon banner */}
        {new Date() < new Date('2026-09-01') && (
          <div style={{background:"#0d0f1a",border:"1px solid #ff6b0040",borderRadius:8,padding:"18px 20px",marginBottom:18,textAlign:"center"}}>
            <div style={{fontSize:28,marginBottom:8}}>🏈</div>
            <div style={{fontSize:16,fontWeight:900,color:nflOrange,letterSpacing:2,marginBottom:6}}>NFL SEASON COMING SOON</div>
            <div style={{fontSize:12,color:"#3a5570",letterSpacing:1}}>KICKOFF · SEPTEMBER 2026</div>
          </div>
        )}

        {/* Tab switcher */}
        <div style={{display:"flex",gap:6,marginBottom:16}}>
          {Object.entries(catLabels).map(([key,label])=>(
            <button key={key} onClick={()=>setTab(key)}
              style={{padding:"8px 16px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:1.5,
                background:tab===key?catColor[key]+"18":"#0c1322",
                border:`1px solid ${tab===key?catColor[key]:"#182030"}`,
                borderRadius:5,cursor:"pointer",color:tab===key?catColor[key]:"#3a5070"}}>
              {label}
            </button>
          ))}
        </div>

        {!rankings&&(
          <div style={{textAlign:"center",padding:40}}>
            <div style={{fontSize:24,marginBottom:8}}>⏳</div>
            <div style={{fontSize:14,color:nflOrange,fontWeight:800,letterSpacing:1,marginBottom:6}}>RANKINGS UPDATING</div>
            <div style={{fontSize:11,color:"#2a3a50"}}>NFL season stats loading · Check back soon</div>
          </div>
        )}

        {players.length>0&&(
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
              <thead><tr>
                <th style={thS}>RNK</th>
                <th style={thS}>Player</th>
                <th style={thS}>Team</th>
                <th style={{...thS,color:catColor[tab]}}>YDS</th>
                <th style={thS}>YDS/G</th>
                <th style={thS}>TDs</th>
                <th style={thS}>{players[0]?.extraLabel||"EXTRA"}</th>
                <th style={thS}>GP</th>
              </tr></thead>
              <tbody>
                {players.map((p,i)=>{
                  const medal=i===0?"🥇":i===1?"🥈":i===2?"🥉":null;
                  const clr=catColor[tab];
                  return(
                    <tr key={p.id||p.name} style={{borderBottom:"1px solid #0a1020",background:i<3?"#0a0f1e":i<10?"#090d18":"transparent"}}>
                      <td style={{padding:"9px 10px",width:40}}>
                        {medal?<span style={{fontSize:16}}>{medal}</span>:<span style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:"#5080c0"}}>#{i+1}</span>}
                      </td>
                      <td style={{padding:"9px 10px",minWidth:160}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          {p.id&&<img src={`https://a.espncdn.com/i/headshots/nfl/players/full/${p.id}.png`} alt={p.name}
                            style={{width:i<3?40:32,height:i<3?40:32,borderRadius:"50%",objectFit:"cover",background:"#0a1020",flexShrink:0,border:`2px solid ${clr}40`}}
                            onError={e=>{e.target.style.display="none";}}/>}
                          <div>
                            <div style={{fontSize:15,fontWeight:800,color:"#e8f0ff"}}>{p.shortName||p.name}</div>
                            <div style={{fontSize:10,color:"#3a5070",marginTop:1}}>{p.team}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{padding:"9px 10px"}}>
                        <div style={{display:"inline-block",padding:"2px 7px",borderRadius:3,background:`#${p.teamColor||"1a2a4a"}30`,border:`1px solid #${p.teamColor||"1a2a4a"}60`,fontFamily:"monospace",fontSize:11,fontWeight:700,color:`#${p.teamColor||"7090c0"}`}}>{p.team}</div>
                      </td>
                      <td style={{padding:"9px 10px"}}>
                        <div style={{fontFamily:"monospace",fontSize:15,fontWeight:900,color:clr}}>{p.yds?.toLocaleString()||"—"}</div>
                      </td>
                      <td style={{padding:"9px 10px"}}>
                        <div style={{fontFamily:"monospace",fontSize:13,color:p.ydsPerGame>=300?"#00e676":p.ydsPerGame>=200?nflOrange:"#5080c0"}}>{p.ydsPerGame||"—"}</div>
                      </td>
                      <td style={{padding:"9px 10px"}}>
                        <div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:p.tds>=10?"#00e676":p.tds>=5?nflOrange:"#5080c0"}}>{p.tds||0}</div>
                      </td>
                      <td style={{padding:"9px 10px"}}>
                        <div style={{fontFamily:"monospace",fontSize:12,color:"#4a6080"}}>{p.extra>0?p.extra.toFixed(1):"—"}</div>
                      </td>
                      <td style={{padding:"9px 10px"}}>
                        <div style={{fontFamily:"monospace",fontSize:11,color:"#3a5070"}}>{p.gp||"—"}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MMA SECTION — ESPN Free API (no key needed)
// ════════════════════════════════════════════════════════════════
const ESPN_MMA  = "https://site.api.espn.com/apis/site/v2/sports/mma";
const ESPN_CORE = "https://sports.core.api.espn.com/v2/sports/mma";
// eslint-disable-next-line no-unused-vars
const MMA_API   = "/api/mma";
// eslint-disable-next-line no-unused-vars
const MMA_KEY   = "721c3d06c13d5f2683f467e31defd819";

const PROMO_ORGS = [
  {id:"ufc", label:"UFC", color:"#e63946", apiId:"1"},
  {id:"pfl", label:"PFL", color:"#2a9d8f", apiId:"3"},
];

function MMASection(){
  const[org,setOrg]=useState("ufc");
  const[events,setEvents]=useState([]);
  const[selEvent,setSelEvent]=useState(null);
  const[fights,setFights]=useState([]);
  const[fighters,setFighters]=useState({}); // id → fighter data
  const[oddsMap,setOddsMap]=useState({}); // name.toLowerCase() → moneyline
  const[loading,setLoading]=useState(true);
  const[loadingFights,setLoadingFights]=useState(false);
  const[err,setErr]=useState(null);
  const[mmaView,setMmaView]=useState("fights"); // "fights" | "picks"

  useEffect(()=>{
    setLoading(true);setEvents([]);setSelEvent(null);setFights([]);
    loadEvents(org);
  },[org]);

  const loadEvents=async(o)=>{
    try{
      setErr(null);
      const d=await feedFetch("/api/feed?sport=mma");
      if(!d?.mma?.events?.length){
        setErr("MMA data not ready yet. Check back soon.");
        setLoading(false);return;
      }
      const sorted=[...d.mma.events].sort((a,b)=>{
        const aLive=a.status?.type?.state==="in";
        const bLive=b.status?.type?.state==="in";
        const aDone=a.status?.type?.completed;
        const bDone=b.status?.type?.completed;
        if(aLive&&!bLive) return -1;
        if(bLive&&!aLive) return 1;
        if(!aDone&&bDone) return -1;
        if(aDone&&!bDone) return 1;
        return new Date(a.date)-new Date(b.date);
      });
      setEvents(sorted);
      setLoading(false);
      if(d.mma.card&&d.mma.current){
        const evt=sorted.find(e=>e.id===d.mma.current.eventId)||sorted[0];
        if(evt){
          setSelEvent(evt);
          setFights(d.mma.card.fights||[]);
          setFighters(d.mma.card.fighters||{});
          if(d.mma.odds?.oddsMap) setOddsMap(d.mma.odds.oddsMap);
          setLoadingFights(false);
        }
      } else {
        setLoadingFights(false);
      }
    }catch(e){setErr("MMA data unavailable. Try again later.");setLoading(false);}
  };

  const loadFightCard=async(evt)=>{
    // Fight card data comes from cron cache only — loaded in loadEvents
    setSelEvent(evt);
  };

  // ── Fighter score + finish prediction ────────────────────────────
  const scoreFighter=(f)=>{
    if(!f) return null;
    const wins=f.wins||0,losses=f.losses||0;
    const total=Math.max(1,wins+losses);
    const winPct=wins/total;
    const tkos=f.tkos||0,subs=f.subs||0;
    const finishes=tkos+subs;
    const finishRate=finishes/Math.max(1,wins); // how often they finish opponents
    const tkoLosses=f.tkoLosses||0,subLosses=f.subLosses||0;
    const stoppedRate=(tkoLosses+subLosses)/Math.max(1,losses); // how often they get finished
    const titleWins=f.titleWins||0;

    // Recent form from last5 — now includes method data from Wikipedia
    const last5=f.last5||[];
    // last5 items are {result,method,opponent,date} or just "W"/"L" strings
    const getResult=x=>typeof x==="string"?x:x?.result||"?";
    const getMethod=x=>typeof x==="string"?"":x?.method||"";
    const recentWins=last5.filter(x=>getResult(x)==="W").length;
    const recentLosses=last5.filter(x=>getResult(x)==="L").length;
    // Method analysis — is this fighter a finisher recently?
    const recentFinishes=last5.filter(x=>{
      const m=(getMethod(x)||"").toLowerCase();
      return getResult(x)==="W"&&(m.includes("ko")||m.includes("tko")||m.includes("sub")||m.includes("choke")||m.includes("lock"));
    }).length;
    const recentDecisions=last5.filter(x=>getResult(x)==="W"&&(getMethod(x)||"").toLowerCase().includes("dec")).length;

    // Score 0-100
    // 1. SOS — avg win% of last 5 opponents (35pts) — most important, quality > quantity
    const sos=f.sos!=null?f.sos:0.5;
    const sosHasData=f.sos!=null;
    const sosSc=sosHasData?Math.round(sos*35):17;
    // 2. Recent form — last 5 results (10pts)
    const recentSc=last5.length>0?Math.min(10,recentWins*2):5;
    // 3. Win % (15pts)
    const wpSc=Math.min(15,winPct*15);
    // 4. Recent finishing ability (15pts)
    const recentFinSc=last5.length>0?Math.min(15,recentFinishes*5):7;
    // 5. Finish rate — career (10pts)
    const finSc=Math.min(10,finishRate*10);
    // 6. Chin/durability (8pts)
    const chinSc=Math.min(8,(1-stoppedRate)*8);
    // 7. Title history (5pts)
    const titleSc=Math.min(5,titleWins*3);
    // 8. Experience (2pts)
    const expSc=Math.min(2,total*0.07);

    const score=Math.min(100,Math.round(sosSc+recentSc+wpSc+recentFinSc+finSc+chinSc+titleSc+expSc));
    return{score,winPct,finishRate,stoppedRate,tkos,subs,tkoLosses,subLosses,finishes,wins,losses,recentWins,recentLosses,recentFinishes,recentDecisions,last5,sos:f.sos,sosHasData};
  };

  const analyzeFight=(f1,f2)=>{
    if(!f1||!f2) return null;
    const s1=scoreFighter(f1);
    const s2=scoreFighter(f2);
    if(!s1||!s2) return null;

    // Finish likelihood for this fight
    // Average of both fighters' offensive finish rates × (1 + opponent's vulnerability)
    const finLikelihood1=s1.finishRate*(1+s2.stoppedRate*0.5);
    const finLikelihood2=s2.finishRate*(1+s1.stoppedRate*0.5);
    const overallFinishPct=Math.min(95,Math.round(((finLikelihood1+finLikelihood2)/2)*100));

    // Who finishes how
    const tkoChance1=Math.round((s1.tkos/Math.max(1,s1.wins))*100);
    const subChance1=Math.round((s1.subs/Math.max(1,s1.wins))*100);
    const tkoChance2=Math.round((s2.tkos/Math.max(1,s2.wins))*100);
    const subChance2=Math.round((s2.subs/Math.max(1,s2.wins))*100);

    // Edge
    const scoreDiff=s1.score-s2.score;
    const edge=Math.abs(scoreDiff)<5?"even":scoreDiff>0?"f1":"f2";
    const edgeStrength=Math.abs(scoreDiff)<5?"EVEN":Math.abs(scoreDiff)<15?"SLIGHT EDGE":Math.abs(scoreDiff)<25?"CLEAR EDGE":"STRONG EDGE";

    // Method prediction
    let method="Decision likely";
    if(overallFinishPct>=65) method="Finish likely";
    else if(overallFinishPct>=45) method="Could go either way";

    return{s1,s2,edge,edgeStrength,overallFinishPct,tkoChance1,subChance1,tkoChance2,subChance2,method,scoreDiff};
  };

  const fmtRecord=(f)=>{
    if(!f) return "—";
    if(f.record) return f.record;
    const w=f.wins||0,l=f.losses||0,d=f.draws||0;
    return `${w}-${l}${d>0?`-${d}`:""}`;
  };

  const fmtDate=(dt)=>{try{return new Date(dt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});}catch{return "";}};
  const fmtTime=(dt)=>{try{return new Date(dt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",timeZone:"America/New_York"});}catch{return "";}};

  if(loading) return <Spinner text="LOADING MMA EVENTS..."/>;

  const red="#e63946";

  return(
    <>
      {/* Header rail */}
      <div style={{display:"flex",gap:8,padding:"10px 14px",overflowX:"auto",background:"#070608",borderBottom:"1px solid #1a0808",alignItems:"center",flexWrap:"wrap"}}>
        {/* Org tabs */}
        <div style={{display:"flex",gap:4,flexShrink:0}}>
          {PROMO_ORGS.map(o=>(
            <button key={o.id} onClick={()=>setOrg(o.id)} style={{padding:"7px 14px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:13,fontWeight:800,letterSpacing:1.5,background:org===o.id?"#180808":"#0c0a0a",border:`1px solid ${org===o.id?o.color:"#2a1010"}`,borderRadius:4,cursor:"pointer",color:org===o.id?o.color:"#a08080",transition:"all 0.12s"}}>
              {o.label}
            </button>
          ))}
        </div>
        <div style={{width:1,height:36,background:"#2a1010",flexShrink:0}}/>
        {/* Event pills */}
        {events.length===0&&!loading&&<div style={{color:"#a08080",fontSize:13}}>No events found</div>}
        {events.slice(0,8).map(e=>{
          const isA=selEvent?.id===e.id;
          const isLive=e.status?.type?.state==="in";
          const isDone=e.status?.type?.completed;
          return(
            <div key={e.id} onClick={()=>loadFightCard(e)} style={{flexShrink:0,background:isA?"#1a0808":"#0d0808",border:`1px solid ${isA?red:isLive?"#e6394640":"#2a1010"}`,borderRadius:6,padding:"8px 14px",cursor:"pointer",minWidth:140,transition:"all 0.12s"}}>
              <div style={{fontSize:14,fontWeight:800,color:"#f0d0d0",letterSpacing:0.5,lineHeight:1.2}}>{e.name||e.shortName||"Event"}</div>
              <div style={{fontFamily:"monospace",fontSize:10,color:"#b09090",marginTop:3}}>{fmtDate(e.date)}</div>
              <div style={{display:"inline-block",fontSize:9,padding:"2px 6px",borderRadius:3,marginTop:4,fontWeight:700,background:isLive?"#1a000030":"#200808",color:isLive?red:isDone?"#5a3030":"#8a5050",border:`1px solid ${isLive?"#e6394630":"#2a1010"}`}}>
                {isLive?"● LIVE":isDone?"Final":"Upcoming"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Event detail */}
      {err&&<div style={{padding:40,textAlign:"center",color:"#e63946",fontSize:13}}>{err}</div>}
      {!err&&!selEvent&&!loading&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:300,color:"#a08080",fontSize:13}}>← Select an event</div>}
      {selEvent&&(
        <div style={{padding:"14px 16px 32px"}}>
          {/* Event header */}
          <div style={{marginBottom:14}}>
            <div style={{fontSize:24,fontWeight:900,color:"#fff",letterSpacing:2}}>{selEvent.name||selEvent.shortName}</div>
            <div style={{fontSize:11,color:"#b09090",letterSpacing:1,marginTop:3}}>
              {selEvent.venue?.fullName&&<span>{selEvent.venue.fullName} · </span>}
              {selEvent.venue?.address?.city&&<span>{selEvent.venue.address.city} · </span>}
              {fmtDate(selEvent.date)} · {fmtTime(selEvent.date)} ET
            </div>
          </div>

          {/* View tabs */}
          {!loadingFights&&fights.length>0&&(
            <div style={{display:"flex",gap:4,marginBottom:16}}>
              <button onClick={()=>setMmaView("fights")} style={{padding:"7px 16px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,letterSpacing:1.5,background:mmaView==="fights"?"#180808":"#0c0a0a",border:`1px solid ${mmaView==="fights"?red:"#2a1010"}`,borderRadius:4,cursor:"pointer",color:mmaView==="fights"?red:"#4a3030"}}>
                🥊 FIGHT CARD
              </button>
              <button onClick={()=>setMmaView("picks")} style={{padding:"7px 16px",fontFamily:"'Barlow Condensed',sans-serif",fontSize:14,fontWeight:700,letterSpacing:1.5,background:mmaView==="picks"?"#0a1428":"#0c0a0a",border:`1px solid ${mmaView==="picks"?"#7eb8f7":"#2a1010"}`,borderRadius:4,cursor:"pointer",color:mmaView==="picks"?"#7eb8f7":"#4a3030",boxShadow:mmaView==="picks"?"0 0 8px #7eb8f740":"none"}}>
                🤖 AI TOP PICKS
              </button>
            </div>
          )}

          {loadingFights&&<Spinner text="LOADING FIGHT CARD + FIGHTER DATA..."/>}

          {/* TOP PICKS VIEW */}
          {!loadingFights&&mmaView==="picks"&&(()=>{
            // Build picks: for each fight, find the projected winner + best method
            const picks=[];
            fights.forEach((fight,fi)=>{
              const[c1,c2]=fight.fighters;
              const f1=fighters[c1?.id];
              const f2=fighters[c2?.id];
              if(!f1||!f2) return;
              const analysis=analyzeFight(f1,f2);
              if(!analysis) return;
              const isDone=fight.completed;
              if(isDone) return; // skip finished fights

              const{s1,s2,edge,tkoChance1,subChance1,tkoChance2,subChance2,scoreDiff,overallFinishPct}=analysis;

              // Determine projected winner
              const winnerF=edge==="f1"?f1:edge==="f2"?f2:null;
              const winnerS=edge==="f1"?s1:edge==="f2"?s2:null;
              const loserF=edge==="f1"?f2:edge==="f2"?f1:null;
              const loserS=edge==="f1"?s2:edge==="f2"?s1:null;
              if(!winnerF||!winnerS) return;

              // Best method for this winner
              const tkoC=edge==="f1"?tkoChance1:tkoChance2;
              const subC=edge==="f1"?subChance1:subChance2;
              let method="Decision";
              let methodColor="#7090c0";
              if(overallFinishPct>=55){
                if(tkoC>=subC){method=`TKO/KO (${tkoC}%)`;methodColor="#ff4060";}
                else{method=`Submission (${subC}%)`;methodColor="#ff9020";}
              } else if(overallFinishPct>=35){
                method=`Finish possible (${overallFinishPct}%)`;methodColor="#ffb020";
              }

              // Confidence score: fighter score × edge magnitude
              const confidence=Math.min(99,Math.round(winnerS.score*(1+Math.abs(scoreDiff)/100)));

              picks.push({
                rank:fi+1,
                winnerF,winnerS,loserF,loserS,
                method,methodColor,
                confidence,overallFinishPct,
                edge:analysis.edgeStrength,
                fight,fi,
              });
            });

            // Sort by confidence desc, take top 5
            const top5=picks.sort((a,b)=>b.confidence-a.confidence).slice(0,5);

            if(top5.length===0) return(
              <div style={{textAlign:"center",padding:40,color:"#a08080",fontSize:13}}>No upcoming fights to analyze</div>
            );

            return(
              <div>
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:20,fontWeight:900,color:"#fff",letterSpacing:2}}>🤖 AI TOP 5 PICKS</div>
                  <div style={{fontSize:11,color:"#b09090",letterSpacing:1,marginTop:4}}>RANKED BY CONFIDENCE · WIN % · FINISH RATE · CHIN · EXPERIENCE · TITLE HISTORY</div>
                </div>
                {top5.map((pick,i)=>{
                  const{winnerF,loserF,method,methodColor,confidence,edge,overallFinishPct}=pick;
                  const medal=i===0?"🥇":i===1?"🥈":i===2?"🥉":null;
                  const confColor=confidence>=75?"#00e676":confidence>=55?"#ffb020":"#ff4060";
                  return(
                    <div key={i} style={{background:i===0?"#140505":i<3?"#100404":"#0c0808",border:`1px solid ${i===0?red+"60":i<3?red+"30":"#2a1010"}`,borderRadius:8,padding:"14px 16px",marginBottom:10,display:"grid",gridTemplateColumns:"40px 1fr auto",gap:12,alignItems:"center"}}>
                      {/* Rank */}
                      <div style={{textAlign:"center"}}>
                        {medal?<span style={{fontSize:20}}>{medal}</span>:<span style={{fontFamily:"monospace",fontSize:16,fontWeight:700,color:"#b09090"}}>#{i+1}</span>}
                      </div>
                      {/* Fighter info */}
                      <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,flexShrink:0}}>
                          <img src={`https://a.espncdn.com/i/headshots/mma/players/full/${winnerF.id}.png`}
                            alt={winnerF.name} style={{width:56,height:56,borderRadius:"50%",objectFit:"cover",objectPosition:"top",border:`2px solid ${confColor}60`,background:"#1a0808"}}
                            onError={e=>{e.target.style.display="none";}}/>
                        </div>
                        <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                          <div style={{width:8,height:8,borderRadius:"50%",background:confColor,boxShadow:`0 0 6px ${confColor}`}}/>
                          <span style={{fontSize:18,fontWeight:900,color:"#fff",letterSpacing:0.5}}>{winnerF.name}</span>
                          <span style={{fontSize:11,color:"#b09090",fontFamily:"monospace"}}>{fmtRecord(winnerF)}</span>
                        </div>
                        <div style={{fontSize:12,color:"#c09090",marginBottom:5}}>
                          vs <span style={{color:"#b09090"}}>{loserF?.name||"TBD"}</span>
                          {loserF&&<span style={{fontFamily:"monospace",fontSize:10,color:"#907070",marginLeft:6}}>{fmtRecord(loserF)}</span>}
                        </div>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          <span style={{fontSize:10,color:methodColor,background:methodColor+"15",border:`1px solid ${methodColor}40`,borderRadius:3,padding:"2px 8px",fontWeight:700}}>{method}</span>
                          <span style={{fontSize:10,color:"#b09090",background:"#150505",border:"1px solid #2a1010",borderRadius:3,padding:"2px 8px"}}>{edge}</span>
                          {winnerF.weightClass&&<span style={{fontSize:10,color:"#907070",background:"#100404",border:"1px solid #200808",borderRadius:3,padding:"2px 8px"}}>{winnerF.weightClass}</span>}
                        </div>
                        </div>
                      </div>
                      {/* Confidence */}
                      <div style={{textAlign:"center",minWidth:64}}>
                        <div style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:56,height:56,borderRadius:8,background:confColor+"15",border:`2px solid ${confColor}60`,flexDirection:"column"}}>
                          <div style={{fontFamily:"monospace",fontSize:20,fontWeight:900,color:confColor,lineHeight:1}}>{confidence}</div>
                          <div style={{fontSize:8,color:confColor,opacity:0.7,letterSpacing:1}}>CONF</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div style={{marginTop:12,padding:"10px 14px",background:"#0a0505",border:"1px solid #1a0808",borderRadius:6,fontSize:10,color:"#907070",lineHeight:1.6}}>
                  ⚠️ AI picks are based on historical win/loss records, finish rates, durability, and experience. MMA is unpredictable — use as a guide only.
                </div>
              </div>
            );
          })()}

          {!loadingFights&&mmaView==="fights"&&fights.map((fight,fi)=>{
            const [c1,c2]=fight.fighters; // eslint-disable-line no-unused-vars
            const f1=fighters[c1?.id];
            const f2=fighters[c2?.id];
            const analysis=analyzeFight(f1,f2);

            const isLive=fight.status==="in";
            const isDone=fight.completed;
            const winnerF=fight.winner?fighters[fight.winner]:null;

            const renderFighter=(c,f,side)=>{
              const isWinner=fight.winner&&fight.winner===c?.id;
              const isLoser=fight.winner&&fight.winner!==c?.id&&isDone;
              const isFavored=analysis&&!isDone&&((side==="left"&&analysis.edge==="f1")||(side==="right"&&analysis.edge==="f2"));
              const isUnderdog=analysis&&!isDone&&analysis.edge!=="even"&&!isFavored;
              const wins=f?.wins||0,losses=f?.losses||0;
              const koRate=f?.winsKO!=null?Math.round((f.winsKO/Math.max(1,wins))*100):null;
              const subRate=f?.winsSub!=null?Math.round((f.winsSub/Math.max(1,wins))*100):null;

              return(
                <div style={{flex:1,textAlign:side==="right"?"right":"left",padding:"0 8px"}}>
                  <div style={{display:"flex",flexDirection:"column",alignItems:side==="right"?"flex-end":"flex-start",marginBottom:6}}>
                    <img src={`https://a.espncdn.com/i/headshots/mma/players/full/${c?.id}.png`}
                      alt={c?.displayName||"fighter"} style={{width:54,height:54,borderRadius:"50%",objectFit:"cover",objectPosition:"top",border:`2px solid ${isWinner?"#00e676":isLoser?"#5a3030":isFavored?"#ffffff60":"#3a1818"}`,background:"#1a0808"}}
                      onError={e=>{e.target.style.display="none";}}/>
                  </div>
                  {f?.ranking&&<div style={{fontFamily:"monospace",fontSize:10,color:"#00e676",marginBottom:3}}>#{f.ranking} RANKED</div>}
                  <div style={{fontSize:16,fontWeight:900,color:isFavored?"#ffffff":isLoser?"#886060":"#f0d0d0",letterSpacing:0.5,lineHeight:1.2,wordBreak:"break-word",overflowWrap:"break-word"}}>
                    {isWinner&&<span style={{marginRight:6}}>🏆</span>}
                    {c?.displayName||f?.name||"TBD"}
                  </div>
                  {!isDone&&isFavored&&<div style={{display:"inline-block",marginTop:4,fontSize:11,fontWeight:800,letterSpacing:1.5,color:"#00e676",background:"#00e67620",border:"1px solid #00e67660",borderRadius:3,padding:"3px 10px"}}>⭐ ADVANTAGE</div>}
                  {!isDone&&isUnderdog&&<div style={{display:"inline-block",marginTop:4,fontSize:11,fontWeight:800,letterSpacing:1.5,color:"#ff2244",background:"#ff224420",border:"1px solid #ff224460",borderRadius:3,padding:"3px 10px"}}>✖ TOUGH MATCHUP</div>}
                  {!isDone&&analysis?.edge==="even"&&<div style={{display:"inline-block",marginTop:4,fontSize:11,fontWeight:700,letterSpacing:1.5,color:"#ffffff",background:"#ffffff15",border:"1px solid #ffffff40",borderRadius:3,padding:"3px 10px"}}>— EVEN</div>}
                  {f?.nickname&&<div style={{fontSize:11,color:"#c09090",fontStyle:"italic",marginTop:2}}>"{f.nickname}"</div>}
                  <div style={{fontFamily:"monospace",fontSize:16,fontWeight:700,color:isFavored?"#00e676":isLoser?"#886060":"#c8d0d8",marginTop:4}}>{fmtRecord(f)}</div>
                  {(()=>{
                    const ml=oddsMap[(f?.name||"").toLowerCase()];
                    if(!ml) return null;
                    const isFav=ml<0;
                    const label=isFav?`FAV ${ml}`:`DOG +${ml}`;
                    const color=isFav?"#00c8ff":"#ffb020";
                    return <div style={{fontSize:10,fontWeight:700,color,fontFamily:"monospace",marginTop:2}}>{label}</div>;
                  })()}

                  {/* Strength of Schedule */}
                  {f?.sos!=null&&(
                    <div style={{marginTop:4,fontSize:9,color:"#8090a0"}}>
                      <span style={{fontWeight:700,color:f.sos>=0.6?"#00e676":f.sos>=0.45?"#ffb020":"#e63946"}}>
                        SOS: {(f.sos*100).toFixed(0)}%
                      </span>
                      <span style={{color:"#4a6080"}}> avg opp win%{f.sosFound!=null?` (${f.sosFound}/${f.sosTotal} found)`:""}</span>
                    </div>
                  )}
                  {/* Last 5 fights with method from Wikipedia */}
                  {f?.last5?.length>0&&(
                    <div style={{marginTop:6,display:"flex",flexDirection:"column",alignItems:side==="right"?"flex-end":"flex-start",gap:3}}>
                      <div style={{fontSize:8,color:"#b09090",letterSpacing:1,fontWeight:700}}>LAST 5 FIGHTS</div>
                      <div style={{display:"flex",flexDirection:"column",gap:2,alignItems:side==="right"?"flex-end":"flex-start"}}>
                        {f.last5.map((fight,i)=>{
                          const r=typeof fight==="string"?fight:fight?.result||"?";
                          const method=typeof fight==="string"?"":fight?.method||"";
                          const opp=typeof fight==="string"?"":fight?.opponent||"";
                          const shortMethod=method.replace(/Technical Knockout/i,"TKO").replace(/Knockout/i,"KO").replace(/Submission/i,"Sub").replace(/Unanimous Decision/i,"UD").replace(/Split Decision/i,"SD").replace(/Majority Decision/i,"MD").replace(/Decision/i,"Dec");
                          return(
                            <div key={i} style={{display:"flex",alignItems:"center",gap:4,flexDirection:side==="right"?"row-reverse":"row"}}>
                              <div style={{
                                width:20,height:20,borderRadius:3,flexShrink:0,
                                background:r==="W"?"#00e67625":r==="L"?"#e6394625":"#ffffff08",
                                border:`1px solid ${r==="W"?"#00e67680":r==="L"?"#e6394680":"#333"}`,
                                display:"flex",alignItems:"center",justifyContent:"center",
                                fontFamily:"monospace",fontSize:10,fontWeight:900,
                                color:r==="W"?"#00e676":r==="L"?"#e63946":"#aaa",
                              }}>{r}</div>
                              {shortMethod&&<div style={{fontSize:9,color:r==="W"?"#00c060":r==="L"?"#c03040":"#666",fontWeight:700}}>{shortMethod}</div>}
                              {opp&&<div style={{fontSize:9,color:"#4a6080",maxWidth:70,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{opp}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {f&&(
                    <div style={{marginTop:6}}>
                      {/* Physical */}
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:side==="right"?"flex-end":"flex-start",marginBottom:4}}>
                        {f.height&&<div style={{fontSize:10,color:"#c0c0c0"}}>{f.height}</div>}
                        {f.reach&&<div style={{fontSize:10,color:"#c0c0c0"}}>Reach: {f.reach}</div>}
                        {f.stance&&<div style={{fontSize:10,color:"#c0c0c0",fontStyle:"italic"}}>{f.stance}</div>}
                        {f.country&&<div style={{fontSize:10,color:"#c0c0c0"}}>{f.country}</div>}
                        {f.styles?.length>0&&<div style={{fontSize:10,color:"#c0c0c0"}}>{f.styles.join(", ")}</div>}
                      </div>
                      {/* Win breakdown — KO/Sub/Dec counts + finish rates */}
                      <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:side==="right"?"flex-end":"flex-start",marginTop:3}}>
                        {f.tkos>0&&<div style={{background:"#200808",border:"1px solid #3a1010",borderRadius:3,padding:"2px 7px",fontFamily:"monospace",fontSize:10,color:"#ff6b6b"}}>
                          {f.tkos} KO/TKO {koRate!==null?`(${koRate}%)`:""}
                        </div>}
                        {f.subs>0&&<div style={{background:"#081520",border:"1px solid #103040",borderRadius:3,padding:"2px 7px",fontFamily:"monospace",fontSize:10,color:"#4a90d9"}}>
                          {f.subs} SUB {subRate!==null?`(${subRate}%)`:""}
                        </div>}
                        {f.wins>0&&(f.tkos+f.subs)<f.wins&&<div style={{background:"#0a100a",border:"1px solid #1a3010",borderRadius:3,padding:"2px 7px",fontFamily:"monospace",fontSize:10,color:"#6ab04c"}}>
                          {f.wins-(f.tkos+f.subs)} DEC
                        </div>}
                      </div>
                      {/* Losses breakdown */}
                      {losses>0&&(f.tkoLosses+f.subLosses)>0&&(
                        <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:side==="right"?"flex-end":"flex-start",marginTop:2}}>
                          {f.tkoLosses>0&&<div style={{background:"#1a0808",border:"1px solid #2a1010",borderRadius:3,padding:"1px 6px",fontFamily:"monospace",fontSize:9,color:"#c05050"}}>{f.tkoLosses} KO loss</div>}
                          {f.subLosses>0&&<div style={{background:"#080a14",border:"1px solid #101828",borderRadius:3,padding:"1px 6px",fontFamily:"monospace",fontSize:9,color:"#3a6090"}}>{f.subLosses} SUB loss</div>}
                        </div>
                      )}
                    </div>
                  )}
                  {!f&&<div style={{fontSize:11,color:"#a08080",marginTop:8,fontFamily:"monospace"}}>Loading...</div>}
                </div>
              );
            };

            return(
              <div key={fight.id} style={{background:fi===0?"#0f0505":"#0a0505",border:`1px solid ${fight.isTitleFight?"#e6394640":isLive?"#e6394630":"#1a0808"}`,borderRadius:8,marginBottom:10,overflow:"hidden",width:"100%",boxSizing:"border-box"}}>
                {/* Fight label */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 14px",borderBottom:"1px solid #180808",background:fight.isTitleFight?"#150303":"#0d0303"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {fi===0&&<span style={{fontSize:11,fontWeight:800,color:red,letterSpacing:1.5}}>MAIN EVENT</span>}
                    {fi>0&&fight.isTitleFight&&<span style={{fontSize:11,fontWeight:800,color:"#e09050",letterSpacing:1.5}}>🏆 TITLE FIGHT</span>}
                    {fi>0&&!fight.isTitleFight&&<span style={{fontSize:11,color:"#888888",letterSpacing:1}}>BOUT {fi+1}</span>}
                    {fight.weightClass&&<span style={{fontSize:10,color:"#cccccc",background:"#1a1a1a",border:"1px solid #333333",borderRadius:3,padding:"1px 7px"}}>{fight.weightClass}</span>}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {isLive&&fight.round&&<span style={{fontFamily:"monospace",fontSize:10,color:red}}>R{fight.round} {fight.timeStr}</span>}
                    {isDone&&winnerF&&<span style={{fontFamily:"monospace",fontSize:10,color:"#00e676"}}>{winnerF.name||fight.method}</span>}
                    {isDone&&fight.method&&<span style={{fontFamily:"monospace",fontSize:10,color:"#5a7050"}}>{fight.method}</span>}
                    <div style={{fontSize:9,padding:"2px 6px",borderRadius:3,fontWeight:700,background:isLive?"#1a000030":"#200808",color:isLive?red:isDone?"#5a3030":"#8a5050",border:`1px solid ${isLive?"#e6394630":"#2a1010"}`}}>
                      {isLive?"● LIVE":isDone?"Final":"Upcoming"}
                    </div>
                  </div>
                </div>

                {/* Fighters */}
                <div style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",gap:0,padding:"0",alignItems:"stretch",width:"100%",overflow:"hidden"}}>
                  <div style={{padding:"10px 6px",background:analysis&&!isDone&&analysis.edge==="f1"?"#140505":"transparent",borderRight:"1px solid #1a0808",borderRadius:"0 0 0 8px",minWidth:0,overflow:"hidden"}}>
                  {renderFighter(c1,f1,"left")}
                  </div>
                  <div style={{textAlign:"center",width:70,flexShrink:0,padding:"10px 2px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                    <div style={{fontSize:18,fontWeight:900,color:"#ffffff",letterSpacing:2,marginBottom:6}}>VS</div>
                    {analysis&&!isDone&&(
                      <div>
                        {/* Finish likelihood */}
                        <div style={{background:"#0d0d0d",border:"1px solid #333333",borderRadius:5,padding:"6px 8px",marginBottom:6}}>
                          <div style={{fontSize:9,color:"#aaaaaa",letterSpacing:1,marginBottom:2}}>FINISH PROB</div>
                          <div style={{fontFamily:"monospace",fontSize:18,fontWeight:700,color:analysis.overallFinishPct>=65?red:analysis.overallFinishPct>=45?"#ffb020":"#5a3030"}}>{analysis.overallFinishPct}%</div>
                          <div style={{fontSize:9,color:"#aaaaaa",marginTop:1}}>{analysis.method}</div>
                        </div>
                        {/* Edge */}
                        {analysis.edge!=="even"&&(
                          <div style={{background:"#0d0d0d",border:"1px solid #444444",borderRadius:4,padding:"4px 6px",marginBottom:4}}>
                            <div style={{fontSize:8,color:"#aaaaaa",letterSpacing:1}}>EDGE</div>
                            <div style={{fontSize:10,fontWeight:700,color:red}}>{analysis.edge==="f1"?(f1?.name?.split(" ").slice(-1)[0]||"F1"):(f2?.name?.split(" ").slice(-1)[0]||"F2")}</div>
                            <div style={{fontSize:8,color:"#aaaaaa"}}>{analysis.edgeStrength}</div>
                          </div>
                        )}
                        {/* Scores */}
                        <div style={{display:"flex",justifyContent:"space-between",gap:4}}>
                          <div style={{background:"#0d0d0d",border:"1px solid #333",borderRadius:4,padding:"3px 6px",flex:1}}>
                            <div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:analysis.s1.score>analysis.s2.score?"#00e676":"#ffffff"}}>{analysis.s1.score}</div>
                            <div style={{fontSize:8,color:"#aaaaaa"}}>SCORE</div>
                          </div>
                          <div style={{background:"#0d0d0d",border:"1px solid #333",borderRadius:4,padding:"3px 6px",flex:1}}>
                            <div style={{fontFamily:"monospace",fontSize:13,fontWeight:700,color:analysis.s2.score>analysis.s1.score?"#00e676":"#ffffff"}}>{analysis.s2.score}</div>
                            <div style={{fontSize:8,color:"#aaaaaa"}}>SCORE</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {isDone&&winnerF&&(
                      <div style={{fontSize:10,fontWeight:700,color:"#00e676",marginTop:4}}>🏆 {winnerF.name?.split(" ").slice(-1)[0]}</div>
                    )}
                  </div>
                  <div style={{padding:"10px 6px",background:analysis&&!isDone&&analysis.edge==="f2"?"#140505":"transparent",borderLeft:"1px solid #1a0808",borderRadius:"0 0 8px 0",minWidth:0,overflow:"hidden"}}>
                  {renderFighter(c2,f2,"right")}
                  </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </>
  );
}


function Spinner({text}){return<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:260,flexDirection:"column",gap:12}}><div style={{width:24,height:24,border:"2px solid #1a2540",borderTopColor:"#00c8ff",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/><div style={{fontSize:11,letterSpacing:3,color:"#4a6080"}}>{text}</div></div>;}
function ErrMsg({msg}){return<div style={{padding:40,textAlign:"center",color:"#ff4060",fontSize:13}}>{msg}</div>;}